import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { Calendar, Clock, Users, ChevronDown, ChevronRight, ChevronLeft, Plus, Search, Filter, ZoomIn, ZoomOut, AlertTriangle, User, GripVertical, Plane, Home, X, Trash2, Undo, Redo, Send, ClipboardList, Package, Truck, Settings, Lightbulb, Cherry, TrendingUp, FileText, Check } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
// jsPDF and Roboto font are lazy loaded in generatePDF() to reduce initial bundle size (~600KB savings)
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import StatusBar from './StatusBar';
import TravelTooltip from './TravelTooltip';
import TimelineHeader from './TimelineHeader';
import logger from './utils/logger';
import resourceBoardService from './services/resourceBoardService';
import { useResourceBoardMigration } from './hooks/useResourceBoardMigration';

// Lazy loaded modals - only loaded when needed (~100KB savings)
const TravelPlanningForm = lazy(() => import('./TravelPlanningForm'));
const AssignmentActionsModal = lazy(() => import('./AssignmentActionsModal'));
const CarProjectModal = lazy(() => import('./CarProjectModal'));
const CarTravelPlanningForm = lazy(() => import('./CarTravelPlanningForm'));
const FilterPanel = lazy(() => import('./FilterPanel'));
const PersonnelSection = lazy(() => import('./PersonnelSection'));
const ChatWidget = lazy(() => import('./ChatWidget'));
const QuickAssignmentModal = lazy(() => import('./QuickAssignmentModal'));
const UserManagementModal = lazy(() => import('./UserManagementModal'));

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

// Helper function to format date for API (timezone-safe)
const formatDateForAPI = (date) => {
  if (!(date instanceof Date)) return date;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Throttle helper using requestAnimationFrame for optimal performance
const throttleRAF = (callback) => {
  let rafId = null;
  let lastArgs = null;

  const throttled = (...args) => {
    lastArgs = args;
    if (!rafId) {
      rafId = requestAnimationFrame(() => {
        callback(...lastArgs);
        rafId = null;
      });
    }
  };

  throttled.cancel = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  return throttled;
};

const ResourceBoard = () => {
  const navigate = useNavigate();

  // Use migration hook for main state (from Context)
  const {
    loading, setLoading,
    projects, setProjects,
    users, setUsers,
    assignments, setAssignments,
    expandedProjects, setExpandedProjects,
    selectedTimeRange, setSelectedTimeRange,
    currentDate, setCurrentDate,
    travelData, setTravelData,
    carsData, setCarsData,
    carAssignments, setCarAssignments
  } = useResourceBoardMigration();

  // Local UI state (not shared, stays in useState)
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [assignmentDragState, setAssignmentDragState] = useState(null); // For dragging assignments between users
  const [dropTargetUser, setDropTargetUser] = useState(null); // User being hovered over during drag
  const dropTargetUserRef = useRef(null); // Ref to avoid stale closure in event handlers
  const [dropTargetCar, setDropTargetCar] = useState(null); // Car being hovered over during drag
  const dropTargetCarRef = useRef(null); // Ref to avoid stale closure in event handlers
  const dragPreviewRef = useRef(null); // Ref for drag preview element (avoid re-renders)
  const rafIdRef = useRef(null); // Ref for requestAnimationFrame optimization
  const dragElementRef = useRef(null); // Cache DOM element during drag/resize to avoid querySelector
  const dragElementOriginalWidth = useRef(null); // Cache original width as number
  const lastClickTimeRef = useRef({ time: 0, assignmentId: null }); // Track double-clicks to prevent drag on double-click

  // Car assignments - only modal edit (no resize/move)
  const timelineRef = useRef(null);
  const bodyTimelineRef = useRef(null);
  const personnelColumnRef = useRef(null);
  const [isNewAssignmentModalOpen, setIsNewAssignmentModalOpen] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [selectedRoleSort, setSelectedRoleSort] = useState('all'); // Role to prioritize in sorting
  const [groupByRole, setGroupByRole] = useState(false);
  const [projectRoles, setProjectRoles] = useState({}); // { projectId: [{ id, name, color }] }
  const [newRoleInputs, setNewRoleInputs] = useState({}); // { projectId: '' }
  const [travelsMap, setTravelsMap] = useState(new Map()); // Map travel assignments to travel data
  const [trainingData, setTrainingData] = useState([]);
  const [holidays, setHolidays] = useState([]); // User holidays data
  const [userRoles, setUserRoles] = useState({}); // { userId: { role: 'Site Manager', color: 'bg-red-100' } }
  const [userGlobalRoles, setUserGlobalRoles] = useState([]); // Raw user-global-role assignments from API
  const [projectThresholds, setProjectThresholds] = useState({}); // { projectId: { warning: 30, critical: 45 } }
  const [globalRoles, setGlobalRoles] = useState([]); // [{ id, name, color }]
  const [newGlobalRoleInput, setNewGlobalRoleInput] = useState('');
  
  // Car project assignment modal states
  const [showCarProjectModal, setShowCarProjectModal] = useState(false);
  const [carAssignmentData, setCarAssignmentData] = useState({ carId: null, date: null });
  const [selectedCarProject, setSelectedCarProject] = useState('');

  // Car travel planning modal states
  const [showCarTravelModal, setShowCarTravelModal] = useState(false);
  const [carTravelData, setCarTravelData] = useState({ car: null, date: null, currentProject: null });

  const [sortByRole, setSortByRole] = useState(true); // Sort personnel by role hierarchy

  // Custom tiles state - now loaded from API
  const [customTiles, setCustomTiles] = useState([]);
  const [newTileInput, setNewTileInput] = useState('');
  const [customColorHex, setCustomColorHex] = useState('#9333ea'); // Default purple
  const [roleOrder, setRoleOrder] = useState([]); // Custom role order for sorting [roleId1, roleId2, ...]
  const [showRoleOrderModal, setShowRoleOrderModal] = useState(false);
  const [selectedCarUser, setSelectedCarUser] = useState('');

  // Custom date range states
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dayOffset, setDayOffset] = useState(0); // Offset in days for fine-grained scrolling

  // Filter states
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of user IDs
  const [selectedProjects, setSelectedProjects] = useState([]); // Array of project IDs
  const [selectedCars, setSelectedCars] = useState([]); // Array of car IDs
  const [selectedCustomers, setSelectedCustomers] = useState([]); // Array of customer names
  const [selectedRoles, setSelectedRoles] = useState([]); // Array of role IDs
  const [showFilters, setShowFilters] = useState(false);
  
  // Assignment actions
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentActions, setShowAssignmentActions] = useState(false);
  const [assignmentActionType, setAssignmentActionType] = useState(null); // 'move' or 'copy'
  
  // Car assignment edit functionality
  const [showCarAssignmentEdit, setShowCarAssignmentEdit] = useState(false);
  const [selectedCarAssignment, setSelectedCarAssignment] = useState(null);
  const [originalCarAssignmentDates, setOriginalCarAssignmentDates] = useState(null); // Store original dates
  const [currentEditingAssignmentId, setCurrentEditingAssignmentId] = useState(null); // Track which assignment is being edited
  const [editCarAssignmentUsers, setEditCarAssignmentUsers] = useState([]);
  const [editCarUserSearchQuery, setEditCarUserSearchQuery] = useState('');

  // Car assignment delete confirmation
  const [showCarDeleteConfirm, setShowCarDeleteConfirm] = useState(false);
  const [carAssignmentToDelete, setCarAssignmentToDelete] = useState(null);

  // Holiday approval modal
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [holidayRejectionReason, setHolidayRejectionReason] = useState('');
  
  // Comment functionality
  const [assignmentComments, setAssignmentComments] = useState(() => {
    const savedComments = localStorage.getItem('resourceBoardComments');
    return savedComments ? JSON.parse(savedComments) : {};
  }); // { assignmentId: 'comment text' }
  const [editingComment, setEditingComment] = useState(null); // assignmentId being edited
  const [commentInput, setCommentInput] = useState('');

  // Unsaved changes state management
  const [pendingChanges, setPendingChanges] = useState({
    updated: [], // Modified assignments
    deleted: [], // Deleted assignment IDs
    created: []  // New assignments (without ID yet)
  });
  const [pendingCarChanges, setPendingCarChanges] = useState({
    updated: [], // Modified car assignments
    deleted: [], // Deleted car assignment IDs
    created: []  // New car assignments (without ID yet)
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedAssignments, setSavedAssignments] = useState([]);
  const [savedCarAssignments, setSavedCarAssignments] = useState([]);

  // Publication state management
  const [publishedDateRange, setPublishedDateRange] = useState(null); // { start_date, end_date, published_at }
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishStartDate, setPublishStartDate] = useState('');
  const [publishEndDate, setPublishEndDate] = useState('');
  const [publishEndMode, setPublishEndMode] = useState('date'); // 'date' or 'week'
  const [publishEndWeek, setPublishEndWeek] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp of last save
  const [lastSaveInfo, setLastSaveInfo] = useState(null); // { user, timestamp, changes: {updated, deleted, created} }
  const [showSaveHistoryModal, setShowSaveHistoryModal] = useState(false);
  const [saveHistoryList, setSaveHistoryList] = useState([]); // Full save history from database
  const [publishHistoryList, setPublishHistoryList] = useState([]); // Full publish history from database
  const [showPublishHistoryModal, setShowPublishHistoryModal] = useState(false);

  // Save history filters and pagination
  const [saveHistoryLimit, setSaveHistoryLimit] = useState(10); // Default: show 10 entries
  const [saveHistorySearchUser, setSaveHistorySearchUser] = useState(''); // Filter by user name
  const [saveHistorySearchProject, setSaveHistorySearchProject] = useState(''); // Filter by project name

  // Real-time collaboration states
  const [hasNewChanges, setHasNewChanges] = useState(false); // Flag when other users made changes
  const [lastCheckedTimestamp, setLastCheckedTimestamp] = useState(null); // Last time we checked for updates
  const [activeUsers, setActiveUsers] = useState([]); // List of currently active users on Resource Board
  const [openChats, setOpenChats] = useState([]); // List of open chat widgets
  const [lastMessageCheck, setLastMessageCheck] = useState(new Date()); // Last time we checked for new messages

  // Refs for values used in intervals to avoid stale closures
  const lastMessageCheckRef = useRef(lastMessageCheck);
  const activeUsersRef = useRef(activeUsers);

  // Update refs when state changes
  useEffect(() => {
    lastMessageCheckRef.current = lastMessageCheck;
  }, [lastMessageCheck]);

  useEffect(() => {
    activeUsersRef.current = activeUsers;
  }, [activeUsers]);

  // Fetch custom tiles from API
  useEffect(() => {
    const fetchCustomTiles = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_BASE_URL}/api/custom-tiles/`, { headers });
        console.log('Fetched custom tiles from API:', response.data);
        // Transform snake_case to camelCase for frontend
        const transformedTiles = response.data.map(tile => ({
          id: tile.id,
          name: tile.name,
          hexColor: tile.hex_color
        }));
        setCustomTiles(transformedTiles);
      } catch (error) {
        console.error('Error fetching custom tiles:', error);
        // If fetch fails, keep empty array
        setCustomTiles([]);
      }
    };

    fetchCustomTiles();
  }, []); // Run once on mount

  // Initialize edit car assignment users when modal opens
  useEffect(() => {
    if (showCarAssignmentEdit && selectedCarAssignment) {
      // Check if this is a NEW assignment being edited (different ID) or the same one
      const isNewAssignment = currentEditingAssignmentId !== selectedCarAssignment.id;

      if (isNewAssignment) {
        // IMPORTANT: Get ORIGINAL dates from savedCarAssignments (last saved to backend)
        // NOT from selectedCarAssignment (which may have been modified by dragging)
        const savedAssignment = savedCarAssignments.find(a => a.id === selectedCarAssignment.id);

        let startDate, endDate;
        if (savedAssignment) {
          // Use dates from last saved state
          startDate = new Date(savedAssignment.startDate);
          endDate = new Date(savedAssignment.endDate);
          console.log('🔵 Using dates from SAVED assignment', selectedCarAssignment.id);
        } else {
          // Fallback to current assignment dates if not found in saved
          startDate = new Date(selectedCarAssignment.startDate);
          endDate = new Date(selectedCarAssignment.endDate);
          console.log('🔵 Using dates from CURRENT assignment', selectedCarAssignment.id, '(not found in saved)');
        }

        console.log('🔵 Setting ORIGINAL dates for assignment', selectedCarAssignment.id, ':', { startDate, endDate });
        setOriginalCarAssignmentDates({ startDate, endDate });
        setCurrentEditingAssignmentId(selectedCarAssignment.id);

        // Find ALL assignments for the same car, project, and overlapping with SAVED dates
        const overlappingAssignments = savedCarAssignments.filter(a => {
          if (a.carId !== selectedCarAssignment.carId || a.projectId !== selectedCarAssignment.projectId) return false;
          const aStart = new Date(a.startDate);
          const aEnd = new Date(a.endDate);
          // Check if date ranges overlap with SAVED dates
          return (aStart <= endDate && aEnd >= startDate);
        });

        console.log('🔵 Found overlapping assignments in SAVED:', overlappingAssignments.length);

        // Collect all unique user IDs from overlapping assignments
        const userIds = overlappingAssignments
          .map(a => a.userId)
          .filter(id => id !== null && id !== undefined);

        const uniqueUserIds = Array.from(new Set(userIds));

        setEditCarAssignmentUsers(uniqueUserIds);
      }
    } else {
      // Modal closed - reset everything
      setEditCarAssignmentUsers([]);
      setEditCarUserSearchQuery('');
      setOriginalCarAssignmentDates(null);
      setCurrentEditingAssignmentId(null);
    }
  }, [showCarAssignmentEdit, selectedCarAssignment, savedCarAssignments, currentEditingAssignmentId]);

  // Temporary blacklist for assignments that return 404 (zombie records)
  // NOTE: This is a frontend workaround. Real fix needs to be in backend!
  const [blacklistedAssignments, setBlacklistedAssignments] = useState(new Set());
  
  // Multiple person selection for copying
  const [selectedCopyUsers, setSelectedCopyUsers] = useState([]); // array of user IDs
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [copyStartDate, setCopyStartDate] = useState(''); // custom start date for copying
  const [copyEndDate, setCopyEndDate] = useState(''); // custom end date for copying

  // Travel planning functionality
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [travelPlanningData, setTravelPlanningData] = useState(null); // { user, date }

  // Travel project selection modal (when multiple projects match)
  const [showTravelProjectModal, setShowTravelProjectModal] = useState(false);
  const [travelProjectSelectionData, setTravelProjectSelectionData] = useState(null); // { matchingProjects, pendingTravelData, travelType }

  // Custom alert/confirm modal for travel warnings
  const [travelAlertModal, setTravelAlertModal] = useState({
    show: false,
    type: 'alert', // 'alert' or 'confirm'
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  // Assignment travel planning modal
  const [assignmentTravelModal, setAssignmentTravelModal] = useState({
    show: false,
    assignment: null,
    checkInOption: 'day_before', // 'day_before', 'first_day', 'none'
    checkOutOption: 'day_after'  // 'day_after', 'last_day', 'none'
  });

  // Quick assignment modal functionality
  const [showQuickAssignmentModal, setShowQuickAssignmentModal] = useState(false);
  const [quickAssignmentData, setQuickAssignmentData] = useState(null); // { userId, userName, date }

  // PDF Extract modal
  const [showPdfExtractModal, setShowPdfExtractModal] = useState(false);
  const [pdfExtractConfig, setPdfExtractConfig] = useState({
    projectIds: [], // Changed from projectId to projectIds (array)
    mode: 'weeks', // 'weeks' or 'dates'
    startWeek: '',
    endWeek: '',
    startYear: '', // Year for start week
    endYear: '', // Year for end week
    startWeekNumber: '', // Week number (1-52) for start
    endWeekNumber: '', // Week number (1-52) for end
    startDate: '',
    endDate: '',
    includeUsers: true,
    includeCars: false,
    sortBy: 'alphabet', // 'alphabet' or 'date'
    orientation: 'portrait', // 'portrait' or 'landscape'
    selectedUserIds: [], // Array of selected user IDs
    sendEmail: false, // Send PDF via email (only for single user)
    userSearchQuery: '', // Search query for filtering users
    projectSearchQuery: '', // Search query for filtering projects
    multiProjectLayout: 'by-name' // 'by-name' (alphabetically) or 'by-project' (project under project)
  });

  // Status bar functionality
  const [currentStatus, setCurrentStatus] = useState(null);
  
  // Travel tooltip functionality
  const [showTravelTooltip, setShowTravelTooltip] = useState(false);
  const [selectedTravel, setSelectedTravel] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);

  // Note tooltip functionality
  const [showNoteTooltip, setShowNoteTooltip] = useState(false);
  const [selectedNoteData, setSelectedNoteData] = useState(null); // { notes, user, date }
  const [noteTooltipPosition, setNoteTooltipPosition] = useState({ x: 0, y: 0 });
  const [showConfirmNoteDelete, setShowConfirmNoteDelete] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const noteTooltipTimeoutRef = useRef(null);

  // Car rental edit modal
  const [showCarRentalModal, setShowCarRentalModal] = useState(false);
  const [selectedCarForEdit, setSelectedCarForEdit] = useState(null);
  const [carRentalFormData, setCarRentalFormData] = useState({
    ownership_type: 'own',
    rental_start_date: '',
    rental_end_date: '',
    daily_rental_cost: ''
  });
  
  // UI states
  const [showGlobalRoles, setShowGlobalRoles] = useState(false);

  // View toggles for Personnel section
  const [showPeople, setShowPeople] = useState(true);
  const [showCars, setShowCars] = useState(false); // Hidden by default
  const [showEquipment, setShowEquipment] = useState(false); // Hidden by default
  const [showDisabledUsers, setShowDisabledUsers] = useState(false); // Hidden by default

  // Hidden users tracking (loaded from database via API)
  const [disabledUsers, setDisabledUsers] = useState([]); // Array of hidden user IDs - loaded from API based on hidden_on_board field

  // Row highlighting (for better visual targeting)
  const [highlightedRowId, setHighlightedRowId] = useState(null); // ID of highlighted user or car row

  // Search states for filters
  const [peopleSearch, setPeopleSearch] = useState('');
  const [projectsSearch, setProjectsSearch] = useState('');
  const [carsSearch, setCarsSearch] = useState('');
  const [customersSearch, setCustomersSearch] = useState('');
  const [rolesSearch, setRolesSearch] = useState('');
  const [selectedQuickUsers, setSelectedQuickUsers] = useState([]); // Array of selected users for quick filter
  const [quickUserSearchInput, setQuickUserSearchInput] = useState(''); // Input for searching users
  const [showQuickUserDropdown, setShowQuickUserDropdown] = useState(false);
  const [rentalAlertDismissed, setRentalAlertDismissed] = useState(false);
  
  // Right-click context menu for travel
  const [showTravelContextMenu, setShowTravelContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuData, setContextMenuData] = useState(null); // { user, date }

  // Notes functionality
  const [notes, setNotes] = useState(() => {
    const savedNotes = localStorage.getItem('resourceBoardNotes');
    return savedNotes ? JSON.parse(savedNotes) : [];
  }); // Array of notes: { id, userId, date, content, createdAt }
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteModalData, setNoteModalData] = useState({ user: null, date: null });
  const [noteInput, setNoteInput] = useState('');

  // Delete confirmation modal
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [pendingDeleteAssignmentId, setPendingDeleteAssignmentId] = useState(null);

  // User management modal
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [selectedUserForManagement, setSelectedUserForManagement] = useState(null);

  // Undo/Redo functionality
  const [undoStack, setUndoStack] = useState([]); // Array of operations that can be undone
  const [redoStack, setRedoStack] = useState([]); // Array of operations that can be redone
  const MAX_UNDO_STEPS = 10;

  // Helper functions for pending changes management
  const addPendingUpdate = (assignment, originalAssignment = null) => {
    // Store original state for undo if provided
    if (originalAssignment) {
      addToUndoStack({
        type: 'PENDING_UPDATE',
        assignmentId: assignment.id,
        originalData: { ...originalAssignment },
        newData: { ...assignment }
      });
    }

    // Update local state immediately for UI responsiveness
    setAssignments(prev => prev.map(a => a.id === assignment.id ? assignment : a));

    setPendingChanges(prev => {
      // Check if this is a new assignment (temporary ID or _isNew flag)
      const isNewAssignment = assignment.id?.toString().startsWith('temp_') || assignment._isNew;

      if (isNewAssignment) {
        // Update in the created list instead of adding to updated
        const existingCreatedIndex = prev.created.findIndex(a => a.id === assignment.id);

        if (existingCreatedIndex >= 0) {
          // Replace existing in created list
          const newCreated = [...prev.created];
          newCreated[existingCreatedIndex] = { ...assignment, _isNew: true };
          return { ...prev, created: newCreated };
        }

        // If not found in created, add it there
        return { ...prev, created: [...prev.created, { ...assignment, _isNew: true }] };
      }

      // For existing assignments, handle normally
      const existingIndex = prev.updated.findIndex(a => a.id === assignment.id);

      if (existingIndex >= 0) {
        // Replace existing pending update
        const newUpdated = [...prev.updated];
        newUpdated[existingIndex] = assignment;
        return { ...prev, updated: newUpdated };
      }

      // Add new pending update
      return { ...prev, updated: [...prev.updated, assignment] };
    });

    setHasUnsavedChanges(true);
  };

  const addPendingDelete = (assignmentId) => {
    // Store assignment data for undo before removing
    const assignmentToDelete = (Array.isArray(assignments) ? assignments : []).find(a => a.id === assignmentId);
    if (assignmentToDelete) {
      addToUndoStack({
        type: 'PENDING_DELETE',
        assignmentId: assignmentId,
        data: { ...assignmentToDelete }
      });
    }

    // Remove from local state immediately for UI responsiveness
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));

    setPendingChanges(prev => ({
      ...prev,
      deleted: [...prev.deleted, assignmentId],
      // Also remove from updated list if it was there
      updated: prev.updated.filter(a => a.id !== assignmentId)
    }));

    setHasUnsavedChanges(true);
  };

  const addPendingCreate = (assignment) => {
    // Add to local state immediately with temporary ID
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const newAssignment = { ...assignment, id: tempId, _isNew: true };

    // Add to undo stack so we can undo the creation
    addToUndoStack({
      type: 'PENDING_CREATE',
      assignmentId: tempId,
      data: { ...newAssignment }
    });

    console.log('Adding to pending creates:', {
      tempId,
      isSpecialTile: newAssignment.isSpecialTile,
      projectName: newAssignment.projectName,
      hexColor: newAssignment.hexColor,
      projectId: newAssignment.projectId
    });

    setAssignments(prev => [...prev, newAssignment]);
    setPendingChanges(prev => ({
      ...prev,
      created: [...prev.created, newAssignment]
    }));

    setHasUnsavedChanges(true);
  };

  // Car assignment pending changes functions
  const addPendingCarUpdate = (carAssignment) => {
    // Update local state immediately for UI responsiveness
    setCarAssignments(prev => prev.map(a => a.id === carAssignment.id ? carAssignment : a));

    setPendingCarChanges(prev => {
      // Check if this is a new car assignment (temporary ID or _isNew flag)
      const isNewCarAssignment = carAssignment.id?.toString().startsWith('temp_car_') || carAssignment._isNew;

      if (isNewCarAssignment) {
        // Update in the created list instead of adding to updated
        const existingCreatedIndex = prev.created.findIndex(a => a.id === carAssignment.id);

        if (existingCreatedIndex >= 0) {
          // Replace existing in created list
          const newCreated = [...prev.created];
          newCreated[existingCreatedIndex] = { ...carAssignment, _isNew: true };
          return { ...prev, created: newCreated };
        }

        // If not found in created, add it there
        return { ...prev, created: [...prev.created, { ...carAssignment, _isNew: true }] };
      }

      // For existing assignments, handle normally
      const existingIndex = prev.updated.findIndex(a => a.id === carAssignment.id);

      if (existingIndex >= 0) {
        // Replace existing pending update
        const newUpdated = [...prev.updated];
        newUpdated[existingIndex] = carAssignment;
        return { ...prev, updated: newUpdated };
      }

      // Add new pending update
      return { ...prev, updated: [...prev.updated, carAssignment] };
    });

    setHasUnsavedChanges(true);
  };

  const addPendingCarDelete = (assignmentId) => {
    // Remove from local state immediately for UI responsiveness
    setCarAssignments(prev => prev.filter(a => a.id !== assignmentId));

    setPendingCarChanges(prev => ({
      ...prev,
      deleted: [...prev.deleted, assignmentId],
      // Also remove from updated list if it was there
      updated: prev.updated.filter(a => a.id !== assignmentId)
    }));

    setHasUnsavedChanges(true);
  };

  const addPendingCarCreate = (carAssignment) => {
    // Add to local state immediately with temporary ID
    const tempId = `temp_car_${Date.now()}_${Math.random()}`;
    const newCarAssignment = { ...carAssignment, id: tempId, _isNew: true };

    setCarAssignments(prev => [...prev, newCarAssignment]);
    setPendingCarChanges(prev => ({
      ...prev,
      created: [...prev.created, newCarAssignment]
    }));

    setHasUnsavedChanges(true);
  };

  const saveAllChanges = async () => {
    console.log('=== SAVE ALL CHANGES STARTED ===');
    console.log('Pending changes:', pendingChanges);

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const promises = [];

      // NOTE: Some changes (like drag & drop) save automatically via resourceBoardService
      // BUT resize/manual edits for USER assignments use pendingChanges and need to be saved here

      // Delete assignments
      for (const id of pendingChanges.deleted) {
        console.log('Deleting assignment:', id);
        // User assignments use /api/resource-planning-assignments/
        promises.push(
          axios.delete(`${API_BASE_URL}/api/resource-planning-assignments/${id}/`, { headers })
            .catch(err => console.error(`Error deleting assignment ${id}:`, err))
        );
      }

      // Update assignments (e.g., from resize operations on user assignments)
      for (const assignment of pendingChanges.updated) {
        const { _isNew, userId, projectId, startDate, endDate, role, hexColor, isSpecialTile, projectName, ...rest } = assignment;

        // Prepare data for API (use snake_case)
        const assignmentData = {
          user: userId,
          // For special tiles, project is null
          project: isSpecialTile ? null : projectId,
          start_date: formatDateForAPI(startDate),
          end_date: formatDateForAPI(endDate),
          role: role,
          comment: assignmentComments[assignment.id] || null,
          // Custom tile fields
          is_special_tile: isSpecialTile || false,
          hex_color: hexColor || null,
          custom_name: projectName || null
        };

        // User assignments use /api/resource-planning-assignments/
        promises.push(
          axios.put(`${API_BASE_URL}/api/resource-planning-assignments/${assignment.id}/`, assignmentData, { headers })
            .catch(err => console.error(`Error updating assignment ${assignment.id}:`, err))
        );
      }

      // Create new assignments and collect mapping from temp ID to real ID
      const createdAssignmentsMap = new Map(); // tempId -> real assignment data

      for (const assignment of pendingChanges.created) {
        const { id, _isNew, userId, projectId, startDate, endDate, role, hexColor, isSpecialTile, projectName, ...rest } = assignment;

        // Prepare data for API (use snake_case)
        const assignmentData = {
          user: userId,
          // For special tiles, project is null (custom tiles have fake IDs like "custom-123")
          project: isSpecialTile ? null : projectId,
          start_date: startDate instanceof Date ? formatDateForAPI(startDate) : startDate,
          end_date: endDate instanceof Date ? formatDateForAPI(endDate) : endDate,
          role: role,
          comment: assignmentComments[id] || null,
          // Custom tile fields
          is_special_tile: isSpecialTile || false,
          hex_color: hexColor || null,
          custom_name: projectName || null
        };

        console.log('Creating assignment with data:', assignmentData);

        // User assignments use /api/resource-planning-assignments/
        promises.push(
          axios.post(`${API_BASE_URL}/api/resource-planning-assignments/`, assignmentData, { headers })
            .then(response => {
              console.log('Assignment created successfully:', response.data);
              // Store mapping from temp ID to server response
              createdAssignmentsMap.set(id, response.data);
              return response;
            })
            .catch(err => {
              console.error(`Error creating assignment:`, err);
              console.error('Error response:', err.response?.data);
              throw err;
            })
        );
      }

      // NOTE: Car assignments are already saved to database automatically during drag/drop/resize
      // We only track them in pendingCarChanges for history purposes
      // So we DON'T save them again here, just include them in history below

      await Promise.all(promises);

      // Collect detailed change information
      const createdDetails = pendingChanges.created.map(assignment => {
        // Support both camelCase (carId) and snake_case (car) for compatibility
        const carId = assignment.car_id || assignment.carId || assignment.car;
        const projectId = assignment.project_id || assignment.projectId || assignment.project;
        const userId = assignment.user_id || assignment.userId || assignment.user;
        const startDate = assignment.start_date || assignment.startDate;
        const endDate = assignment.end_date || assignment.endDate;

        const car = carId ? carsData.find(c => c.id === carId) : null;
        const project = projects.find(p => p.id === projectId);
        const user = users.find(u => u.id === userId);

        // Determine display label based on assignment type
        let displayLabel = '';
        if (car) {
          // Car assignment
          displayLabel = `${car.car_brand} ${car.car_model} (${car.plate_number})`;
        } else if (user && project) {
          // User assignment
          displayLabel = `${user.first_name} ${user.last_name} → ${project.name}`;
        } else if (project) {
          displayLabel = `Project: ${project.name}`;
        } else if (user) {
          displayLabel = `${user.first_name} ${user.last_name}`;
        } else {
          displayLabel = 'New Assignment';
        }

        return {
          id: assignment.id,
          displayLabel: displayLabel,
          project: project?.name || `Project #${projectId || 'Unknown'}`,
          user: user ? `${user.first_name} ${user.last_name}` : `User #${userId || 'Unknown'}`,
          start_date: startDate,
          end_date: endDate
        };
      });

      const updatedDetails = pendingChanges.updated.map(assignment => {
        const originalAssignment = savedAssignments.find(a => a.id === assignment.id);

        // Support both camelCase and snake_case
        const carId = assignment.car_id || assignment.carId || assignment.car;
        const projectId = assignment.project_id || assignment.projectId || assignment.project;
        const userId = assignment.user_id || assignment.userId || assignment.user;
        const startDate = assignment.start_date || assignment.startDate;
        const endDate = assignment.end_date || assignment.endDate;

        const car = carsData.find(c => c.id === carId);
        const project = projects.find(p => p.id === projectId);
        const user = users.find(u => u.id === userId);

        const changes = [];
        let displayLabel = '';

        if (originalAssignment) {
          const origCarId = originalAssignment.car_id || originalAssignment.carId || originalAssignment.car;
          const origProjectId = originalAssignment.project_id || originalAssignment.projectId || originalAssignment.project;
          const origUserId = originalAssignment.user_id || originalAssignment.userId || originalAssignment.user;
          const origStartDate = originalAssignment.start_date || originalAssignment.startDate;
          const origEndDate = originalAssignment.end_date || originalAssignment.endDate;

          // Determine what changed and create appropriate display label
          const carChanged = origCarId !== carId;
          const projectChanged = origProjectId !== projectId;
          const userChanged = origUserId !== userId;
          const datesChanged = origStartDate !== startDate || origEndDate !== endDate;

          // Build display label based on what changed
          if (carChanged && car) {
            displayLabel = `${car.car_brand} ${car.car_model} (${car.plate_number})`;
          } else if (projectChanged && project) {
            displayLabel = `Project: ${project.name}`;
          } else if (userChanged && user) {
            displayLabel = `${user.first_name} ${user.last_name}`;
          } else if (car) {
            displayLabel = `${car.car_brand} ${car.car_model} (${car.plate_number})`;
          } else if (project && user) {
            displayLabel = `${user.first_name} ${user.last_name} → ${project.name}`;
          } else if (project) {
            displayLabel = `Project: ${project.name}`;
          } else if (user) {
            displayLabel = `${user.first_name} ${user.last_name}`;
          } else {
            displayLabel = `Assignment #${assignment.id}`;
          }

          // Track detailed changes
          if (origStartDate !== startDate) {
            changes.push(`start date: ${origStartDate} → ${startDate}`);
          }
          if (origEndDate !== endDate) {
            changes.push(`end date: ${origEndDate} → ${endDate}`);
          }
          if (origProjectId !== projectId) {
            const oldProj = projects.find(p => p.id === origProjectId);
            const newProj = projects.find(p => p.id === projectId);
            changes.push(`project: ${oldProj?.name || 'Unknown'} → ${newProj?.name || 'Unknown'}`);
          }
          if (origUserId !== userId) {
            const oldUser = users.find(u => u.id === origUserId);
            const newUser = users.find(u => u.id === userId);
            changes.push(`user: ${oldUser ? oldUser.first_name + ' ' + oldUser.last_name : 'Unknown'} → ${newUser ? newUser.first_name + ' ' + newUser.last_name : 'Unknown'}`);
          }
          if (carChanged) {
            const oldCar = carsData.find(c => c.id === origCarId);
            const newCar = carsData.find(c => c.id === carId);
            changes.push(`car: ${oldCar ? `${oldCar.car_brand} ${oldCar.car_model}` : 'Unknown'} → ${newCar ? `${newCar.car_brand} ${newCar.car_model}` : 'Unknown'}`);
          }
        }

        return {
          id: assignment.id,
          displayLabel: displayLabel || 'Unknown',
          changes: changes.length > 0 ? changes : ['updated']
        };
      });

      // Add car assignment details to the details arrays
      const carCreatedDetails = pendingCarChanges.created.map(carAssignment => {
        const carId = carAssignment.carId;
        const projectId = carAssignment.projectId;
        const startDate = carAssignment.startDate instanceof Date ? formatDateForAPI(carAssignment.startDate) : carAssignment.startDate;
        const endDate = carAssignment.endDate instanceof Date ? formatDateForAPI(carAssignment.endDate) : carAssignment.endDate;

        const car = carsData.find(c => c.id === carId);
        const project = projects.find(p => p.id === projectId);

        const displayLabel = car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : 'New Car Assignment';

        return {
          id: carAssignment.id,
          displayLabel: displayLabel,
          project: project?.name || `Project #${projectId || 'Unknown'}`,
          car: car ? `${car.car_brand} ${car.car_model}` : `Car #${carId || 'Unknown'}`,
          start_date: startDate,
          end_date: endDate
        };
      });

      const carUpdatedDetails = pendingCarChanges.updated.map(carAssignment => {
        const originalCarAssignment = savedCarAssignments.find(a => a.id === carAssignment.id);

        const carId = carAssignment.carId;
        const projectId = carAssignment.projectId;
        const startDate = carAssignment.startDate instanceof Date ? formatDateForAPI(carAssignment.startDate) : carAssignment.startDate;
        const endDate = carAssignment.endDate instanceof Date ? formatDateForAPI(carAssignment.endDate) : carAssignment.endDate;

        const car = carsData.find(c => c.id === carId);
        const project = projects.find(p => p.id === projectId);

        const displayLabel = car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : `Car Assignment #${carAssignment.id}`;

        const changes = [];

        if (originalCarAssignment) {
          const origStartDate = originalCarAssignment.startDate instanceof Date ? formatDateForAPI(originalCarAssignment.startDate) : originalCarAssignment.startDate;
          const origEndDate = originalCarAssignment.endDate instanceof Date ? formatDateForAPI(originalCarAssignment.endDate) : originalCarAssignment.endDate;
          const origProjectId = originalCarAssignment.projectId;
          const origCarId = originalCarAssignment.carId;

          // Track detailed changes
          if (origStartDate !== startDate) {
            changes.push(`start date: ${origStartDate} → ${startDate}`);
          }
          if (origEndDate !== endDate) {
            changes.push(`end date: ${origEndDate} → ${endDate}`);
          }
          if (origProjectId !== projectId) {
            const oldProj = projects.find(p => p.id === origProjectId);
            const newProj = projects.find(p => p.id === projectId);
            changes.push(`project: ${oldProj?.name || 'Unknown'} → ${newProj?.name || 'Unknown'}`);
          }
          if (origCarId !== carId) {
            const oldCar = carsData.find(c => c.id === origCarId);
            const newCar = carsData.find(c => c.id === carId);
            changes.push(`car: ${oldCar ? `${oldCar.car_brand} ${oldCar.car_model}` : 'Unknown'} → ${newCar ? `${newCar.car_brand} ${newCar.car_model}` : 'Unknown'}`);
          }
        }

        return {
          id: carAssignment.id,
          displayLabel: displayLabel,
          changes: changes.length > 0 ? changes : ['updated']
        };
      });

      const carDeletedDetails = pendingCarChanges.deleted.map(id => {
        const carAssignment = carAssignments.find(a => a.id === id);
        if (carAssignment) {
          const car = carsData.find(c => c.id === carAssignment.carId);
          const project = projects.find(p => p.id === carAssignment.projectId);
          const startDate = carAssignment.startDate instanceof Date ? formatDateForAPI(carAssignment.startDate) : carAssignment.startDate;
          const endDate = carAssignment.endDate instanceof Date ? formatDateForAPI(carAssignment.endDate) : carAssignment.endDate;

          const displayLabel = car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : 'Deleted Car Assignment';

          return {
            id: id,
            displayLabel: displayLabel,
            project: project?.name || 'Unknown',
            car: car ? `${car.car_brand} ${car.car_model}` : 'Unknown',
            dates: `${startDate} - ${endDate}`
          };
        }
        return { id: id, displayLabel: 'Deleted car assignment' };
      });

      const deletedDetails = Array.from(pendingChanges.deleted).map(id => {
        const assignment = savedAssignments.find(a => a.id === id);
        if (assignment) {
          const carId = assignment.car_id || assignment.carId || assignment.car;
          const projectId = assignment.project_id || assignment.projectId || assignment.project;
          const userId = assignment.user_id || assignment.userId || assignment.user;
          const startDate = assignment.start_date || assignment.startDate;
          const endDate = assignment.end_date || assignment.endDate;

          const car = carId ? carsData.find(c => c.id === carId) : null;
          const project = projects.find(p => p.id === projectId);
          const user = users.find(u => u.id === userId);

          // Determine display label based on assignment type
          let displayLabel = '';
          if (car) {
            // Car assignment
            displayLabel = `${car.car_brand} ${car.car_model} (${car.plate_number})`;
          } else if (user && project) {
            // User assignment
            displayLabel = `${user.first_name} ${user.last_name} → ${project.name}`;
          } else if (project) {
            displayLabel = `Project: ${project.name}`;
          } else if (user) {
            displayLabel = `${user.first_name} ${user.last_name}`;
          } else {
            displayLabel = 'Deleted Assignment';
          }

          return {
            id: id,
            displayLabel: displayLabel,
            project: project?.name || `Project #${projectId || 'Unknown'}`,
            user: user ? `${user.first_name} ${user.last_name}` : `User #${userId || 'Unknown'}`,
            dates: `${startDate} - ${endDate}`
          };
        }
        return { id: id, displayLabel: 'Deleted assignment' };
      });

      // Combine user and car assignment details
      const allCreatedDetails = [...createdDetails, ...carCreatedDetails];
      const allUpdatedDetails = [...updatedDetails, ...carUpdatedDetails];
      const allDeletedDetails = [...deletedDetails, ...carDeletedDetails];

      // Save history to database
      try {
        console.log('Saving history to database...', {
          url: `${API_BASE_URL}/api/resource-plan-save-history/`,
          data: {
            assignments_created: pendingChanges.created.length + pendingCarChanges.created.length,
            assignments_updated: pendingChanges.updated.length + pendingCarChanges.updated.length,
            assignments_deleted: pendingChanges.deleted.length + pendingCarChanges.deleted.length
          }
        });
        const historyResponse = await axios.post(`${API_BASE_URL}/api/resource-plan-save-history/`, {
          assignments_created: pendingChanges.created.length + pendingCarChanges.created.length,
          assignments_updated: pendingChanges.updated.length + pendingCarChanges.updated.length,
          assignments_deleted: pendingChanges.deleted.length + pendingCarChanges.deleted.length,
          change_details: {
            created: allCreatedDetails,
            updated: allUpdatedDetails,
            deleted: allDeletedDetails
          }
        }, { headers });
        console.log('History saved successfully:', historyResponse.data);

        // Use the saved history data for display
        const savedHistory = historyResponse.data;
        setLastSavedAt(new Date(savedHistory.saved_at));

        // Prefer first_name + last_name, fallback to username or email
        let userName = 'Unknown User';
        if (savedHistory.saved_by_data?.first_name && savedHistory.saved_by_data?.last_name) {
          userName = `${savedHistory.saved_by_data.first_name} ${savedHistory.saved_by_data.last_name}`;
        } else if (savedHistory.saved_by_data?.username) {
          userName = savedHistory.saved_by_data.username;
        } else if (savedHistory.saved_by_data?.email) {
          userName = savedHistory.saved_by_data.email;
        }

        setLastSaveInfo({
          user: userName,
          timestamp: new Date(savedHistory.saved_at),
          changes: {
            updated: savedHistory.assignments_updated,
            deleted: savedHistory.assignments_deleted,
            created: savedHistory.assignments_created
          }
        });
      } catch (err) {
        console.error('Error saving history:', err);
        console.error('Error details:', err.response?.data);
        // Fallback to showing basic info if history save fails
        const now = new Date();
        setLastSavedAt(now);
        setLastSaveInfo({
          user: 'Unknown User',
          timestamp: now,
          changes: {
            updated: pendingChanges.updated.length,
            deleted: pendingChanges.deleted.length,
            created: pendingChanges.created.length
          }
        });
      }

      // Update assignments in state with real IDs from server
      setAssignments(prev => prev.map(assignment => {
        // If this is a temporary assignment that was just created
        if (createdAssignmentsMap.has(assignment.id)) {
          const serverData = createdAssignmentsMap.get(assignment.id);
          // Parse dates from server response
          const parseDateSafe = (dateString) => {
            if (!dateString) return null;
            const datePart = dateString.split('T')[0];
            const [year, month, day] = datePart.split('-').map(Number);
            return new Date(year, month - 1, day);
          };

          // Replace temp assignment with server data
          return {
            id: serverData.id, // Real ID from server
            userId: serverData.user,
            projectId: serverData.project,
            startDate: parseDateSafe(serverData.start_date),
            endDate: parseDateSafe(serverData.end_date),
            role: serverData.role || 'Team Member',
            isSpecialTile: serverData.is_special_tile || false,
            hexColor: serverData.hex_color || null,
            projectName: serverData.custom_name || null
          };
        }
        return assignment;
      }));

      // Reset pending changes
      setPendingChanges({ updated: [], deleted: [], created: [] });
      setPendingCarChanges({ updated: [], deleted: [], created: [] });
      setHasUnsavedChanges(false);
      setSavedAssignments(prev => prev.map(assignment => {
        if (createdAssignmentsMap.has(assignment.id)) {
          const serverData = createdAssignmentsMap.get(assignment.id);
          const parseDateSafe = (dateString) => {
            if (!dateString) return null;
            const datePart = dateString.split('T')[0];
            const [year, month, day] = datePart.split('-').map(Number);
            return new Date(year, month - 1, day);
          };
          return {
            id: serverData.id,
            userId: serverData.user,
            projectId: serverData.project,
            startDate: parseDateSafe(serverData.start_date),
            endDate: parseDateSafe(serverData.end_date),
            role: serverData.role || 'Team Member',
            isSpecialTile: serverData.is_special_tile || false,
            hexColor: serverData.hex_color || null,
            projectName: serverData.custom_name || null
          };
        }
        return assignment;
      }));
      setSavedCarAssignments([...carAssignments]);
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes. Please try again.');
    }
  };

  const cancelAllChanges = () => {
    // Revert to saved state
    setAssignments([...savedAssignments]);
    setCarAssignments([...savedCarAssignments]);
    setPendingChanges({ updated: [], deleted: [], created: [] });
    setPendingCarChanges({ updated: [], deleted: [], created: [] });
    setHasUnsavedChanges(false);
  };

  const fetchSaveHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      console.log('Fetching save history from:', `${API_BASE_URL}/api/resource-plan-save-history/`);
      const response = await axios.get(`${API_BASE_URL}/api/resource-plan-save-history/`, { headers });
      console.log('Save history response:', response.data);
      setSaveHistoryList(response.data);
    } catch (error) {
      console.error('Error fetching save history:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchPublishHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/resource-plan-publish-history/`, { headers });
      setPublishHistoryList(response.data);
    } catch (error) {
      console.error('Error fetching publish history:', error);
    }
  };

  const fetchCurrentPublication = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/resource-plan-publish-history/`, { headers });

      // Get the most recent publication
      if (response.data && response.data.length > 0) {
        const latestPublication = response.data[0];
        setPublishedDateRange({
          start_date: latestPublication.start_date,
          end_date: latestPublication.end_date,
          published_at: latestPublication.published_at ? new Date(latestPublication.published_at) : null
        });
      }
    } catch (error) {
      console.error('Error fetching current publication:', error);
    }
  };

  const checkForNewChanges = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch only the most recent save history entry
      const response = await axios.get(`${API_BASE_URL}/api/resource-plan-save-history/recent/`, { headers });

      if (response.data && response.data.length > 0) {
        const latestSave = response.data[0];
        const latestTimestamp = new Date(latestSave.saved_at);

        // Check if this is a newer save than what we currently have
        if (lastSaveInfo) {
          const currentTimestamp = new Date(lastSaveInfo.timestamp);

          // If there's a newer save AND it's not from the current user
          if (latestTimestamp > currentTimestamp && latestSave.saved_by !== localStorage.getItem('user_id')) {
            setHasNewChanges(true);

            // Update last save info to show the latest
            const fullName = latestSave.saved_by_data?.first_name && latestSave.saved_by_data?.last_name
              ? `${latestSave.saved_by_data.first_name} ${latestSave.saved_by_data.last_name}`
              : latestSave.saved_by_data?.username || latestSave.saved_by_data?.email || 'Unknown User';

            setLastSaveInfo({
              user: fullName,
              timestamp: latestTimestamp,
              changes: {
                updated: latestSave.assignments_updated,
                deleted: latestSave.assignments_deleted,
                created: latestSave.assignments_created
              }
            });
          }
        } else {
          // First time loading - just set the info without notification
          const fullName = latestSave.saved_by_data?.first_name && latestSave.saved_by_data?.last_name
            ? `${latestSave.saved_by_data.first_name} ${latestSave.saved_by_data.last_name}`
            : latestSave.saved_by_data?.username || latestSave.saved_by_data?.email || 'Unknown User';

          setLastSaveInfo({
            user: fullName,
            timestamp: new Date(latestSave.saved_at),
            changes: {
              updated: latestSave.assignments_updated,
              deleted: latestSave.assignments_deleted,
              created: latestSave.assignments_created
            }
          });
        }

        setLastCheckedTimestamp(new Date());
      }
    } catch (error) {
      console.error('Error checking for new changes:', error);
    }
  };

  // Send heartbeat to mark user as active on Resource Board
  const sendHeartbeat = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API_BASE_URL}/api/resource-board-activity/heartbeat/`, {}, { headers });
    } catch (error) {
      // Silently fail - heartbeat is not critical for functionality
      // Only log if it's not a 500 error (which indicates server-side issue)
      if (error.response?.status !== 500) {
        console.error('Error sending heartbeat:', error);
      }
    }
  };

  // Fetch list of currently active users on Resource Board
  const fetchActiveUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE_URL}/api/resource-board-activity/active/`, { headers });

      if (response.data) {
        setActiveUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching active users:', error);
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple notification beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Get current user ID from JWT token
  const getCurrentUserId = () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    try {
      // Decode JWT token to get user_id
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      return payload.user_id || payload.sub || payload.id;
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  };

  // Check for new messages and auto-open chat windows - using last_message tracking
  const checkForNewMessages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const currentUserId = getCurrentUserId();

      // Only check if there are active users
      if (!activeUsersRef.current || activeUsersRef.current.length === 0) {
        return;
      }

      // Get last checked message IDs from localStorage
      const lastCheckedKey = `lastCheckedMessages_user${currentUserId}`;
      const lastCheckedMessages = JSON.parse(localStorage.getItem(lastCheckedKey) || '{}');

      // Fetch all conversations
      const response = await axios.get(`${API_BASE_URL}/api/conversations/`, { headers });

      if (response.data && response.data.length > 0) {
        const sendersWithNewMessages = [];
        const newLastCheckedMessages = { ...lastCheckedMessages };

        // Check only conversations with active users
        const activeUserIds = activeUsersRef.current.map(u => u.user_data.id);

        for (const conversation of response.data) {
          const otherParticipantId = conversation.participant_ids?.find(
            id => parseInt(id) !== parseInt(currentUserId)
          );

          // Skip if no other participant or participant not active
          if (!otherParticipantId || !activeUserIds.includes(parseInt(otherParticipantId))) {
            continue;
          }

          // Check if conversation has a last message
          if (conversation.last_message) {
            const lastMsg = conversation.last_message;
            const lastMsgId = lastMsg.id;
            const lastCheckedId = lastCheckedMessages[conversation.id];

            // Check if this is a new message from someone else
            const isNewMsg = lastMsgId !== lastCheckedId;
            const isFromOther = parseInt(lastMsg.sender_id) !== parseInt(currentUserId);

            // Debug logging
            if (isNewMsg && !isFromOther) {
              console.log('[Chat] Skipping own message:', {
                msgId: lastMsgId,
                senderId: lastMsg.sender_id,
                myId: currentUserId
              });
            }

            if (isNewMsg && isFromOther) {
              console.log('[Chat] New message detected!', {
                msgId: lastMsgId,
                from: lastMsg.sender_id,
                myId: currentUserId,
                otherUser: otherParticipantId
              });

              // Find the SENDER of the message in active users (not otherParticipant!)
              const sender = activeUsersRef.current.find(
                user => user.user_data.id === parseInt(lastMsg.sender_id)
              );

              if (sender) {
                console.log('[Chat] Sender found:', sender.user_data.first_name);
                const alreadyInList = sendersWithNewMessages.find(
                  s => s.user_data.id === sender.user_data.id
                );
                if (!alreadyInList) {
                  sendersWithNewMessages.push(sender);
                }
              } else {
                console.log('[Chat] Sender NOT in active users, sender_id:', lastMsg.sender_id);
              }
            }

            // Update last checked message for this conversation
            newLastCheckedMessages[conversation.id] = lastMsgId;
          }
        }

        // Save updated last checked messages
        localStorage.setItem(lastCheckedKey, JSON.stringify(newLastCheckedMessages));

        // Open chats for all senders with new messages
        if (sendersWithNewMessages.length > 0) {
          console.log('[Chat] Opening chats for users with new messages:',
            sendersWithNewMessages.map(s => s.user_data.first_name).join(', '));

          setOpenChats(currentChats => {
            const newChats = [...currentChats];

            sendersWithNewMessages.forEach(sender => {
              const chatAlreadyOpen = newChats.find(
                chat => chat.user_data.id === sender.user_data.id
              );

              if (!chatAlreadyOpen) {
                newChats.push(sender);
              }
            });

            return newChats;
          });

          // Play notification sound once
          playNotificationSound();
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error checking for new messages:', error);
      }
    }
  };

  const handlePublish = async () => {
    if (!publishEndDate) {
      alert('Please select an end date for publication.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Use a very early start date to include all historic data
      const historicStartDate = '2010-01-01';

      // Get assignments up to the selected end date (all historic data)
      const publishedAssignments = assignments.filter(assignment => {
        const assignmentStart = new Date(assignment.startDate);
        const assignmentEnd = new Date(assignment.endDate);
        const rangeEnd = new Date(publishEndDate);

        // Check if assignment ends before or on the publish end date
        return assignmentStart <= rangeEnd;
      });

      // Prepare published data with user details
      const publishedData = publishedAssignments.map(assignment => {
        const user = users.find(u => u.id === assignment.userId);
        const project = projects.find(p => p.id === assignment.projectId);

        return {
          user_id: assignment.userId,
          user_name: user ? `${user.first_name} ${user.last_name}` : 'Unknown',
          project_id: assignment.projectId,
          project_name: assignment.isSpecialTile
            ? (assignment.projectName || 'Custom Task')
            : (project?.name || 'Unknown Project'),
          start_date: assignment.startDate instanceof Date
            ? formatDateForAPI(assignment.startDate)
            : assignment.startDate,
          end_date: assignment.endDate instanceof Date
            ? formatDateForAPI(assignment.endDate)
            : assignment.endDate,
          role: assignment.role,
          // Custom tile fields
          is_special_tile: assignment.isSpecialTile || false,
          custom_name: assignment.projectName || null,
          hex_color: assignment.hexColor || null
        };
      });

      // Save publish history to database
      await axios.post(`${API_BASE_URL}/api/resource-plan-publish-history/`, {
        start_date: historicStartDate,
        end_date: publishEndDate,
        published_data: {
          total_assignments: publishedAssignments.length,
          date_range: `All historic data until ${publishEndDate}`,
          assignments: publishedData
        }
      }, { headers });

      const now = new Date();
      setPublishedDateRange({
        start_date: historicStartDate,
        end_date: publishEndDate,
        published_at: now
      });
      setShowPublishModal(false);

      alert(`Schedule published for mobile users!\nAll historic assignments until ${publishEndDate}\n${publishedAssignments.length} assignments included.`);
    } catch (error) {
      console.error('Error publishing schedule:', error);
      alert('Error publishing schedule. Please try again.');
    }
  };

  // Helper function to format date and time
  const formatDateTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const dateStr = d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} o ${timeStr}`;
  };

  // Save notes to localStorage whenever notes change
  useEffect(() => {
    localStorage.setItem('resourceBoardNotes', JSON.stringify(notes));
  }, [notes]);

  // Save assignment comments to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('resourceBoardComments', JSON.stringify(assignmentComments));
  }, [assignmentComments]);

  // Note: Disabled users are now loaded from API based on is_active field
  // No need to save to localStorage - changes are saved to database

  // Load comments from assignments data ONLY on initial load - prevent re-renders
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  useEffect(() => {
    if (assignments && assignments.length > 0 && !commentsLoaded) {
      const commentsFromAPI = {};
      assignments.forEach(assignment => {
        if (assignment.comment) {
          commentsFromAPI[assignment.id] = assignment.comment;
        }
      });

      // Merge with existing comments (in case some were added locally)
      setAssignmentComments(prev => ({
        ...commentsFromAPI,
        ...prev // Local comments take precedence
      }));
      setCommentsLoaded(true);
    }
  }, [assignments, commentsLoaded]);

  // Load last saved info from localStorage on mount and fetch current publication from API
  useEffect(() => {
    const savedLastSavedAt = localStorage.getItem('resourceBoardLastSavedAt');
    const savedLastSaveInfo = localStorage.getItem('resourceBoardLastSaveInfo');

    if (savedLastSavedAt) {
      setLastSavedAt(new Date(savedLastSavedAt));
    }
    if (savedLastSaveInfo) {
      const parsed = JSON.parse(savedLastSaveInfo);
      setLastSaveInfo({
        ...parsed,
        timestamp: new Date(parsed.timestamp)
      });
    }

    // Clean up old localStorage data for publication (now using API instead)
    localStorage.removeItem('resourceBoardPublishedRange');

    // Fetch current publication from API (not from localStorage - everyone should see the same publication)
    fetchCurrentPublication();
  }, []);

  // Save last saved info to localStorage whenever it changes
  useEffect(() => {
    if (lastSavedAt) {
      localStorage.setItem('resourceBoardLastSavedAt', lastSavedAt.toISOString());
    }
  }, [lastSavedAt]);

  // Note: publishedDateRange is now always fetched from API, not saved to localStorage
  // This ensures all users see the same publication status

  // Save last save info to localStorage whenever it changes
  useEffect(() => {
    if (lastSaveInfo) {
      localStorage.setItem('resourceBoardLastSaveInfo', JSON.stringify(lastSaveInfo));
    }
  }, [lastSaveInfo]);

  // Fetch save history when modal opens
  useEffect(() => {
    if (showSaveHistoryModal) {
      fetchSaveHistory();
    }
  }, [showSaveHistoryModal]);

  // Fetch publish history when modal opens
  useEffect(() => {
    if (showPublishHistoryModal) {
      fetchPublishHistory();
    }
  }, [showPublishHistoryModal]);

  // Auto-refresh: Check for new changes from other users every 15 seconds
  useEffect(() => {
    // Initial check when component mounts
    checkForNewChanges();

    // Set up interval for periodic checks
    const interval = setInterval(() => {
      checkForNewChanges();
    }, 30000); // 30 seconds (reduced from 15 for performance)

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [lastSaveInfo]); // Re-run when lastSaveInfo changes

  // Active users tracking: Send heartbeat and fetch active users every 30 seconds (optimized)
  useEffect(() => {
    // Initial heartbeat and fetch when component mounts
    sendHeartbeat();
    fetchActiveUsers();

    // Set up interval for periodic updates
    const interval = setInterval(() => {
      sendHeartbeat();
      fetchActiveUsers();
    }, 30000); // 30 seconds (reduced from 15 for performance)

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []); // Empty dependency array - only runs once on mount

  // Check for unread messages every 10 seconds and auto-open chat
  useEffect(() => {
    // Initial check after a delay
    const initialTimeout = setTimeout(() => {
      checkForNewMessages();
    }, 3000);

    // Set up interval for periodic checks (every 10 seconds)
    const interval = setInterval(() => {
      checkForNewMessages();
    }, 10000);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []); // Empty dependency - only create interval once on mount

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        performUndo();
      } else if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
        event.preventDefault();
        performRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undoStack, redoStack]);

  // Handle ESC key to close modals and Ctrl+Z for undo
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showNoteModal) {
          setShowNoteModal(false);
          setNoteInput('');
          setNoteModalData({ user: null, date: null });
        } else if (showCarRentalModal) {
          setShowCarRentalModal(false);
        } else if (showTravelContextMenu) {
          setShowTravelContextMenu(false);
          setContextMenuData(null);
        }
      } else if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
        // Ctrl+Z for undo
        event.preventDefault();
        performUndo();
      } else if (event.ctrlKey && event.key === 'y') {
        // Ctrl+Y for redo
        event.preventDefault();
        performRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showNoteModal, showCarRentalModal, showTravelContextMenu, undoStack, redoStack]);

  // Undo/Redo functions
  const addToUndoStack = (operation) => {
    setUndoStack(prev => {
      const newStack = [...prev, operation];
      // Keep only last MAX_UNDO_STEPS operations
      if (newStack.length > MAX_UNDO_STEPS) {
        return newStack.slice(-MAX_UNDO_STEPS);
      }
      return newStack;
    });
    // Clear redo stack when new operation is added
    setRedoStack([]);
  };

  const performUndo = async () => {
    if (undoStack.length === 0) return;

    const operation = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));

    logger.info('Performing UNDO operation:', operation.type, operation);

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      switch (operation.type) {
        // ============ PENDING (LOCAL) OPERATIONS - no API calls ============
        case 'PENDING_CREATE':
          // Undo a pending create - remove the assignment from local state
          setAssignments(prev => prev.filter(a => a.id !== operation.assignmentId));
          setPendingChanges(prev => ({
            ...prev,
            created: prev.created.filter(a => a.id !== operation.assignmentId)
          }));
          addToRedoStack({ type: 'PENDING_CREATE', assignmentId: operation.assignmentId, data: operation.data });
          setCurrentStatus('Creation undone (Ctrl+Y to redo)');
          // Check if there are still unsaved changes
          setPendingChanges(prev => {
            const hasChanges = prev.created.length > 0 || prev.updated.length > 0 || prev.deleted.length > 0;
            setHasUnsavedChanges(hasChanges);
            return prev;
          });
          return; // Exit early, no need for fetchData

        case 'PENDING_DELETE':
          // Undo a pending delete - restore the assignment to local state
          setAssignments(prev => [...prev, operation.data]);
          setPendingChanges(prev => ({
            ...prev,
            deleted: prev.deleted.filter(id => id !== operation.assignmentId)
          }));
          addToRedoStack({ type: 'PENDING_DELETE', assignmentId: operation.assignmentId, data: operation.data });
          setCurrentStatus('Deletion undone (Ctrl+Y to redo)');
          return; // Exit early, no need for fetchData

        case 'PENDING_UPDATE':
          // Undo a pending update - restore original data
          setAssignments(prev => prev.map(a =>
            a.id === operation.assignmentId ? operation.originalData : a
          ));
          setPendingChanges(prev => ({
            ...prev,
            updated: prev.updated.map(a =>
              a.id === operation.assignmentId ? operation.originalData : a
            )
          }));
          addToRedoStack({
            type: 'PENDING_UPDATE',
            assignmentId: operation.assignmentId,
            originalData: operation.newData,
            newData: operation.originalData
          });
          setCurrentStatus('Change undone (Ctrl+Y to redo)');
          return; // Exit early, no need for fetchData

        // ============ SAVED (API) OPERATIONS ============
        case 'DELETE_ASSIGNMENT':
          // Restore deleted assignment - transform data to API format
          const assignmentData = operation.data;
          const response = await resourceBoardService.createAssignment({
            user: assignmentData.userId || assignmentData.user,
            project: assignmentData.projectId || assignmentData.project,
            start_date: assignmentData.start_date || formatDateForAPI(assignmentData.startDate),
            end_date: assignmentData.end_date || formatDateForAPI(assignmentData.endDate),
            role: assignmentData.role || 'Team Member'
          });
          // Save the restored assignment data with new ID to redo stack
          const restoredData = {
            ...operation.data,
            id: response.id
          };
          addToRedoStack({ type: 'DELETE_ASSIGNMENT', assignmentId: response.id, data: restoredData });
          break;

        case 'MOVE_ASSIGNMENT':
          // Handle different types of moves
          logger.info('Undoing MOVE_ASSIGNMENT:', operation);
          if (operation.moveType === 'USER_CHANGE') {
            // Move assignment back to original user
            const userId = operation.originalData?.userId || operation.originalData?.user;
            if (!userId) {
              logger.error('UNDO: Missing userId in originalData', operation);
              setCurrentStatus('Failed to undo: missing user data');
              return;
            }
            await resourceBoardService.patchAssignment(operation.assignmentId, { user: userId });
          } else if (operation.moveType === 'DATE_CHANGE') {
            // Update dates back to original
            if (!operation.originalData?.startDate || !operation.originalData?.endDate) {
              logger.error('UNDO: Missing dates in originalData', operation);
              setCurrentStatus('Failed to undo: missing date data');
              return;
            }
            await resourceBoardService.updateAssignmentDates(operation.assignmentId,
              formatDateForAPI(new Date(operation.originalData.startDate)),
              formatDateForAPI(new Date(operation.originalData.endDate)));
          } else {
            // Fallback for other types of moves
            const userId = operation.originalData?.userId || operation.originalData?.user;
            if (userId) {
              await resourceBoardService.patchAssignment(operation.assignmentId, { user: userId });
            } else {
              logger.error('UNDO: Unknown move type and no userId', operation);
              setCurrentStatus('Failed to undo: unknown operation');
              return;
            }
          }
          addToRedoStack({
            type: 'MOVE_ASSIGNMENT',
            assignmentId: operation.assignmentId,
            moveType: operation.moveType,
            originalData: operation.newData,
            newData: operation.originalData
          });
          break;

        case 'CREATE_ASSIGNMENT':
          // Delete the created assignment
          await resourceBoardService.deleteAssignment(operation.assignmentId);
          addToRedoStack({ type: 'CREATE_ASSIGNMENT', data: operation.data });
          break;

        case 'CREATE_CAR_ASSIGNMENT':
          // Delete the created car assignment
          await resourceBoardService.deleteVehicleAssignment(operation.assignmentId);
          setCarAssignments(prev => prev.filter(a => a.id !== operation.assignmentId));
          addToRedoStack({ type: 'CREATE_CAR_ASSIGNMENT', data: operation.data, assignmentId: operation.assignmentId });
          break;

        case 'MOVE_CAR_ASSIGNMENT':
          // Move car assignment back to original state
          logger.info('Undoing MOVE_CAR_ASSIGNMENT:', operation);
          if (operation.moveType === 'CAR_CHANGE') {
            const carId = operation.originalData?.carId || operation.originalData?.car;
            if (!carId) {
              logger.error('UNDO: Missing carId in originalData', operation);
              setCurrentStatus('Failed to undo: missing car data');
              return;
            }
            // Build patch data - include dates if available (backend may require them)
            const patchData = { car: carId };
            if (operation.originalData?.startDate) {
              patchData.assigned_date = formatDateForAPI(new Date(operation.originalData.startDate));
            }
            if (operation.originalData?.endDate) {
              patchData.expected_return_date = formatDateForAPI(new Date(operation.originalData.endDate));
            }
            logger.info('UNDO MOVE_CAR_ASSIGNMENT (CAR_CHANGE) patch data:', patchData);
            await axios.patch(
              `${API_BASE_URL}/api/vehicle-assignments/${operation.assignmentId}/`,
              patchData,
              { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
            );
            setCarAssignments(prev => prev.map(assignment =>
              assignment.id === operation.assignmentId
                ? { ...assignment, carId: carId, startDate: operation.originalData?.startDate, endDate: operation.originalData?.endDate }
                : assignment
            ));
          } else if (operation.moveType === 'DATE_CHANGE') {
            // Undo date change - restore original dates
            if (!operation.originalData?.startDate || !operation.originalData?.endDate) {
              logger.error('UNDO: Missing dates in originalData', operation);
              setCurrentStatus('Failed to undo: missing date data');
              return;
            }
            logger.info('UNDO MOVE_CAR_ASSIGNMENT (DATE_CHANGE) - restoring dates:', operation.originalData.startDate, operation.originalData.endDate);
            await resourceBoardService.updateVehicleAssignmentDates(
              operation.assignmentId,
              formatDateForAPI(new Date(operation.originalData.startDate)),
              formatDateForAPI(new Date(operation.originalData.endDate))
            );
            setCarAssignments(prev => prev.map(assignment =>
              assignment.id === operation.assignmentId
                ? { ...assignment, startDate: new Date(operation.originalData.startDate), endDate: new Date(operation.originalData.endDate) }
                : assignment
            ));
          }
          addToRedoStack({
            type: 'MOVE_CAR_ASSIGNMENT',
            assignmentId: operation.assignmentId,
            moveType: operation.moveType,
            originalData: operation.newData,
            newData: operation.originalData
          });
          break;

        default:
          logger.warn('Unknown operation type:', operation.type);
      }

      // Reload data
      await fetchData();
      setCurrentStatus('Operation undone successfully');
    } catch (error) {
      logger.error('Error performing undo:', error);
      setCurrentStatus('Failed to undo operation');
    }
  };

  const addToRedoStack = (operation) => {
    setRedoStack(prev => [...prev, operation]);
  };

  const performRedo = async () => {
    if (redoStack.length === 0) return;

    const operation = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));

    logger.info('Performing REDO operation:', operation.type, operation);

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      switch (operation.type) {
        case 'DELETE_ASSIGNMENT':
          // Delete the assignment again
          await resourceBoardService.deleteAssignment(operation.assignmentId);
          addToUndoStack({ type: 'DELETE_ASSIGNMENT', data: operation.data });
          break;

        case 'MOVE_ASSIGNMENT':
          // Handle different types of moves
          logger.info('Redoing MOVE_ASSIGNMENT:', operation);
          if (operation.moveType === 'USER_CHANGE') {
            // Move assignment to new user again
            const userId = operation.newData?.userId || operation.newData?.user;
            if (!userId) {
              logger.error('REDO: Missing userId in newData', operation);
              setCurrentStatus('Failed to redo: missing user data');
              return;
            }
            await resourceBoardService.patchAssignment(operation.assignmentId, { user: userId });
          } else if (operation.moveType === 'DATE_CHANGE') {
            // Update dates to new values
            if (!operation.newData?.startDate || !operation.newData?.endDate) {
              logger.error('REDO: Missing dates in newData', operation);
              setCurrentStatus('Failed to redo: missing date data');
              return;
            }
            await resourceBoardService.updateAssignmentDates(operation.assignmentId,
              formatDateForAPI(new Date(operation.newData.startDate)),
              formatDateForAPI(new Date(operation.newData.endDate)));
          } else {
            // Fallback for other types of moves
            const userId = operation.newData?.userId || operation.newData?.user;
            if (userId) {
              await resourceBoardService.patchAssignment(operation.assignmentId, { user: userId });
            } else {
              logger.error('REDO: Unknown move type and no userId', operation);
              setCurrentStatus('Failed to redo: unknown operation');
              return;
            }
          }
          addToUndoStack({
            type: 'MOVE_ASSIGNMENT',
            assignmentId: operation.assignmentId,
            moveType: operation.moveType,
            originalData: operation.originalData,
            newData: operation.newData
          });
          break;

        case 'CREATE_ASSIGNMENT':
          // Create the assignment again
          const response = await resourceBoardService.createAssignment(operation.data);
          addToUndoStack({ type: 'CREATE_ASSIGNMENT', assignmentId: response.id, data: operation.data });
          break;

        case 'CREATE_CAR_ASSIGNMENT':
          // Create the car assignment again
          const carResponse = await axios.post(
            `${API_BASE_URL}/api/vehicle-assignments/`,
            {
              car: operation.data.carId,
              project: operation.data.projectId,
              assigned_date: formatDateForAPI(operation.data.startDate),
              expected_return_date: formatDateForAPI(operation.data.endDate),
              status: 'assigned',
              assignment_reason: 'Redo operation'
            },
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
          );
          const redoneCarAssignment = {
            id: carResponse.data.id,
            carId: operation.data.carId,
            projectId: operation.data.projectId,
            startDate: new Date(operation.data.startDate),
            endDate: new Date(operation.data.endDate),
            originalAssignment: carResponse.data
          };
          setCarAssignments(prev => [...prev, redoneCarAssignment]);
          addToUndoStack({ type: 'CREATE_CAR_ASSIGNMENT', assignmentId: carResponse.data.id, data: operation.data });
          break;

        case 'MOVE_CAR_ASSIGNMENT':
          // Move car assignment to new state again
          logger.info('Redoing MOVE_CAR_ASSIGNMENT:', operation);
          if (operation.moveType === 'CAR_CHANGE') {
            const carId = operation.newData?.carId || operation.newData?.car;
            if (!carId) {
              logger.error('REDO: Missing carId in newData', operation);
              setCurrentStatus('Failed to redo: missing car data');
              return;
            }
            // Build patch data - include dates if available
            const patchData = { car: carId };
            if (operation.newData?.startDate) {
              patchData.assigned_date = formatDateForAPI(new Date(operation.newData.startDate));
            }
            if (operation.newData?.endDate) {
              patchData.expected_return_date = formatDateForAPI(new Date(operation.newData.endDate));
            }
            logger.info('REDO MOVE_CAR_ASSIGNMENT (CAR_CHANGE) patch data:', patchData);
            await axios.patch(
              `${API_BASE_URL}/api/vehicle-assignments/${operation.assignmentId}/`,
              patchData,
              { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } }
            );
            setCarAssignments(prev => prev.map(assignment =>
              assignment.id === operation.assignmentId
                ? { ...assignment, carId: carId, startDate: operation.newData?.startDate, endDate: operation.newData?.endDate }
                : assignment
            ));
          } else if (operation.moveType === 'DATE_CHANGE') {
            // Redo date change - apply new dates
            if (!operation.newData?.startDate || !operation.newData?.endDate) {
              logger.error('REDO: Missing dates in newData', operation);
              setCurrentStatus('Failed to redo: missing date data');
              return;
            }
            logger.info('REDO MOVE_CAR_ASSIGNMENT (DATE_CHANGE) - applying dates:', operation.newData.startDate, operation.newData.endDate);
            await resourceBoardService.updateVehicleAssignmentDates(
              operation.assignmentId,
              formatDateForAPI(new Date(operation.newData.startDate)),
              formatDateForAPI(new Date(operation.newData.endDate))
            );
            setCarAssignments(prev => prev.map(assignment =>
              assignment.id === operation.assignmentId
                ? { ...assignment, startDate: new Date(operation.newData.startDate), endDate: new Date(operation.newData.endDate) }
                : assignment
            ));
          }
          addToUndoStack({
            type: 'MOVE_CAR_ASSIGNMENT',
            assignmentId: operation.assignmentId,
            moveType: operation.moveType,
            originalData: operation.originalData,
            newData: operation.newData
          });
          break;

        // ============ PENDING (LOCAL) OPERATIONS ============
        case 'PENDING_CREATE':
          // Redo a pending create - restore the assignment to local state
          setAssignments(prev => [...prev, operation.data]);
          setPendingChanges(prev => ({
            ...prev,
            created: [...prev.created, operation.data]
          }));
          addToUndoStack({ type: 'PENDING_CREATE', assignmentId: operation.assignmentId, data: operation.data });
          setHasUnsavedChanges(true);
          setCurrentStatus('Creation redone (Ctrl+Z to undo)');
          return; // Exit early, no need for fetchData

        case 'PENDING_DELETE':
          // Redo a pending delete - remove the assignment from local state again
          setAssignments(prev => prev.filter(a => a.id !== operation.assignmentId));
          setPendingChanges(prev => ({
            ...prev,
            deleted: [...prev.deleted, operation.assignmentId]
          }));
          addToUndoStack({ type: 'PENDING_DELETE', assignmentId: operation.assignmentId, data: operation.data });
          setHasUnsavedChanges(true);
          setCurrentStatus('Deletion redone (Ctrl+Z to undo)');
          return; // Exit early, no need for fetchData

        case 'PENDING_UPDATE':
          // Redo a pending update - apply the new data again
          setAssignments(prev => prev.map(a =>
            a.id === operation.assignmentId ? operation.newData : a
          ));
          setPendingChanges(prev => ({
            ...prev,
            updated: prev.updated.some(u => u.id === operation.assignmentId)
              ? prev.updated.map(a => a.id === operation.assignmentId ? operation.newData : a)
              : [...prev.updated, operation.newData]
          }));
          addToUndoStack({
            type: 'PENDING_UPDATE',
            assignmentId: operation.assignmentId,
            originalData: operation.originalData,
            newData: operation.newData
          });
          setHasUnsavedChanges(true);
          setCurrentStatus('Change redone (Ctrl+Z to undo)');
          return; // Exit early, no need for fetchData

        default:
          logger.warn('Unknown operation type:', operation.type);
      }

      // Reload data
      await fetchData();
      setCurrentStatus('Operation redone successfully');
    } catch (error) {
      logger.error('Error performing redo:', error);
      setCurrentStatus('Failed to redo operation');
    }
  };



  // Colors for different projects
  const projectColors = [
    'bg-brand-100 border-brand-300 text-brand-800',
    'bg-green-100 border-green-300 text-green-800', 
    'bg-purple-100 border-purple-300 text-purple-800',
    'bg-red-100 border-red-300 text-red-800',
    'bg-yellow-100 border-yellow-300 text-yellow-800',
    'bg-indigo-100 border-indigo-300 text-indigo-800',
    'bg-pink-100 border-pink-300 text-pink-800',
    'bg-teal-100 border-teal-300 text-teal-800'
  ];

  // Initial fetch on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch when date range changes (dayOffset excluded - it only adjusts visible range, not data fetch)
  useEffect(() => {
    if (selectedTimeRange || currentDate) {
      fetchData();
    }
  }, [selectedTimeRange, currentDate, customStartDate, customEndDate]);


  // Window resize listener for responsive day width calculation
  useEffect(() => {
    const handleResize = () => {
      // Force re-render when window size changes
      setCurrentDate(new Date(currentDate));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentDate]);


  const fetchData = async () => {
    // Helper function to parse date safely (timezone-independent)
    const parseDateSafe = (dateString) => {
      if (!dateString) return null;
      // Extract only the date part (YYYY-MM-DD) to avoid timezone issues
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    };

    try {
      // Get date range filters for lazy loading
      const dateFilters = getDateRangeForFetch;

      // ========== INSTANT CACHE CHECK ==========
      // Check if we have cached data - if so, show immediately while fetching fresh data
      const cachedProjects = sessionStorage.getItem('rb_cache_projects');
      const cachedUsers = sessionStorage.getItem('rb_cache_users');

      if (cachedProjects && cachedUsers) {
        try {
          const projectsCache = JSON.parse(cachedProjects);
          const usersCache = JSON.parse(cachedUsers);

          if (Date.now() < projectsCache.expiry && Date.now() < usersCache.expiry) {
            // Use cached data immediately - no loading spinner!
            const projectsData = Array.isArray(projectsCache.data) ? projectsCache.data : [];
            const usersData = Array.isArray(usersCache.data) ? usersCache.data : [];

            setProjects(projectsData);
            setUsers(usersData);

            const disabledUserIds = usersData
              .filter(user => user.hidden_on_board === true)
              .map(user => user.id);
            setDisabledUsers(disabledUserIds);

            // Don't show loading - we have data!
            setLoading(false);
            logger.debug('Using cached data for instant render');
          }
        } catch (e) {
          // Cache parse error - continue with normal loading
          setLoading(true);
        }
      } else {
        setLoading(true);
      }

      // ========== PHASE 1: Critical data for initial render ==========
      const [projectsResponse, usersResponse, assignmentsResponse] = await Promise.all([
        resourceBoardService.getProjectsAlt().catch(() => []),
        resourceBoardService.getUsers().catch(() => []),
        resourceBoardService.getAssignments(dateFilters).catch(() => []),
      ]);

      // Process critical data
      const projectsData = Array.isArray(projectsResponse) ? projectsResponse : [];
      setProjects(projectsData);

      const usersData = Array.isArray(usersResponse) ? usersResponse : [];
      setUsers(usersData);

      // Set disabled users based on hidden_on_board field from API
      const disabledUserIds = usersData
        .filter(user => user.hidden_on_board === true)
        .map(user => user.id);
      setDisabledUsers(disabledUserIds);

      // Process assignments (without car filtering for now - will update after phase 2)
      if (Array.isArray(assignmentsResponse)) {
        const allAssignments = assignmentsResponse.map(assignment => {
          const mapped = {
            id: assignment.id,
            userId: assignment.user,
            projectId: assignment.project,
            startDate: parseDateSafe(assignment.start_date),
            endDate: parseDateSafe(assignment.end_date),
            role: assignment.role || 'Team Member',
            isSpecialTile: assignment.is_special_tile || false,
            hexColor: assignment.hex_color || null,
            projectName: assignment.custom_name || null
          };
          return mapped;
        });
        setAssignments(allAssignments);
        setSavedAssignments(allAssignments);
      }

      // UI can render now with basic data!
      setLoading(false);

      // ========== PHASE 2: Secondary data loaded in background ==========
      const [travelResponse, trainingResponse, globalRolesResponse, projectRolesResponse, userGlobalRolesResponse, carsResponse, vehicleAssignmentsResponse, roleOrderResponse, holidaysResponse] = await Promise.all([
        resourceBoardService.getTravel(dateFilters).catch(() => []),
        resourceBoardService.getTrainings(dateFilters).catch(() => []),
        resourceBoardService.getGlobalRoles().catch(() => []),
        resourceBoardService.getAllProjectRoles().catch(() => []),
        resourceBoardService.getUserGlobalRoles().catch(() => []),
        resourceBoardService.getCars().catch(() => []),
        resourceBoardService.getVehicleAssignments(dateFilters).catch(() => []),
        resourceBoardService.getRoleOrderSetting().catch(() => ({ role_order: [] })),
        resourceBoardService.getHolidays(dateFilters).catch(() => [])
      ]);

      // Process travel data
      const travelDataProcessed = Array.isArray(travelResponse) ? travelResponse : [];
      setTravelData(travelDataProcessed);

      const travelMap = new Map();
      travelDataProcessed.forEach(travel => {
        travelMap.set(travel.id, travel);
      });
      setTravelsMap(travelMap);

      // Process training data
      const trainingDataProcessed = Array.isArray(trainingResponse) ? trainingResponse : [];
      setTrainingData(trainingDataProcessed);

      // Process holidays data
      const holidaysDataProcessed = Array.isArray(holidaysResponse?.results) ? holidaysResponse.results :
                                    Array.isArray(holidaysResponse) ? holidaysResponse : [];

      const mappedHolidays = holidaysDataProcessed.map(holiday => ({
        id: holiday.id,
        userId: holiday.user || holiday.user_id,
        startDate: parseDateSafe(holiday.start_date),
        endDate: parseDateSafe(holiday.end_date),
        notes: holiday.notes || '',
        status: holiday.status || 'pending',
        holidayType: holiday.holiday_type || 'H'
      }));
      setHolidays(mappedHolidays);

      // Process cars data
      const carsDataProcessed = Array.isArray(carsResponse?.results) ? carsResponse.results :
                                Array.isArray(carsResponse) ? carsResponse : [];
      setCarsData(carsDataProcessed);

      // Process vehicle assignments data
      const vehicleAssignmentsData = Array.isArray(vehicleAssignmentsResponse?.results) ?
        vehicleAssignmentsResponse.results :
        Array.isArray(vehicleAssignmentsResponse) ? vehicleAssignmentsResponse : [];

      const calendarVehicleAssignments = vehicleAssignmentsData
        .filter(assignment => assignment.status === 'assigned')
        .filter(assignment => !blacklistedAssignments.has(assignment.id))
        .map(assignment => {
          const startDate = parseDateSafe(assignment.assigned_date);
          const endDate = assignment.expected_return_date ?
            parseDateSafe(assignment.expected_return_date) : startDate;

          return {
            id: assignment.id,
            carId: assignment.car,
            projectId: assignment.project,
            userId: assignment.user,
            startDate: startDate,
            endDate: endDate,
            assignmentReason: assignment.assignment_reason,
            originalAssignment: assignment
          };
        });

      setCarAssignments(calendarVehicleAssignments);
      setSavedCarAssignments(calendarVehicleAssignments);

      // Process global roles
      if (Array.isArray(globalRolesResponse)) {
        setGlobalRoles(globalRolesResponse.map(role => ({
          id: role.id,
          name: role.name,
          color: role.color
        })));
      }

      // Process project roles
      if (Array.isArray(projectRolesResponse)) {
        const rolesGroupedByProject = {};
        projectRolesResponse.forEach(role => {
          if (!rolesGroupedByProject[role.project]) {
            rolesGroupedByProject[role.project] = [];
          }
          rolesGroupedByProject[role.project].push({
            id: role.id,
            name: role.name,
            color: role.color
          });
        });
        setProjectRoles(rolesGroupedByProject);
      }

      // Process user global roles
      if (Array.isArray(userGlobalRolesResponse)) {
        setUserGlobalRoles(userGlobalRolesResponse);
        const userRolesMap = {};
        userGlobalRolesResponse.forEach(userRole => {
          const globalRole = Array.isArray(globalRolesResponse)
            ? globalRolesResponse.find(r => r.id === userRole.role)
            : null;
          if (globalRole) {
            const userId = userRole.user || userRole.user_id;

            if (!userRolesMap[userId]) {
              userRolesMap[userId] = {
                roles: [],
                colors: []
              };
            }

            userRolesMap[userId].roles.push(globalRole.name);
            userRolesMap[userId].colors.push(globalRole.color);

            if (!userRolesMap[userId].role) {
              userRolesMap[userId].role = globalRole.name;
              userRolesMap[userId].color = globalRole.color;
            }
          }
        });
        setUserRoles(userRolesMap);
      }

      // Load role order setting
      if (roleOrderResponse && roleOrderResponse.role_order) {
        setRoleOrder(roleOrderResponse.role_order);
      }

    } catch (error) {
      logger.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Refresh only travel data without showing loading spinner
  const refreshTravelData = async () => {
    try {
      const travelResponse = await resourceBoardService.getTravel().catch(() => []);
      const travelDataProcessed = Array.isArray(travelResponse) ? travelResponse : [];

      setTravelData(travelDataProcessed);

      // Create map of travel data for quick lookup
      const travelMap = new Map();
      travelDataProcessed.forEach(travel => {
        travelMap.set(travel.id, travel);
      });
      setTravelsMap(travelMap);
    } catch (error) {
      logger.error('Error refreshing travel data:', error);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // ⚡ CRITICAL OPTIMIZATION: Memoized timeline days calculation
  // This is called hundreds of times during drag/resize operations
  const getTimeRangeDays = useMemo(() => {
    const days = [];
    let start, end;

    if (selectedTimeRange === 'custom') {
      // Use custom date range if both dates are set
      if (customStartDate && customEndDate) {
        start = new Date(customStartDate);
        end = new Date(customEndDate);
      } else {
        // Fallback to current month if custom dates not set
        start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      }
    } else {
      start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

      switch (selectedTimeRange) {
        case 'month':
          end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          break;
        case 'quarter':
          const quarterStartMonth = Math.floor(currentDate.getMonth() / 3) * 3;
          start.setMonth(quarterStartMonth);
          end = new Date(currentDate.getFullYear(), quarterStartMonth + 3, 0);
          break;
        case 'half-year':
          const halfStartMonth = currentDate.getMonth() < 6 ? 0 : 6;
          start.setMonth(halfStartMonth);
          end = new Date(currentDate.getFullYear(), halfStartMonth + 6, 0);
          break;
        default:
          end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      }
    }

    // Apply day offset for fine-grained scrolling
    start.setDate(start.getDate() + dayOffset);
    end.setDate(end.getDate() + dayOffset);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [selectedTimeRange, currentDate, customStartDate, customEndDate, dayOffset]);


  // ⚡ CRITICAL OPTIMIZATION: Memoized day width calculation
  // Depends on memoized getTimeRangeDays
  const getDayWidth = useMemo(() => {
    const screenWidth = window.innerWidth;
    const sidebarWidth = isSidebarVisible ? 320 : 0;
    const personnelColumnWidth = isSidebarVisible ? 192 : 256;
    const availableWidth = screenWidth - sidebarWidth - personnelColumnWidth - 60;

    const dayWidth = Math.max(availableWidth / getTimeRangeDays.length, 25);

    // console.log('🚗 getDayWidth calculated:', { dayWidth, availableWidth, daysLength: getTimeRangeDays.length });

    return dayWidth;
  }, [getTimeRangeDays, isSidebarVisible]);

  // Get maximum offset for current time range (days in period)
  const getMaxOffset = useCallback(() => {
    switch (selectedTimeRange) {
      case 'month':
        return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
      case 'quarter':
        return 90; // Approximate
      case 'half-year':
        return 180; // Approximate
      default:
        return 30;
    }
  }, [selectedTimeRange, currentDate]);

  // ⚡ OPTIMIZED: Memoized date range for API filtering
  const getDateRangeForFetch = useMemo(() => {
    const days = getTimeRangeDays;
    if (days.length === 0) return null;

    const startDate = new Date(days[0]);
    const endDate = new Date(days[days.length - 1]);

    // Add buffer matching max scroll offset to ensure data is available when scrolling
    const buffer = getMaxOffset() + 7; // maxOffset + extra 7 days safety margin
    startDate.setDate(startDate.getDate() - buffer);
    endDate.setDate(endDate.getDate() + buffer);

    return {
      start_date: formatDateForAPI(startDate),
      end_date: formatDateForAPI(endDate)
    };
  }, [getTimeRangeDays, getMaxOffset]);

  // Get equipment icon and color based on equipment_type
  const getEquipmentIcon = (equipmentType) => {
    switch (equipmentType) {
      case 'car':
        return { Icon: Truck, color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'machine':
        return { Icon: Settings, color: 'text-brand-600', bgColor: 'bg-brand-100' };
      case 'lighting_tower':
        return { Icon: Lightbulb, color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
      case 'cherry_picker':
        return { Icon: Cherry, color: 'text-pink-600', bgColor: 'bg-pink-100' };
      case 'lift':
        return { Icon: TrendingUp, color: 'text-brand-600', bgColor: 'bg-purple-100' };
      default:
        return { Icon: Truck, color: 'text-green-600', bgColor: 'bg-green-100' };
    }
  };

  const getProjectColor = (project, index) => {
    if (project.color) {
      if (project.color.includes('bg-')) {
        return project.color;
      } else if (project.color.startsWith('#')) {
        return null;
      } else {
        return `bg-${project.color}-100 text-${project.color}-800 border-${project.color}-200`;
      }
    } else {
      return projectColors[index % projectColors.length];
    }
  };

  const getProjectInlineStyles = (project) => {
    if (project.color && project.color.startsWith('#')) {
      return {
        backgroundColor: project.color,
        borderColor: project.color,
        color: '#ffffff'
      };
    }
    return {};
  };

  // Placeholder - will be defined after getFilteredUsers

  // Calculate days spent in each country for a user
  const calculateDaysInCountries = (userId) => {
    const userAssignments = getFilteredAssignments.filter(a => a.userId === userId);
    const countryDays = {};

    // Count assignment days
    userAssignments.forEach(assignment => {
      const project = projects.find(p => p.id === assignment.projectId);
      if (!project || !project.country) return;

      const country = project.country;

      // Calculate number of days in this assignment
      const startDate = new Date(assignment.startDate);
      const endDate = new Date(assignment.endDate);
      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      countryDays[country] = (countryDays[country] || 0) + days;
    });

    // Get ALL user assignments (not filtered) for matching travels to projects
    const allUserAssignments = (Array.isArray(assignments) ? assignments : []).filter(a => a.userId === userId);

    // Count check-in and check-out travel days
    const userTravels = (Array.isArray(travelData) ? travelData : []).filter(travel => {
      const userMatches = travel.user === userId ||
                         travel.user_id === userId ||
                         travel.user === parseInt(userId) ||
                         travel.user_id === parseInt(userId);

      // Only count check-in and check-out travels
      const isCheckInOrOut = travel.travel_type === 'check_in' || travel.travel_type === 'check_out';

      return userMatches && isCheckInOrOut;
    });

    userTravels.forEach(travel => {
      const travelStart = new Date(travel.start_date || travel.departure_date);
      const travelEnd = new Date(travel.end_date || travel.return_date);
      const travelDateOnly = new Date(travelStart.getFullYear(), travelStart.getMonth(), travelStart.getDate());

      // Find which project this travel is related to
      let relatedProject = null;

      if (travel.travel_type === 'check_in') {
        // Check-in travel: find project that starts 0-2 days after this travel
        relatedProject = allUserAssignments.find(assignment => {
          const project = projects.find(p => p.id === assignment.projectId);
          if (!project || !project.country) return false;

          const projectStart = new Date(assignment.startDate);
          const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
          const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));
          return daysBefore >= 0 && daysBefore <= 2;
        });
      } else if (travel.travel_type === 'check_out') {
        // Check-out travel: find project that ends 0-2 days before this travel
        relatedProject = allUserAssignments.find(assignment => {
          const project = projects.find(p => p.id === assignment.projectId);
          if (!project || !project.country) return false;

          const projectEnd = new Date(assignment.endDate);
          const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
          const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));
          return daysAfter >= 0 && daysAfter <= 2;
        });
      }

      // If we found a related project, add 1 day for this travel (check-in = 1 day, check-out = 1 day)
      if (relatedProject) {
        const project = projects.find(p => p.id === relatedProject.projectId);
        if (project && project.country) {
          countryDays[project.country] = (countryDays[project.country] || 0) + 1;
        }
      }
    });

    return countryDays;
  };

  // Parse city from personal_address and create abbreviation
  const getCityAbbreviation = (user) => {
    if (!user || !user.personal_address) return null;

    // Assuming format: "City, street number apartment postal_code"
    // Extract city (everything before the first comma)
    const addressParts = user.personal_address.split(',');
    if (addressParts.length === 0) return null;

    const city = addressParts[0].trim();
    if (!city) return null;

    // Create abbreviation: first 3 letters, uppercase
    return city.substring(0, 3).toUpperCase();
  };

  const getAssignmentStyle = (assignment, projectIndex, trackNumber = 0) => {
    const days = getTimeRangeDays;
    const dayWidth = getDayWidth;

    if (days.length === 0) {
      return { display: 'none' };
    }

    const assignmentStart = new Date(assignment.startDate);
    const assignmentEnd = new Date(assignment.endDate);
    const visibleStart = new Date(days[0]);
    const visibleEnd = new Date(days[days.length - 1]);

    // Hide only if assignment is completely outside visible range
    if (assignmentEnd < visibleStart || assignmentStart > visibleEnd) {
      return { display: 'none' };
    }

    const startDay = days.findIndex(day =>
      day.toDateString() === assignment.startDate.toDateString()
    );
    let endDay = days.findIndex(day =>
      day.toDateString() === assignment.endDate.toDateString()
    );

    // If assignment starts before visible range, clip to first day
    const effectiveStartDay = startDay === -1 ? 0 : startDay;

    // If assignment ends after visible range, clip to last day
    const effectiveEndDay = endDay === -1 ? days.length - 1 : endDay;

    const left = effectiveStartDay * dayWidth;
    const width = (effectiveEndDay - effectiveStartDay + 1) * dayWidth;

    const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);

    // Check if this is a special tile with custom hex color
    let color, inlineStyles;
    if (assignment.hexColor && assignment.isSpecialTile) {
      // Use custom hex color for special tiles (same style as projects with hex colors)
      color = null;
      inlineStyles = {
        backgroundColor: assignment.hexColor,
        borderColor: assignment.hexColor,
        color: '#ffffff'
      };
    } else {
      // Use normal project color
      color = project ? getProjectColor(project, projectIndex) : projectColors[projectIndex % projectColors.length];
      inlineStyles = project ? getProjectInlineStyles(project) : {};
    }

    const hasConflicts = hasConflict(assignment);
    const conflictClass = hasConflicts ? 'ring-2 ring-red-400 ring-opacity-75' : '';

    // Calculate top position based on track number (stackowanie)
    const assignmentHeight = 24; // Height of each assignment bar
    const trackPadding = 1; // Space between tracks
    const topOffset = 2 + (trackNumber * (assignmentHeight + trackPadding));

    return {
      left: `${left}px`,
      width: `${width}px`,
      position: 'absolute',
      top: `${topOffset}px`,
      height: `${assignmentHeight}px`,
      className: color ? `${color} ${conflictClass} rounded border-l-4 px-2 text-xs flex items-center cursor-move select-none relative` : `${conflictClass} rounded border-l-4 px-2 text-xs flex items-center cursor-move select-none relative`,
      style: color ? {} : inlineStyles
    };
  };

  const getHolidayStyle = (holiday, trackNumber = 0) => {
    const days = getTimeRangeDays;
    const dayWidth = getDayWidth;

    if (days.length === 0) {
      return { display: 'none' };
    }

    const holidayStart = new Date(holiday.startDate);
    const holidayEnd = new Date(holiday.endDate);
    const visibleStart = new Date(days[0]);
    const visibleEnd = new Date(days[days.length - 1]);

    // Hide only if holiday is completely outside visible range
    if (holidayEnd < visibleStart || holidayStart > visibleEnd) {
      return { display: 'none' };
    }

    const startDay = days.findIndex(day =>
      day.toDateString() === holiday.startDate.toDateString()
    );
    let endDay = days.findIndex(day =>
      day.toDateString() === holiday.endDate.toDateString()
    );

    // If holiday starts before visible range, clip to first day
    const effectiveStartDay = startDay === -1 ? 0 : startDay;

    // If holiday ends after visible range, clip to last day
    const effectiveEndDay = endDay === -1 ? days.length - 1 : endDay;

    const left = effectiveStartDay * dayWidth;
    const width = (effectiveEndDay - effectiveStartDay + 1) * dayWidth;

    // Holiday styling - amber/yellow for holidays with status indicator
    const holidayHeight = 24;
    const trackPadding = 1;
    const topOffset = 2 + (trackNumber * (holidayHeight + trackPadding));

    // Color based on status
    const statusColors = {
      'approved': { bg: '#fbbf24', border: '#f59e0b', text: '#78350f' }, // amber
      'pending': { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' }, // light amber
      'rejected': { bg: '#fee2e2', border: '#ef4444', text: '#7f1d1d' }  // red
    };

    const colors = statusColors[holiday.status] || statusColors.pending;

    return {
      left: `${left}px`,
      width: `${width}px`,
      position: 'absolute',
      top: `${topOffset}px`,
      height: `${holidayHeight}px`,
      className: 'rounded border-l-4 px-2 text-xs flex items-center cursor-pointer select-none relative',
      style: {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: colors.text
      }
    };
  };

  const navigateTime = (direction) => {
    const newDate = new Date(currentDate);
    switch (selectedTimeRange) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + direction);
        break;
      case 'quarter':
        newDate.setMonth(newDate.getMonth() + (direction * 3));
        break;
      case 'half-year':
        newDate.setMonth(newDate.getMonth() + (direction * 6));
        break;
    }
    setCurrentDate(newDate);
    setDayOffset(0); // Reset day offset when navigating between periods
  };

  const getTimeRangeLabel = () => {
    switch (selectedTimeRange) {
      case 'month':
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'quarter':
        const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
        return `Q${quarter} ${currentDate.getFullYear()}`;
      case 'half-year':
        const half = currentDate.getMonth() < 6 ? 'H1' : 'H2';
        return `${half} ${currentDate.getFullYear()}`;
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return 'Custom Range';
      default:
        return '';
    }
  };

  const handleMouseDown = useCallback((e, assignment, action, handle = null) => {
    e.preventDefault();
    e.stopPropagation();

    // Detect double-click and skip drag logic
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current.time;
    const isSameAssignment = lastClickTimeRef.current.assignmentId === assignment.id;

    // If clicked within 300ms on the same assignment, it's a double-click - skip drag
    if (timeSinceLastClick < 300 && isSameAssignment) {
      console.log('🔴 Double-click detected - skipping drag logic');
      return; // Let the onDoubleClick handler open the modal
    }

    // Update last click time
    lastClickTimeRef.current = { time: now, assignmentId: assignment.id };

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const isAltPressed = e.altKey;

    if (action === 'drag') {
      // Cache DOM element and original width to avoid querySelector in every frame
      const assignmentEl = document.querySelector(`[data-assignment-id="${assignment.id}"]`);
      if (assignmentEl) {
        dragElementRef.current = assignmentEl;
        dragElementOriginalWidth.current = parseFloat(assignmentEl.dataset.originalWidth || assignmentEl.style.width);
      }

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';

      setDragState({
        assignmentId: assignment.id,
        startX: e.clientX - rect.left,
        deltaX: 0,
        originalAssignment: { ...assignment },
        isCopying: isAltPressed // Track if ALT is pressed for copy mode
      });
    } else if (action === 'resize') {
      // Cache DOM element and original width to avoid querySelector in every frame
      const assignmentEl = document.querySelector(`[data-assignment-id="${assignment.id}"]`);
      if (assignmentEl) {
        dragElementRef.current = assignmentEl;
        dragElementOriginalWidth.current = parseFloat(assignmentEl.dataset.originalWidth || assignmentEl.style.width);
      }

      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';

      setResizeState({
        assignmentId: assignment.id,
        handle: handle,
        startX: e.clientX - rect.left,
        deltaX: 0,
        originalAssignment: { ...assignment }
      });
    } else if (action === 'move-user') {
      console.log('Setting assignmentDragState for vertical drag', { assignmentId: assignment.id, isAltPressed });
      setAssignmentDragState({
        assignment: assignment,
        startY: e.clientY,
        startX: e.clientX,
        isDragging: true,
        isCopying: isAltPressed, // Track if ALT is pressed for copy mode
        type: 'user' // Track type for different handling
      });

      // Prevent default drag behavior
      e.preventDefault();

      // Add visual feedback immediately
      document.body.style.cursor = isAltPressed ? 'copy' : 'grabbing';
      console.log('Cursor set to:', isAltPressed ? 'copy' : 'grabbing');
    } else if (action === 'move-car') {
      console.log('Setting assignmentDragState for car vertical drag', { assignmentId: assignment.id, isAltPressed });
      setAssignmentDragState({
        assignment: assignment,
        startY: e.clientY,
        startX: e.clientX,
        isDragging: true,
        isCopying: isAltPressed, // Track if ALT is pressed for copy mode
        type: 'car' // Track type for different handling
      });

      // Prevent default drag behavior
      e.preventDefault();

      // Add visual feedback immediately
      document.body.style.cursor = isAltPressed ? 'copy' : 'grabbing';
      console.log('Car cursor set to:', isAltPressed ? 'copy' : 'grabbing');
    }
  }, [timelineRef, dragElementRef, dragElementOriginalWidth, setDragState, setResizeState, setAssignmentDragState]);

  const handleMouseMove = useCallback((e) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Update drag preview position directly via DOM (no re-render!)
    if (assignmentDragState && assignmentDragState.isDragging && dragPreviewRef.current) {
      dragPreviewRef.current.style.transform = `translate(${e.clientX + 15}px, ${e.clientY - 12}px)`;

      // Also track horizontal movement (deltaX) for timeline date shift
      const currentX = e.clientX - rect.left;
      const deltaX = currentX - (assignmentDragState.startX - rect.left);

      // Store deltaX in assignmentDragState for handleMouseUp
      assignmentDragState.deltaX = deltaX;
    }

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Use requestAnimationFrame to throttle DOM updates
    rafIdRef.current = requestAnimationFrame(() => {
      if (dragState && dragElementRef.current) {
        const currentX = e.clientX - rect.left;
        const deltaX = currentX - dragState.startX;

        // Update transform directly on the cached element - NO querySelector!
        // NO state updates during drag - only pure DOM manipulation for maximum performance
        dragElementRef.current.style.transform = `translateX(${deltaX}px)`;

        // Store current deltaX in the element's dataset for handleMouseUp
        dragElementRef.current.dataset.currentDeltaX = deltaX;
      }

      if (resizeState && dragElementRef.current && dragElementOriginalWidth.current !== null) {
        const currentX = e.clientX - rect.left;
        const deltaX = currentX - resizeState.startX;

        // Update width/transform directly on the cached element - NO querySelector or parseFloat!
        // NO state updates during resize - only pure DOM manipulation for maximum performance
        if (resizeState.handle === 'left') {
          dragElementRef.current.style.transform = `translateX(${deltaX}px)`;
          dragElementRef.current.style.width = `${dragElementOriginalWidth.current - deltaX}px`;
        } else {
          dragElementRef.current.style.width = `${dragElementOriginalWidth.current + deltaX}px`;
        }

        // Store current deltaX in the element's dataset for handleMouseUp
        dragElementRef.current.dataset.currentDeltaX = deltaX;
      }
    });

    // Car assignments use only modal edit - no mouse move interactions

    // Wyłączone podświetlanie docelowego wiersza dla wydajności
    // Target row będzie wykrywany tylko przy drop, nie przy każdym mousemove
  }, [timelineRef, assignmentDragState, dragPreviewRef, rafIdRef, dragState, dragElementRef, resizeState, dragElementOriginalWidth]);

  const handleMouseUp = async (e) => {
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    console.log('Mouse up event', {
      assignmentDragState,
      dropTargetUser,
      dropTargetCar,
      hasAssignmentDrag: !!assignmentDragState,
      isDragging: assignmentDragState?.isDragging,
      type: assignmentDragState?.type
    });

    if (assignmentDragState && assignmentDragState.isDragging) {
      // Handle USER assignments
      if (assignmentDragState.type === 'user') {
        // Wykryj docelowy wiersz TYLKO przy drop (nie przy każdym mousemove)
        const userRows = document.querySelectorAll('[data-user-row]');
        let currentDropTarget = null;
        for (const row of userRows) {
          const rowRect = row.getBoundingClientRect();
          if (e.clientY >= rowRect.top && e.clientY <= rowRect.bottom) {
            currentDropTarget = parseInt(row.dataset.userId);
            break;
          }
        }

        console.log('User vertical drag ended - drop target:', currentDropTarget, 'original user:', assignmentDragState.assignment.userId);

        if (!currentDropTarget) {
          console.log('No drop target - aborting');
          setAssignmentDragState(null);
          setDropTargetUser(null);
          dropTargetUserRef.current = null;
          document.body.style.cursor = 'default';
          return;
        }

        // Calculate date shift based on horizontal movement (deltaX)
        const dayWidth = getDayWidth;
        const deltaX = assignmentDragState.deltaX || 0;
        const dayShift = Math.round(deltaX / dayWidth);

        // Allow dropping on same user only if:
        // 1. It's copy mode (Alt pressed) AND
        // 2. There's a date shift (horizontal movement)
        if (currentDropTarget === assignmentDragState.assignment.userId) {
          if (!assignmentDragState.isCopying || dayShift === 0) {
            console.log('Drop target is same as original user and not copying with date shift - aborting');
            setAssignmentDragState(null);
            setDropTargetUser(null);
            dropTargetUserRef.current = null;
            document.body.style.cursor = 'default';
            return;
          }
          console.log('Copying to same user with date shift - proceeding');
        }

        console.log('Proceeding with user move/copy operation');
        if (currentDropTarget) {

        // Apply date shift to original dates
        const originalStartDate = new Date(assignmentDragState.assignment.startDate);
        const originalEndDate = new Date(assignmentDragState.assignment.endDate);
        const newStartDate = new Date(originalStartDate);
        const newEndDate = new Date(originalEndDate);
        newStartDate.setDate(newStartDate.getDate() + dayShift);
        newEndDate.setDate(newEndDate.getDate() + dayShift);

        // Check for overlaps at the new user with shifted dates
        const movedAssignment = {
          ...assignmentDragState.assignment,
          userId: currentDropTarget,
          startDate: newStartDate,
          endDate: newEndDate
        };

        const overlaps = checkForOverlaps(movedAssignment, currentDropTarget)
          .filter(o => o.assignment.id !== assignmentDragState.assignment.id); // Exclude self

        let shouldProceed = true;
        if (overlaps.length > 0) {
          const overlapMessage = overlaps.map(o => o.projectName).join(', ');
          const targetUser = (Array.isArray(users) ? users : []).find(u => u.id === currentDropTarget);
          const actionWord = assignmentDragState.isCopying ? 'Copying' : 'Moving';
          const confirmMessage = `${actionWord} this assignment to ${targetUser?.last_name} ${targetUser?.first_name} will create overlap with: ${overlapMessage}\n\nDo you want to continue?`;

          shouldProceed = window.confirm(confirmMessage);
        }

        if (shouldProceed) {
          try {
            console.log('Starting pending changes - isCopying:', assignmentDragState.isCopying, 'dayShift:', dayShift);
            if (assignmentDragState.isCopying) {
              // COPY MODE - Create pending new assignment for target user with shifted dates
              const newAssignment = {
                userId: currentDropTarget,
                projectId: assignmentDragState.assignment.projectId,
                projectName: assignmentDragState.assignment.projectName, // Preserve tile name
                startDate: newStartDate,
                endDate: newEndDate,
                role: assignmentDragState.assignment.role,
                hexColor: assignmentDragState.assignment.hexColor, // Preserve custom color
                isSpecialTile: assignmentDragState.assignment.isSpecialTile // Preserve special tile flag
              };

              console.log('Adding assignment to pending creates:', newAssignment);

              // Use pending changes system instead of direct API call
              addPendingCreate(newAssignment);

              // Add to undo stack
              addToUndoStack({
                type: 'CREATE_ASSIGNMENT',
                data: newAssignment
              });

              setCurrentStatus('Assignment copied to user - Save to apply (Ctrl+Z to undo)');
            } else {
              // MOVE MODE - Update existing assignment with new user and shifted dates
              const originalUserId = assignmentDragState.assignment.userId;
              const originalStartDate = assignmentDragState.assignment.startDate;
              const originalEndDate = assignmentDragState.assignment.endDate;

              console.log('Moving assignment:', assignmentDragState.assignment.id, 'to user:', currentDropTarget, 'with dayShift:', dayShift);

              const updatedAssignment = {
                ...assignmentDragState.assignment,
                userId: currentDropTarget,
                startDate: newStartDate,
                endDate: newEndDate
              };

              // Use pending changes system instead of direct API call
              addPendingUpdate(updatedAssignment);

              console.log('Assignment moved to pending updates');

              // Add to undo stack - use PENDING_UPDATE since this is a local change
              addToUndoStack({
                type: 'PENDING_UPDATE',
                assignmentId: assignmentDragState.assignment.id,
                originalData: {
                  ...assignmentDragState.assignment,
                  userId: originalUserId,
                  startDate: originalStartDate,
                  endDate: originalEndDate
                },
                newData: updatedAssignment
              });

              setCurrentStatus('Assignment moved to user - Save to apply (Ctrl+Z to undo)');
            }

          } catch (error) {
            logger.error('Error moving/copying assignment to user:', error);
            alert(`Failed to move/copy assignment: ${error.message}`);
          }
        }
        }
      } else if (assignmentDragState.type === 'car') {
        // Handle CAR assignments
        // Wykryj docelowy wiersz TYLKO przy drop (nie przy każdym mousemove)
        const carRows = document.querySelectorAll('[data-car-row]');
        let currentDropTarget = null;
        for (const row of carRows) {
          const rowRect = row.getBoundingClientRect();
          if (e.clientY >= rowRect.top && e.clientY <= rowRect.bottom) {
            currentDropTarget = parseInt(row.dataset.carId);
            break;
          }
        }

        console.log('Car vertical drag ended - drop target:', currentDropTarget, 'original car:', assignmentDragState.assignment.carId);

        if (!currentDropTarget) {
          console.log('No drop target - aborting');
          setAssignmentDragState(null);
          setDropTargetCar(null);
          dropTargetCarRef.current = null;
          document.body.style.cursor = 'default';
          return;
        }

        if (currentDropTarget === assignmentDragState.assignment.carId) {
          // Same car - only allow if it's a copy operation (Alt pressed)
          if (!assignmentDragState.isCopying) {
            console.log('Drop target is same as original car and not copying - aborting');
            setAssignmentDragState(null);
            setDropTargetCar(null);
            dropTargetCarRef.current = null;
            document.body.style.cursor = 'default';
            return;
          }
          console.log('Drop target is same as original car but copying (Alt pressed) - proceeding');
        }

        console.log('Proceeding with car move/copy operation');
        if (currentDropTarget) {
          // Calculate date shift based on horizontal movement (deltaX)
          const dayWidth = getDayWidth;
          const deltaX = assignmentDragState.deltaX || 0;
          const dayShift = Math.round(deltaX / dayWidth);

          // Apply date shift to original dates
          const originalStartDate = new Date(assignmentDragState.assignment.startDate);
          const originalEndDate = new Date(assignmentDragState.assignment.endDate);
          const newStartDate = new Date(originalStartDate);
          const newEndDate = new Date(originalEndDate);
          newStartDate.setDate(newStartDate.getDate() + dayShift);
          newEndDate.setDate(newEndDate.getDate() + dayShift);

          try {
            console.log('Starting car API call - isCopying:', assignmentDragState.isCopying, 'dayShift:', dayShift);
            if (assignmentDragState.isCopying) {
              // COPY MODE - Create new car assignment with shifted dates
              const response = await axios.post(
                `${API_BASE_URL}/api/vehicle-assignments/`,
                {
                  car: currentDropTarget,
                  project: assignmentDragState.assignment.projectId,
                  assigned_date: formatDateForAPI(newStartDate),
                  expected_return_date: formatDateForAPI(newEndDate),
                  status: 'assigned',
                  assignment_reason: 'Copied from another car'
                },
                {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                }
              );

              console.log('Car assignment copied successfully:', response.data);

              const newAssignment = {
                id: response.data.id,
                carId: currentDropTarget,
                projectId: assignmentDragState.assignment.projectId,
                startDate: newStartDate,
                endDate: newEndDate,
                originalAssignment: response.data
              };

              setCarAssignments(prev => [...prev, newAssignment]);

              // Add to undo stack
              addToUndoStack({
                type: 'CREATE_CAR_ASSIGNMENT',
                assignmentId: newAssignment.id,
                data: {
                  carId: currentDropTarget,
                  projectId: assignmentDragState.assignment.projectId,
                  startDate: newStartDate,
                  endDate: newEndDate
                }
              });

              setCurrentStatus('Car assignment copied (Ctrl+Z to undo)');
            } else {
              // MOVE MODE - Update existing car assignment with new car and shifted dates
              const originalCarId = assignmentDragState.assignment.carId;
              const originalStartDateCar = assignmentDragState.assignment.startDate;
              const originalEndDateCar = assignmentDragState.assignment.endDate;

              console.log('Moving car assignment:', assignmentDragState.assignment.id, 'to car:', currentDropTarget, 'with dayShift:', dayShift);

              // Save to database immediately
              await axios.patch(
                `${API_BASE_URL}/api/vehicle-assignments/${assignmentDragState.assignment.id}/`,
                {
                  car: currentDropTarget,
                  assigned_date: formatDateForAPI(newStartDate),
                  expected_return_date: formatDateForAPI(newEndDate)
                },
                {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                }
              );

              console.log('Car assignment moved successfully');

              setCarAssignments(prev => prev.map(assignment =>
                assignment.id === assignmentDragState.assignment.id
                  ? {
                      ...assignment,
                      carId: currentDropTarget,
                      startDate: newStartDate,
                      endDate: newEndDate
                    }
                  : assignment
              ));

              // Add to pending changes for history tracking (but NOT for saving again)
              const updatedCarAssignment = {
                ...assignmentDragState.assignment,
                carId: currentDropTarget,
                car: currentDropTarget,
                startDate: newStartDate,
                endDate: newEndDate
              };
              addPendingCarUpdate(updatedCarAssignment);

              // Add to undo stack
              addToUndoStack({
                type: 'MOVE_CAR_ASSIGNMENT',
                moveType: 'CAR_CHANGE',
                assignmentId: assignmentDragState.assignment.id,
                originalData: {
                  ...assignmentDragState.assignment,
                  carId: originalCarId,
                  startDate: originalStartDateCar,
                  endDate: originalEndDateCar
                },
                newData: {
                  ...assignmentDragState.assignment,
                  carId: currentDropTarget,
                  startDate: newStartDate,
                  endDate: newEndDate
                }
              });

              setCurrentStatus('Car assignment moved (Ctrl+Z to undo)');
            }

          } catch (error) {
            logger.error('Error moving/copying car assignment:', error);
            logger.error('Error details:', error.response?.data);
            alert(`Failed to move/copy car assignment: ${error.response?.data?.detail || error.message}`);
          }
        }
      }

      setAssignmentDragState(null);
      setDropTargetUser(null);
      setDropTargetCar(null);
      dropTargetUserRef.current = null;
      dropTargetCarRef.current = null;
      document.body.style.cursor = 'default';
      return;
    }
    
    if (dragState || resizeState) {
      const dayWidth = getDayWidth;
      const days = getTimeRangeDays;

      if (dragState) {
        // Get final deltaX from element's dataset (set by handleMouseMove)
        const finalDeltaX = dragElementRef.current
          ? parseFloat(dragElementRef.current.dataset.currentDeltaX || 0)
          : dragState.deltaX || 0;
        const dayShift = Math.round(finalDeltaX / dayWidth);
        const originalAssignment = dragState.originalAssignment;

        // Check if originalAssignment exists and has required properties
        if (!originalAssignment || !originalAssignment.startDate || !originalAssignment.endDate) {
          logger.error('Invalid originalAssignment in dragState:', originalAssignment);
          // Reset transform before clearing state - use cached element
          if (dragElementRef.current) {
            dragElementRef.current.style.transform = '';
          }
          dragElementRef.current = null;
          dragElementOriginalWidth.current = null;
          // Restore text selection
          document.body.style.userSelect = '';
          document.body.style.webkitUserSelect = '';
          setDragState(null);
          return;
        }

        const newStartDate = new Date(originalAssignment.startDate);
        const newEndDate = new Date(originalAssignment.endDate);
        newStartDate.setDate(newStartDate.getDate() + dayShift);
        newEndDate.setDate(newEndDate.getDate() + dayShift);

        const firstDay = days[0];
        const lastDay = days[days.length - 1];

        // Normalize dates for comparison (remove time component)
        const normalizedNewStartDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth(), newStartDate.getDate());
        const normalizedNewEndDate = new Date(newEndDate.getFullYear(), newEndDate.getMonth(), newEndDate.getDate());
        const normalizedFirstDay = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
        const normalizedLastDay = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());

        // Check if dates are valid - allow projects to extend beyond visible range
        // Only require that the project starts within or before the visible range
        const isStartValid = normalizedNewStartDate <= normalizedLastDay;
        const isEndValid = normalizedNewEndDate >= normalizedFirstDay;
        const isDatesOrdered = normalizedNewStartDate <= normalizedNewEndDate;

        const isValidDateRange = isStartValid && isEndValid && isDatesOrdered;

        // Only update if there's actual movement AND the dates are within valid range
        if (Math.abs(dayShift) > 0 && isValidDateRange) {
            try {
              // Check if this is copy mode (ALT was pressed)
              if (dragState.isCopying) {
                // COPY MODE - Create new assignment
                if (originalAssignment.carId) {
                  // Car assignment copy - create new vehicle assignment with new dates
                  const payload = {
                    car: originalAssignment.carId,
                    project: originalAssignment.projectId,
                    assigned_date: formatDateForAPI(newStartDate),
                    expected_return_date: formatDateForAPI(newEndDate),
                    status: 'assigned',
                    assignment_reason: 'Resource planning assignment (copied)'
                  };

                  // Add user if present
                  if (originalAssignment.userId) {
                    payload.user = originalAssignment.userId;
                  }

                  const response = await axios.post(
                    `${API_BASE_URL}/api/vehicle-assignments/`,
                    payload,
                    {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                    }
                  );

                  const newCarAssignment = {
                    id: response.data.id,
                    carId: originalAssignment.carId,
                    userId: originalAssignment.userId || null,
                    projectId: originalAssignment.projectId,
                    startDate: newStartDate,
                    endDate: newEndDate,
                    originalAssignment: response.data
                  };

                  // Add to state with React Strict Mode protection
                  setCarAssignments(prev => {
                    if (prev.some(a => a.id === newCarAssignment.id)) return prev;
                    return [...prev, newCarAssignment];
                  });
                  setSavedCarAssignments(prev => {
                    if (prev.some(a => a.id === newCarAssignment.id)) return prev;
                    return [...prev, newCarAssignment];
                  });

                  setCurrentStatus('Car assignment copied successfully');
                  logger.info('Car assignment copied:', { from: originalAssignment.id, to: response.data.id });
                } else {
                  // User assignment - create copy
                  const newAssignment = await resourceBoardService.createAssignment({
                    user: originalAssignment.userId,
                    project: originalAssignment.projectId,
                    start_date: formatDateForAPI(newStartDate),
                    end_date: formatDateForAPI(newEndDate),
                    role: originalAssignment.role
                  });

                  setAssignments(prev => [...prev, {
                    id: newAssignment.id,
                    userId: originalAssignment.userId,
                    projectId: originalAssignment.projectId,
                    projectName: originalAssignment.projectName, // Preserve tile name
                    startDate: newStartDate,
                    endDate: newEndDate,
                    role: originalAssignment.role,
                    hexColor: originalAssignment.hexColor, // Preserve custom color
                    isSpecialTile: originalAssignment.isSpecialTile // Preserve special tile flag
                  }]);

                  // Add to undo stack
                  addToUndoStack({
                    type: 'CREATE_ASSIGNMENT',
                    data: {
                      id: newAssignment.id,
                      userId: originalAssignment.userId,
                      projectId: originalAssignment.projectId,
                      startDate: newStartDate,
                      endDate: newEndDate,
                      role: originalAssignment.role
                    }
                  });

                  setCurrentStatus('Assignment copied (Ctrl+Z to undo)');
                }
              } else {
                // MOVE MODE - Update existing assignment
                // Check if this is a car assignment (has carId) or user assignment (has userId)
                if (originalAssignment.carId) {
                  // Car assignment - save to database immediately, but also add to pending changes for history
                  await resourceBoardService.updateVehicleAssignmentDates(
                    dragState.assignmentId,
                    formatDateForAPI(newStartDate),
                    formatDateForAPI(newEndDate)
                  );

                  setCarAssignments(prev => {
                    if (!Array.isArray(prev)) {
                      logger.error('prev is not an array!', prev);
                      return prev;
                    }

                    return prev.map(assignment =>
                      assignment.id === dragState.assignmentId
                        ? { ...assignment, startDate: newStartDate, endDate: newEndDate }
                        : assignment
                    );
                  });

                  // Add to pending changes for history tracking (but NOT for saving again)
                  const updatedCarAssignment = {
                    ...originalAssignment,
                    startDate: newStartDate,
                    endDate: newEndDate,
                    start_date: formatDateForAPI(newStartDate),
                    end_date: formatDateForAPI(newEndDate)
                  };
                  addPendingCarUpdate(updatedCarAssignment);

                  // Add to undo stack - this is a car assignment saved directly to API
                  addToUndoStack({
                    type: 'MOVE_CAR_ASSIGNMENT',
                    moveType: 'DATE_CHANGE',
                    assignmentId: dragState.assignmentId,
                    originalData: originalAssignment,
                    newData: { ...originalAssignment, startDate: newStartDate, endDate: newEndDate }
                  });
                } else {
                  logger.warn(`Updating USER assignment ${dragState.assignmentId} (userId: ${originalAssignment.userId})`);

                  // Use pending changes system instead of immediate API call
                  const updatedAssignment = {
                    ...originalAssignment,
                    startDate: newStartDate,
                    endDate: newEndDate,
                    start_date: formatDateForAPI(newStartDate),
                    end_date: formatDateForAPI(newEndDate)
                  };

                  addPendingUpdate(updatedAssignment);

                  // Add to undo stack - use PENDING_UPDATE since this is a local change
                  addToUndoStack({
                    type: 'PENDING_UPDATE',
                    assignmentId: dragState.assignmentId,
                    originalData: originalAssignment,
                    newData: updatedAssignment
                  });

                  logger.warn('Updated user assignments:', {
                    assignmentId: dragState.assignmentId,
                    updated: true
                  });
                }
              }
            } catch (error) {
              logger.error('Error updating assignment dates:', error);

              // If assignment doesn't exist (404), blacklist it and remove from local state
              if (error.message?.includes('404') || error.message?.includes('not found') || error.message?.includes('No ResourcePlanningAssignment')) {
                logger.warn(`Assignment ${dragState.assignmentId} not found in database, adding to blacklist and removing from local state`);

                // Add to blacklist to prevent it from being loaded again
                setBlacklistedAssignments(prev => new Set([...prev, dragState.assignmentId]));

                if (originalAssignment.carId) {
                  // Remove from car assignments
                  setCarAssignments(prev => prev.filter(a => a.id !== dragState.assignmentId));
                } else {
                  // Remove from user assignments
                  setAssignments(prev => prev.filter(a => a.id !== dragState.assignmentId));
                }
              }
            }
          }
        // Reset transform before clearing state - use cached element
        if (dragElementRef.current) {
          dragElementRef.current.style.transform = '';
        }
        dragElementRef.current = null;
        dragElementOriginalWidth.current = null;
        // Restore text selection
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
        setDragState(null);
      }

      if (resizeState) {
        // Get final deltaX from element's dataset (set by handleMouseMove)
        const finalDeltaX = dragElementRef.current
          ? parseFloat(dragElementRef.current.dataset.currentDeltaX || 0)
          : resizeState.deltaX || 0;
        const dayShift = Math.round(finalDeltaX / dayWidth);
        const originalAssignment = resizeState.originalAssignment;

        // Check if originalAssignment exists and has required properties
        if (!originalAssignment || !originalAssignment.startDate || !originalAssignment.endDate) {
          logger.error('Invalid originalAssignment in resizeState:', originalAssignment);
          // Reset transform and width before clearing state - use cached element
          if (dragElementRef.current) {
            dragElementRef.current.style.transform = '';
            if (dragElementOriginalWidth.current !== null) {
              dragElementRef.current.style.width = `${dragElementOriginalWidth.current}px`;
            }
          }
          dragElementRef.current = null;
          dragElementOriginalWidth.current = null;
          // Restore text selection
          document.body.style.userSelect = '';
          document.body.style.webkitUserSelect = '';
          setResizeState(null);
          return;
        }

        let newStartDate = new Date(originalAssignment.startDate);
        let newEndDate = new Date(originalAssignment.endDate);

        if (resizeState.handle === 'left') {
          newStartDate.setDate(newStartDate.getDate() + dayShift);
        } else if (resizeState.handle === 'right') {
          newEndDate.setDate(newEndDate.getDate() + dayShift);
        }

        const days = getTimeRangeDays;
        const firstDay = days[0];
        const lastDay = days[days.length - 1];

        // Normalize dates for comparison (remove time component)
        const normalizedNewStartDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth(), newStartDate.getDate());
        const normalizedNewEndDate = new Date(newEndDate.getFullYear(), newEndDate.getMonth(), newEndDate.getDate());
        const normalizedFirstDay = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
        const normalizedLastDay = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());

        // Check if dates are valid - allow resizing beyond visible range
        // Only require that the project partially overlaps with visible range
        const isStartValid = normalizedNewStartDate <= normalizedLastDay;
        const isEndValid = normalizedNewEndDate >= normalizedFirstDay;
        const isDatesOrdered = normalizedNewStartDate <= normalizedNewEndDate;

        const isValidDateRange = isStartValid && isEndValid && isDatesOrdered;

        if (Math.abs(dayShift) > 0 && isValidDateRange) {
            try {
              // Check if this is a car assignment (has carId) or user assignment (has userId)
              if (originalAssignment.carId) {
                // Car assignment - save to database immediately, but also add to pending changes for history
                await resourceBoardService.updateVehicleAssignmentDates(
                  resizeState.assignmentId,
                  formatDateForAPI(newStartDate),
                  formatDateForAPI(newEndDate)
                );

                setCarAssignments(prev => prev.map(assignment =>
                  assignment.id === resizeState.assignmentId
                    ? { ...assignment, startDate: newStartDate, endDate: newEndDate }
                    : assignment
                ));

                // Add to pending changes for history tracking (but NOT for saving again)
                const updatedCarAssignment = {
                  ...originalAssignment,
                  startDate: newStartDate,
                  endDate: newEndDate,
                  start_date: formatDateForAPI(newStartDate),
                  end_date: formatDateForAPI(newEndDate)
                };
                addPendingCarUpdate(updatedCarAssignment);

                // Add to undo stack - this is a car assignment saved directly to API
                addToUndoStack({
                  type: 'MOVE_CAR_ASSIGNMENT',
                  moveType: 'DATE_CHANGE',
                  assignmentId: resizeState.assignmentId,
                  originalData: originalAssignment,
                  newData: { ...originalAssignment, startDate: newStartDate, endDate: newEndDate }
                });
              } else {
                // User assignment - use pending changes system
                const updatedAssignment = {
                  ...originalAssignment,
                  startDate: newStartDate,
                  endDate: newEndDate,
                  start_date: formatDateForAPI(newStartDate),
                  end_date: formatDateForAPI(newEndDate)
                };

                addPendingUpdate(updatedAssignment);

                // Add to undo stack - use PENDING_UPDATE since this is a local change
                addToUndoStack({
                  type: 'PENDING_UPDATE',
                  assignmentId: resizeState.assignmentId,
                  originalData: originalAssignment,
                  newData: updatedAssignment
                });
              }
            } catch (error) {
              logger.error('Error updating assignment dates:', error);

              // If assignment doesn't exist (404), blacklist it and remove from local state
              if (error.message?.includes('404') || error.message?.includes('not found') || error.message?.includes('No ResourcePlanningAssignment')) {
                logger.warn(`Assignment ${resizeState.assignmentId} not found in database, adding to blacklist and removing from local state`);

                // Add to blacklist to prevent it from being loaded again
                setBlacklistedAssignments(prev => new Set([...prev, resizeState.assignmentId]));

                if (originalAssignment.carId) {
                  // Remove from car assignments
                  setCarAssignments(prev => prev.filter(a => a.id !== resizeState.assignmentId));
                } else {
                  // Remove from user assignments
                  setAssignments(prev => prev.filter(a => a.id !== resizeState.assignmentId));
                }
              }
            }
          }
        // Reset transform and width before clearing state - use cached element
        if (dragElementRef.current) {
          dragElementRef.current.style.transform = '';
          if (dragElementOriginalWidth.current !== null) {
            dragElementRef.current.style.width = `${dragElementOriginalWidth.current}px`;
          }
        }
        dragElementRef.current = null;
        dragElementOriginalWidth.current = null;
        // Restore text selection
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
        setResizeState(null);
      }
    }
    // Car assignments use only modal edit - no mouse up interactions
  };

  useEffect(() => {
    if (dragState || resizeState || assignmentDragState) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Cancel any pending animation frame
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [dragState, resizeState, assignmentDragState]);

  // Synchronize scrolling between header and body timelines
  const handleBodyScroll = useCallback((e) => {
    // Synchronize horizontal scroll to header
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = e.target.scrollLeft;
    }
    // Synchronize vertical scroll with personnel column
    if (personnelColumnRef.current) {
      personnelColumnRef.current.scrollTop = e.target.scrollTop;
    }
  }, [timelineRef, personnelColumnRef]);

  const handlePersonnelScroll = useCallback((e) => {
    // Synchronize personnel column vertical scroll with timeline body
    if (bodyTimelineRef.current) {
      bodyTimelineRef.current.scrollTop = e.target.scrollTop;
    }
  }, [bodyTimelineRef]);


  // Update status based on current actions
  useEffect(() => {
    if (assignmentDragState?.isDragging) {
      setCurrentStatus('Moving assignment between users');
    } else if (dragState) {
      setCurrentStatus('Adjusting assignment timeline');
    } else if (resizeState) {
      setCurrentStatus('Resizing assignment duration');
    } else if (editingComment) {
      setCurrentStatus('Editing assignment comment');
    } else if (showTravelModal) {
      setCurrentStatus('Planning travel');
    } else {
      setCurrentStatus(null);
    }
  }, [assignmentDragState, dragState, resizeState, editingComment, showTravelModal]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTravelContextMenu) {
        setShowTravelContextMenu(false);
        setContextMenuData(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showTravelContextMenu]);

  // Handle Shift + Scroll and Horizontal Scroll for timeline navigation
  useEffect(() => {
    const handleWheelScroll = (e) => {
      // Check if it's horizontal scroll (deltaX != 0) or Shift + vertical scroll
      const isHorizontalScroll = Math.abs(e.deltaX) > 0;
      const isShiftScroll = e.shiftKey && Math.abs(e.deltaY) > 0;

      if (isHorizontalScroll || isShiftScroll) {
        e.preventDefault();
        e.stopPropagation();

        // Calculate scroll amount and convert to days
        const scrollAmount = isHorizontalScroll ? e.deltaX : e.deltaY;
        // Convert pixels to days: every ~100px = 1 day
        const daysToMove = Math.round(scrollAmount / 100);

        if (daysToMove !== 0) {
          setDayOffset(prevOffset => {
            const maxOffset = getMaxOffset();
            const newOffset = prevOffset + daysToMove;
            // Clamp between -maxOffset and +maxOffset
            return Math.max(-maxOffset, Math.min(maxOffset, newOffset));
          });
        }
      }
    };

    // Add event listener to document to catch all wheel events
    document.addEventListener('wheel', handleWheelScroll, { passive: false });
    return () => document.removeEventListener('wheel', handleWheelScroll);
  }, [getMaxOffset]);

  const openNewAssignmentModal = () => {
    setIsNewAssignmentModalOpen(true);
  };

  const handleProjectDragStart = (e, project) => {
    const dragData = {
      type: 'project',
      project: project,
      role: 'Team Member'
    };
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
  };

  const handleRoleDragStart = (e, project, role) => {
    e.stopPropagation();
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'role',
      project: project,
      role: role.name
    }));
  };

  // Custom tiles management
  const addCustomTile = async (name) => {
    if (!name || !name.trim()) return;

    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const response = await axios.post(`${API_BASE_URL}/api/custom-tiles/`, {
        name: name.trim(),
        hex_color: customColorHex
      }, { headers });

      console.log('Created custom tile:', response.data);
      // Transform snake_case to camelCase for frontend
      const newTile = {
        id: response.data.id,
        name: response.data.name,
        hexColor: response.data.hex_color
      };
      setCustomTiles(prev => [...prev, newTile]);
      setNewTileInput('');
      setCustomColorHex('#9333ea'); // Reset to default purple
    } catch (error) {
      console.error('Error creating custom tile:', error);
      alert('Failed to create custom tile. Please try again.');
    }
  };

  const removeCustomTile = async (tileId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE_URL}/api/custom-tiles/${tileId}/`, { headers });

      console.log('Deleted custom tile:', tileId);
      setCustomTiles(prev => prev.filter(t => t.id !== tileId));
    } catch (error) {
      console.error('Error deleting custom tile:', error);
      alert('Failed to delete custom tile. Please try again.');
    }
  };

  const checkForOverlaps = (newAssignment, userId) => {
    const userAssignments = (Array.isArray(assignments) ? assignments : []).filter(a => a.userId === userId);
    const overlaps = [];

    userAssignments.forEach(existing => {
      if (newAssignment.startDate <= existing.endDate && newAssignment.endDate >= existing.startDate) {
        const project = (Array.isArray(projects) ? projects : []).find(p => p.id === existing.projectId);
        overlaps.push({
          assignment: existing,
          projectName: project?.name || 'Unknown Project'
        });
      }
    });
    
    return overlaps;
  };

  const handleUserDrop = async (e, user, dayIndex) => {
    e.preventDefault();

    try {
      const dragDataString = e.dataTransfer.getData('application/json');

      if (!dragDataString) {
        console.log('No drag data found');
        return;
      }

      const dragData = JSON.parse(dragDataString);
      console.log('Dropped item:', dragData);

      if (dragData.type === 'project' || dragData.type === 'role' || dragData.type === 'special-tile') {
        const days = getTimeRangeDays;
        const actualIndex = Math.min(dayIndex, days.length - 1);
        const startDate = days[actualIndex] || new Date();

        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 2);

        const newAssignment = {
          userId: user.id,
          projectId: dragData.project.id,
          startDate: startDate,
          endDate: endDate,
          role: dragData.role
        };

        // Check for overlaps
        const overlaps = checkForOverlaps(newAssignment, user.id);

        if (overlaps.length > 0) {
          const overlapMessage = overlaps.map(o => o.projectName).join(', ');
          const confirmMessage = `This assignment will overlap with existing project(s): ${overlapMessage}\n\nDo you want to continue?`;

          if (!window.confirm(confirmMessage)) {
            return;
          }
        }

        // Use pending changes system instead of immediate API call
        const createdAssignment = {
          userId: user.id,
          user: user.id,
          projectId: dragData.project.id,
          project: dragData.project.id,
          projectName: dragData.project.name, // Store project/tile name
          startDate: startDate,
          start_date: formatDateForAPI(startDate),
          endDate: endDate,
          end_date: formatDateForAPI(endDate),
          role: dragData.role,
          hexColor: dragData.project.hexColor || null, // Store custom hex color
          isSpecialTile: dragData.type === 'special-tile'
        };

        addPendingCreate(createdAssignment);
        setCurrentStatus('Tag created (not saved yet)');
      }
    } catch (error) {
      logger.error('Error handling drop:', error);
    }
  };

  const handleUserRoleDrop = async (e, user) => {
    e.preventDefault();
    
    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      if (!dragDataString) return;
      
      const dragData = JSON.parse(dragDataString);
      
      if (dragData.type === 'global-role') {
        const globalRole = globalRoles.find(r => r.name === dragData.role);
        if (globalRole) {
          try {
            await axios.post(
              `${API_BASE_URL}/api/user-global-roles/`,
              {
                user: user.id,
                role: globalRole.id
              },
              {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
              }
            );
            
            setUserRoles(prev => ({
              ...prev,
              [user.id]: {
                role: dragData.role,
                color: globalRole.color
              }
            }));
          } catch (error) {
            logger.error('Error assigning role to user:', error);
          }
        }
      } else if (dragData.type === 'role') {
        let roleColor = 'bg-steel-100 text-slate-800 border-steel-200';
        const roleColors = [
          'bg-red-100 text-red-800 border-red-200',
          'bg-orange-100 text-orange-800 border-orange-200', 
          'bg-yellow-100 text-yellow-800 border-yellow-200',
          'bg-green-100 text-green-800 border-green-200',
          'bg-brand-100 text-brand-800 border-brand-200',
          'bg-purple-100 text-purple-800 border-purple-200'
        ];
        roleColor = roleColors[Math.floor(Math.random() * roleColors.length)];
        
        setUserRoles(prev => ({
          ...prev,
          [user.id]: {
            role: dragData.role,
            color: roleColor
          }
        }));
      }
    } catch (error) {
      logger.error('Error handling role drop:', error);
    }
  };

  const handleUserDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  // ⚡ OPTIMIZED: Memoized to prevent recalculation on every render
  const getFilteredUsers = useMemo(() => {
    // Early return when loading or no data
    if (loading || !users || users.length === 0) return [];

    let filteredUsers = users;

    // Filter out disabled users unless explicitly showing them
    if (!showDisabledUsers) {
      filteredUsers = filteredUsers.filter(user => !disabledUsers.includes(user.id));
    }

    // Filter by selected users in quick search (header multi-select)
    if (selectedQuickUsers.length > 0) {
      const selectedQuickUserIds = selectedQuickUsers.map(u => u.id);
      filteredUsers = filteredUsers.filter(user => selectedQuickUserIds.includes(user.id));
    }

    // Filter by selected users in filter panel (if any)
    if (selectedUsers.length > 0) {
      filteredUsers = filteredUsers.filter(user => selectedUsers.includes(user.id));
    }

    // Then filter by selected projects - show only users who have assignments in those projects
    if (selectedProjects.length > 0) {
      const usersInSelectedProjects = new Set();
      (Array.isArray(assignments) ? assignments : [])
        .filter(assignment => selectedProjects.includes(assignment.projectId))
        .forEach(assignment => usersInSelectedProjects.add(assignment.userId));

      filteredUsers = filteredUsers.filter(user => usersInSelectedProjects.has(user.id));
    }

    // Filter by selected customers - show only users who have assignments in projects with those customers
    if (selectedCustomers.length > 0) {
      const usersInCustomerProjects = new Set();
      (Array.isArray(assignments) ? assignments : [])
        .forEach(assignment => {
          const project = projects.find(p => p.id === assignment.projectId);
          if (project && project.customer && selectedCustomers.includes(project.customer)) {
            usersInCustomerProjects.add(assignment.userId);
          }
        });

      filteredUsers = filteredUsers.filter(user => usersInCustomerProjects.has(user.id));
    }

    // Filter by selected roles - show only users with selected roles
    if (selectedRoles.length > 0) {
      filteredUsers = filteredUsers.filter(user => {
        const userRole = userGlobalRoles.find(
          ugr => (ugr.user === user.id || ugr.user_id === user.id)
        );
        return userRole && selectedRoles.includes(userRole.role);
      });
    }

    return filteredUsers.sort((a, b) => {
      // If a specific role is selected for sorting, prioritize users with that role
      if (selectedRoleSort && selectedRoleSort !== 'all') {
        const aHasSelectedRole = userGlobalRoles.find(
          ugr => (ugr.user === a.id || ugr.user_id === a.id) && ugr.role === parseInt(selectedRoleSort)
        );
        const bHasSelectedRole = userGlobalRoles.find(
          ugr => (ugr.user === b.id || ugr.user_id === b.id) && ugr.role === parseInt(selectedRoleSort)
        );

        // Users with selected role come first
        if (aHasSelectedRole && !bHasSelectedRole) return -1;
        if (!aHasSelectedRole && bHasSelectedRole) return 1;

        // If both have or both don't have selected role, continue to role order sorting
      }

      // Get role order index from roleOrder state (lower index = higher priority)
      const getRoleOrderIndex = (userId) => {
        if (!userRoles[userId]) return 9999; // No role = lowest priority

        // Get the global role for this user
        const userGlobalRole = userGlobalRoles.find(
          ugr => (ugr.user === userId || ugr.user_id === userId)
        );

        if (!userGlobalRole) return 9999; // No role assignment = lowest priority

        const roleId = userGlobalRole.role;
        const orderIndex = roleOrder.indexOf(roleId);

        // If role not in order list, put at end
        return orderIndex === -1 ? 9999 : orderIndex;
      };

      const aIndex = getRoleOrderIndex(a.id);
      const bIndex = getRoleOrderIndex(b.id);

      // First sort by role order
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      // Then by name within same role level
      return (a.last_name || '').localeCompare(b.last_name || '');
    });
  }, [loading, users, showDisabledUsers, disabledUsers, selectedQuickUsers, selectedUsers, selectedRoles, selectedRoleSort, selectedProjects, selectedCustomers, assignments, projects, userRoles, userGlobalRoles, roleOrder]);

  // ⚡ OPTIMIZED: Memoized to prevent recalculation on every render
  const getFilteredCars = useMemo(() => {
    // Early return when loading or no data
    if (loading || !carsData || carsData.length === 0) return [];

    let filteredCars = carsData;

    // First filter by selected cars if any
    if (selectedCars.length > 0) {
      filteredCars = filteredCars.filter(car => selectedCars.includes(car.id));
    }

    // Then filter by selected projects - show only cars that have assignments in those projects
    if (selectedProjects.length > 0) {
      const carsInSelectedProjects = new Set();
      carAssignments
        .filter(assignment => selectedProjects.includes(assignment.projectId))
        .forEach(assignment => carsInSelectedProjects.add(assignment.carId));

      filteredCars = filteredCars.filter(car => carsInSelectedProjects.has(car.id));
    }

    // Filter by selected customers - show only cars that have assignments in projects with those customers
    if (selectedCustomers.length > 0) {
      const carsInCustomerProjects = new Set();
      carAssignments.forEach(assignment => {
        const project = projects.find(p => p.id === assignment.projectId);
        if (project && project.customer && selectedCustomers.includes(project.customer)) {
          carsInCustomerProjects.add(assignment.carId);
        }
      });

      filteredCars = filteredCars.filter(car => carsInCustomerProjects.has(car.id));
    }

    return filteredCars.sort((a, b) => {
      return `${a.car_brand} ${a.car_model}`.localeCompare(`${b.car_brand} ${b.car_model}`);
    });
  }, [loading, carsData, selectedCars, selectedProjects, selectedCustomers, carAssignments, projects]);

  // ⚡ OPTIMIZED: Memoized to prevent recalculation on every render
  const getFilteredProjects = useMemo(() => {
    // Early return when loading or no data
    if (loading || !projects || projects.length === 0) return [];

    // Filter out inactive projects from the project list/panel
    let filteredProjects = projects.filter(project => project.is_active !== false);

    if (selectedProjects.length > 0) {
      filteredProjects = filteredProjects.filter(project => selectedProjects.includes(project.id));
    }

    if (selectedCustomers.length > 0) {
      filteredProjects = filteredProjects.filter(project =>
        project.customer && selectedCustomers.includes(project.customer)
      );
    }

    return filteredProjects;
  }, [loading, projects, selectedProjects, selectedCustomers]);

  // ⚡ OPTIMIZED: Memoized to prevent recalculation on every render
  const getFilteredAssignments = useMemo(() => {
    // Early return when loading or no data
    if (loading || !assignments || assignments.length === 0) return [];

    let filteredAssignments = Array.isArray(assignments) ? assignments : [];

    if (selectedUsers.length > 0) {
      filteredAssignments = filteredAssignments.filter(assignment =>
        selectedUsers.includes(assignment.userId)
      );
    }

    // NOTE: We NO LONGER filter by selectedProjects here!
    // Instead, we show ALL assignments but visually "mute" those that don't match the filter.
    // This allows users to see other allocations (training, vacation, other projects) when filtering.

    if (selectedCustomers.length > 0) {
      filteredAssignments = filteredAssignments.filter(assignment => {
        const project = projects.find(p => p.id === assignment.projectId);
        return project && project.customer && selectedCustomers.includes(project.customer);
      });
    }

    return filteredAssignments;
  }, [loading, assignments, selectedUsers, selectedCustomers, projects]);

  // Helper function to check if an assignment should be visually "muted"
  // An assignment is muted when project filter is active and this assignment doesn't match
  const isAssignmentMuted = useCallback((assignment) => {
    // If no project filter is active, nothing is muted
    if (selectedProjects.length === 0) return false;

    // Special tiles (quick tags like training, vacation) are never muted - always show clearly
    if (assignment.isSpecialTile) return false;

    // Check if assignment's project is in the selected filter
    return !selectedProjects.includes(assignment.projectId);
  }, [selectedProjects]);

  // ⚡ OPTIMIZED: Memoized to prevent recalculation on every render
  const getFilteredCarAssignments = useMemo(() => {
    let filteredCarAssignments = Array.isArray(carAssignments) ? carAssignments : [];

    if (selectedCars.length > 0) {
      filteredCarAssignments = filteredCarAssignments.filter(assignment =>
        selectedCars.includes(assignment.carId)
      );
    }

    // NOTE: We NO LONGER filter by selectedProjects here!
    // Instead, we show ALL car assignments but visually "mute" those that don't match the filter.

    if (selectedCustomers.length > 0) {
      filteredCarAssignments = filteredCarAssignments.filter(assignment => {
        const project = projects.find(p => p.id === assignment.projectId);
        return project && project.customer && selectedCustomers.includes(project.customer);
      });
    }

    return filteredCarAssignments;
  }, [carAssignments, selectedCars, selectedCustomers, projects]);

  // Helper function to check if a car assignment should be visually "muted"
  const isCarAssignmentMuted = useCallback((assignment) => {
    if (selectedProjects.length === 0) return false;
    return !selectedProjects.includes(assignment.projectId);
  }, [selectedProjects]);

  // ⚡ CRITICAL: Memoize all track calculations per user to avoid O(n²) on every render
  const userTracksCache = useMemo(() => {
    // Early return when loading or no data
    if (loading || getFilteredUsers.length === 0) return new Map();

    const cache = new Map();

    getFilteredUsers.forEach(user => {
      const userAssignments = getFilteredAssignments.filter(a => a.userId === user.id);
      const tracks = {};

      // Sort assignments by start date
      const sortedAssignments = [...userAssignments].sort((a, b) =>
        new Date(a.startDate) - new Date(b.startDate)
      );

      // Assign each assignment to a track
      sortedAssignments.forEach(assignment => {
        // Find the first available track (starting from 0)
        let trackNumber = 0;
        let trackAvailable = false;

        while (!trackAvailable) {
          // Check if this track is available for this assignment's date range
          const overlaps = sortedAssignments.some(other => {
            if (other.id === assignment.id) return false;
            if ((tracks[other.id] || 0) !== trackNumber) return false;

            // Check date overlap
            const assignmentStart = new Date(assignment.startDate);
            const assignmentEnd = new Date(assignment.endDate);
            const otherStart = new Date(other.startDate);
            const otherEnd = new Date(other.endDate);

            return assignmentStart <= otherEnd && assignmentEnd >= otherStart;
          });

          if (!overlaps) {
            trackAvailable = true;
            tracks[assignment.id] = trackNumber;
          } else {
            trackNumber++;
          }
        }
      });

      const trackNumbers = Object.values(tracks);
      const maxTracks = trackNumbers.length > 0 ? Math.max(...trackNumbers) + 1 : 1;

      cache.set(user.id, { tracks, maxTracks });
    });

    return cache;
  }, [loading, getFilteredUsers, getFilteredAssignments]);

  const calculateAssignmentTracks = (userId) => {
    return userTracksCache.get(userId)?.tracks || {};
  };

  const getMaxTracksForUser = (userId) => {
    return userTracksCache.get(userId)?.maxTracks || 1;
  };

  const handleAssignmentDoubleClick = useCallback((e, assignment) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedAssignment(assignment);
    setEditingComment(assignment.id);
    setCommentInput(assignmentComments[assignment.id] || '');
    // Set initial copy dates to assignment dates
    setCopyStartDate(formatDateForAPI(assignment.startDate));
    setCopyEndDate(formatDateForAPI(assignment.endDate));
    setShowAssignmentActions(true);
  }, [assignmentComments, setSelectedAssignment, setEditingComment, setCommentInput, setCopyStartDate, setCopyEndDate, setShowAssignmentActions]);

  const handleCarAssignmentDoubleClick = useCallback((e, assignment) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCarAssignment(assignment);
    setShowCarAssignmentEdit(true);
  }, [setSelectedCarAssignment, setShowCarAssignmentEdit]);

  const handleTimelineCellDoubleClick = useCallback((userId, date) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setQuickAssignmentData({
      userId: userId,
      userName: `${user.last_name} ${user.first_name}`,
      date: date
    });
    setShowQuickAssignmentModal(true);
  }, [users, setQuickAssignmentData, setShowQuickAssignmentModal]);

  // Close PDF Extract Modal and reset search
  const closePdfExtractModal = () => {
    setShowPdfExtractModal(false);
    setPdfExtractConfig(prev => ({
      ...prev,
      userSearchQuery: '', // Clear search query when closing modal
      projectSearchQuery: '' // Clear project search query when closing modal
    }));
  };

  // Generate PDF Extract - TIMELINE FORMAT
  const generatePDF = async () => {
    try {
      // Lazy load jsPDF and Roboto font to reduce initial bundle size (~600KB savings)
      const [{ default: jsPDF }, { robotoRegularBase64 }] = await Promise.all([
        import('jspdf'),
        import('../ecc/roboto')
      ]);
      await import('jspdf-autotable');

      console.log('Generating PDF...');
      console.log('Users:', users);
      console.log('Projects:', projects);
      console.log('Assignments:', assignments);

      // Helper function to convert week to date range
      const weekToDateRange = (weekString) => {
        // weekString format: "2025-W40"
        const [year, week] = weekString.split('-W').map(Number);
        const simple = new Date(year, 0, 1 + (week - 1) * 7);
        const dow = simple.getDay();
        const ISOweekStart = simple;
        if (dow <= 4)
          ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
        else
          ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());

        const weekStart = new Date(ISOweekStart);
        const weekEnd = new Date(ISOweekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        return { start: weekStart, end: weekEnd };
      };

      // Determine date range based on mode
      let startDate, endDate;
      if (pdfExtractConfig.mode === 'weeks') {
        const startRange = weekToDateRange(pdfExtractConfig.startWeek);
        const endRange = weekToDateRange(pdfExtractConfig.endWeek);
        startDate = startRange.start;
        endDate = endRange.end;
        console.log('Week mode - Start week:', pdfExtractConfig.startWeek, 'End week:', pdfExtractConfig.endWeek);
      } else {
        startDate = new Date(pdfExtractConfig.startDate);
        endDate = new Date(pdfExtractConfig.endDate);
        console.log('Date mode - Start:', pdfExtractConfig.startDate, 'End:', pdfExtractConfig.endDate);
      }

      console.log('Calculated date range:');
      console.log('  Start:', startDate, startDate.toISOString());
      console.log('  End:', endDate, endDate.toISOString());
      console.log('PDF Config:', pdfExtractConfig);
      console.log('Total assignments to filter:', assignments.length);

      // Add diagnostic logging for first assignment to see date format
      if (assignments.length > 0) {
        console.log('Sample assignment for debugging:', {
          startDate: assignments[0].startDate,
          start_date: assignments[0].start_date,
          endDate: assignments[0].endDate,
          end_date: assignments[0].end_date,
          parsed_start: new Date(assignments[0].startDate || assignments[0].start_date),
          parsed_end: new Date(assignments[0].endDate || assignments[0].end_date)
        });
      }

      // Normalize startDate and endDate to beginning of day (local time) for comparison
      const normalizedStart = new Date(startDate);
      normalizedStart.setHours(0, 0, 0, 0);
      const normalizedEnd = new Date(endDate);
      normalizedEnd.setHours(23, 59, 59, 999);

      console.log('Normalized date range for filtering:', {
        start: normalizedStart,
        end: normalizedEnd
      });

      // Filter assignments based on date range and project
      const filteredAssignments = assignments.filter((assignment, index) => {
        const assignStart = new Date(assignment.startDate || assignment.start_date);
        const assignEnd = new Date(assignment.endDate || assignment.end_date);

        // Normalize assignment dates to beginning/end of day
        assignStart.setHours(0, 0, 0, 0);
        assignEnd.setHours(23, 59, 59, 999);

        // Check if assignment overlaps with selected date range
        const overlaps = assignStart <= normalizedEnd && assignEnd >= normalizedStart;

        // Check if this is a special tile (quick tag) - always include them regardless of project filter
        const isSpecialTile = assignment.isSpecialTile || assignment.is_special_tile;

        // Check project filter (multiple projects support)
        // Special tiles (quick tags) always pass the project filter
        const projectId = assignment.projectId || assignment.project_id || assignment.project;
        const projectMatch = isSpecialTile ||
          !Array.isArray(pdfExtractConfig.projectIds) || pdfExtractConfig.projectIds.length === 0 ||
          pdfExtractConfig.projectIds.includes(projectId);

        // Debug log for special tiles and first few assignments
        if (isSpecialTile || index < 3) {
          console.log(`Assignment ${index}:`, {
            assignStart,
            assignEnd,
            overlaps,
            projectMatch,
            isSpecialTile,
            projectName: assignment.projectName || assignment.custom_name,
            result: overlaps && projectMatch
          });
        }

        return overlaps && projectMatch;
      });

      console.log('Filtered assignments:', filteredAssignments);
      console.log(`Filtering result: ${filteredAssignments.length} out of ${assignments.length} assignments match`);

      // Log special tiles in filtered results
      const specialTilesInFiltered = filteredAssignments.filter(a => a.isSpecialTile || a.is_special_tile);
      console.log(`Special tiles in filtered: ${specialTilesInFiltered.length}`, specialTilesInFiltered.map(a => ({
        id: a.id,
        projectName: a.projectName,
        customName: a.custom_name,
        userId: a.userId
      })));

      // Group assignments by user
      const userAssignments = {};
      filteredAssignments.forEach(assignment => {
        const userId = assignment.userId || assignment.user || assignment.user_id;
        if (!userAssignments[userId]) {
          userAssignments[userId] = [];
        }
        userAssignments[userId].push(assignment);
      });

      // Filter by selected users if any are specified
      if (pdfExtractConfig.selectedUserIds.length > 0) {
        const selectedUserIdsSet = new Set(pdfExtractConfig.selectedUserIds);
        Object.keys(userAssignments).forEach(userId => {
          if (!selectedUserIdsSet.has(parseInt(userId))) {
            delete userAssignments[userId];
          }
        });
      }

      console.log('User assignments grouped:', userAssignments);
      console.log('Selected user IDs:', pdfExtractConfig.selectedUserIds);

      // Filter car assignments if needed
      const filteredCarAssignments = pdfExtractConfig.includeCars ? carAssignments.filter(assignment => {
        const assignStart = new Date(assignment.startDate);
        const assignEnd = new Date(assignment.endDate);

        // Normalize assignment dates to beginning/end of day
        assignStart.setHours(0, 0, 0, 0);
        assignEnd.setHours(23, 59, 59, 999);

        const overlaps = assignStart <= normalizedEnd && assignEnd >= normalizedStart;
        const projectMatch = !Array.isArray(pdfExtractConfig.projectIds) || pdfExtractConfig.projectIds.length === 0 ||
          pdfExtractConfig.projectIds.includes(assignment.projectId);
        return overlaps && projectMatch;
      }) : [];

      // Group car assignments by car
      const carAssignmentsByCar = {};
      filteredCarAssignments.forEach(assignment => {
        if (!carAssignmentsByCar[assignment.carId]) {
          carAssignmentsByCar[assignment.carId] = [];
        }
        carAssignmentsByCar[assignment.carId].push(assignment);
      });

      // Collect unique project IDs and check-in/check-out presence for legend
      // IMPORTANT: Collect ONLY from assignments that will actually be in the PDF (after user filtering)
      const uniqueProjectIds = new Set();

      // Collect from user assignments (after filtering by selected users)
      Object.values(userAssignments).forEach(assignments => {
        assignments.forEach(assignment => {
          const projectId = assignment.projectId || assignment.project_id || assignment.project;
          if (projectId) uniqueProjectIds.add(projectId);
        });
      });

      // Collect from car assignments (after filtering)
      filteredCarAssignments.forEach(assignment => {
        if (assignment.projectId) uniqueProjectIds.add(assignment.projectId);
      });

      // Debug: Log all travels to see what we have
      console.log('All travel data:', travelData);
      console.log('Total travels:', Array.isArray(travelData) ? travelData.length : 0);

      // Check if there are any check-in/check-out travels in the date range FOR SELECTED USERS
      const selectedUserIdsForCheck = Object.keys(userAssignments).map(id => parseInt(id));
      console.log('Checking for check-in/check-out for users:', selectedUserIdsForCheck);

      const hasCheckInOrOut = (Array.isArray(travelData) ? travelData : []).some(travel => {
        const travelUserId = travel.user_id || travel.user; // FIX: Check user_id (integer) first, not user (string)

        // Debug each travel for selected users
        if (selectedUserIdsForCheck.includes(parseInt(travelUserId))) {
          console.log(`Checking travel ID ${travel.id} for user ${travelUserId}:`, {
            travel_type: travel.travel_type,
            type: travel.type,
            start_date: travel.start_date,
            departure_date: travel.departure_date,
            travel_date: travel.travel_date,
            date: travel.date,
            fullTravel: travel
          });
        }

        const isCheckInOrOut = travel.travel_type === 'check_in' || travel.travel_type === 'check_out';
        if (!isCheckInOrOut) {
          if (selectedUserIdsForCheck.includes(parseInt(travelUserId))) {
            console.log(`  ❌ Travel ${travel.id} rejected: not check-in/check-out (travel_type = ${travel.travel_type})`);
          }
          return false;
        }

        // Check if this travel belongs to one of the selected users
        if (!selectedUserIdsForCheck.includes(parseInt(travelUserId))) return false;

        const travelDate = new Date(travel.start_date || travel.departure_date || travel.travel_date || travel.date);
        const inRange = travelDate >= startDate && travelDate <= endDate;

        if (inRange) {
          console.log(`  ✅ Found ${travel.travel_type} for user ${travelUserId} on ${travelDate.toLocaleDateString()}`);
        } else {
          console.log(`  ❌ Travel ${travel.id} rejected: date ${travelDate.toLocaleDateString()} not in range ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);
        }

        return inRange;
      });

      // Check if there are any trainings in the date range FOR SELECTED USERS
      const hasTraining = (Array.isArray(trainingData) ? trainingData : []).some(training => {
        // Check if training includes any selected user
        if (!training.users_data) return false;
        const hasSelectedUser = training.users_data.some(user =>
          selectedUserIdsForCheck.includes(parseInt(user.id))
        );
        if (!hasSelectedUser) return false;

        // Check if training overlaps with date range
        const trainingStart = new Date(training.start_date || training.training_date || training.date);
        const trainingEnd = training.end_date ? new Date(training.end_date) : trainingStart;
        trainingStart.setHours(0, 0, 0, 0);
        trainingEnd.setHours(23, 59, 59, 999);

        return trainingStart <= endDate && trainingEnd >= startDate;
      });

      console.log('Has training in range:', hasTraining);

      // Check if there's any data to export
      const hasUserData = pdfExtractConfig.includeUsers && Object.keys(userAssignments).length > 0;
      const hasCarData = pdfExtractConfig.includeCars && Object.keys(carAssignmentsByCar).length > 0;

      if (!hasUserData && !hasCarData) {
        const dateRangeText = pdfExtractConfig.mode === 'weeks'
          ? `tygodnie ${pdfExtractConfig.startWeek} - ${pdfExtractConfig.endWeek}`
          : `${normalizedStart.toLocaleDateString('pl-PL')} - ${normalizedEnd.toLocaleDateString('pl-PL')}`;

        alert(`Brak danych do wyeksportowania!\n\nNie znaleziono żadnych przypisań w wybranym zakresie dat: ${dateRangeText}\n\nSprawdź:\n1. Czy wybrano właściwy zakres dat\n2. Czy w tym okresie są jakieś przypisania\n3. Czy zaznaczono opcje "Include Users" lub "Include Cars"`);
        return;
      }

      // Create array of all days in the date range
      const allDays = [];
      const currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        allDays.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }

      // Check if we need individual PDFs for bulk email
      const isBulkEmailMode = pdfExtractConfig.sendEmail && pdfExtractConfig.selectedUserIds.length > 1;
      const bulkPDFData = []; // Will collect {userId, pdfBase64, fileName} for bulk mode
      const userIdsForPDFGeneration = isBulkEmailMode ? pdfExtractConfig.selectedUserIds : [null]; // null = all users

      // Period text - used in PDF title and filename (defined before loop)
      const periodText = pdfExtractConfig.mode === 'weeks'
        ? `Week ${pdfExtractConfig.startWeek.split('-W')[1]} - Week ${pdfExtractConfig.endWeek.split('-W')[1]}, ${pdfExtractConfig.startWeek.split('-W')[0]}`
        : `${startDate.toLocaleDateString('en-GB')} - ${endDate.toLocaleDateString('en-GB')}`;

      // Variable to hold the last generated doc (for single user/download mode)
      let doc = null;

      // Generate PDF(s) - loop runs once for normal mode, multiple times for bulk email
      for (const targetUserId of userIdsForPDFGeneration) {
        // Filter userAssignments for this specific user if in bulk mode
        const currentUserAssignments = targetUserId !== null
          ? { [targetUserId]: userAssignments[targetUserId] || [] }
          : userAssignments;

        // Recalculate uniqueProjectIds and travel flags for current user's assignments
        const currentUniqueProjectIds = new Set();
        Object.values(currentUserAssignments).forEach(assignments => {
          assignments.forEach(assignment => {
            const projectId = assignment.projectId || assignment.project_id || assignment.project;
            if (projectId) currentUniqueProjectIds.add(projectId);
          });
        });

        // Check for travels for current user only
        const currentUserIds = Object.keys(currentUserAssignments).map(id => parseInt(id));
        const currentHasCheckInOrOut = (Array.isArray(travelData) ? travelData : []).some(travel => {
          const travelUserId = travel.user_id || travel.user;
          if (!currentUserIds.includes(parseInt(travelUserId))) return false;
          const isCheckInOrOut = travel.travel_type === 'check_in' || travel.travel_type === 'check_out';
          if (!isCheckInOrOut) return false;
          const travelDate = new Date(travel.start_date || travel.departure_date || travel.travel_date || travel.date);
          return travelDate >= startDate && travelDate <= endDate;
        });

        const currentHasTraining = (Array.isArray(trainingData) ? trainingData : []).some(training => {
          if (!training.users_data) return false;
          const hasSelectedUser = training.users_data.some(user =>
            currentUserIds.includes(parseInt(user.id))
          );
          if (!hasSelectedUser) return false;
          const trainingStart = new Date(training.start_date || training.training_date || training.date);
          const trainingEnd = training.end_date ? new Date(training.end_date) : trainingStart;
          return trainingStart <= endDate && trainingEnd >= startDate;
        });

        // Skip users with no assignments in bulk mode
        if (targetUserId !== null && Object.keys(currentUserAssignments).length === 0) {
          continue;
        }
        if (targetUserId !== null && !currentUserAssignments[targetUserId]?.length) {
          continue;
        }

      // Create PDF with proper encoding for Polish characters
      doc = new jsPDF({
        orientation: pdfExtractConfig.orientation || 'portrait',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        compress: true
      });

      // Add Roboto font to support Polish characters
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.setFont('Roboto', 'normal');

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Title
      doc.setFontSize(14);
      doc.setFont('Roboto', 'normal');
      doc.text('Resource Plan - Timeline View', pageWidth / 2, 10, { align: 'center' });

      // Period info
      doc.setFontSize(8);
      doc.setFont('Roboto', 'normal');
      doc.text(`Period: ${periodText}`, 10, 17);

      // Project info (supports multiple projects)
      if (Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 0) {
        const selectedProjects = projects.filter(p => pdfExtractConfig.projectIds.includes(p.id));
        const projectNames = selectedProjects.map(p => p.name).join(', ');
        const displayText = projectNames.length > 60 ? projectNames.substring(0, 57) + '...' : projectNames;
        doc.text(`Projects: ${displayText}`, 10, 21);
      } else {
        doc.text('Projects: All Projects', 10, 21);
      }

      // Timeline Configuration - adapt based on orientation
      const isLandscape = pdfExtractConfig.orientation === 'landscape';
      const leftMargin = isLandscape ? 50 : 45; // Space for names

      // Calculate legend height to ensure topMargin is below it
      const itemsPerRow = isLandscape ? 3 : 2;
      const legendItemHeight = 4;
      const projectCount = currentUniqueProjectIds.size;
      const projectRows = Math.ceil(projectCount / itemsPerRow);
      const legendHeight = projectRows * legendItemHeight + (currentHasCheckInOrOut ? 5 : 0) + (currentHasTraining ? 5 : 0); // +5 for travel/training legend if exists
      const minTopMargin = 26;
      const legendEndY = 18 + 5 + legendHeight; // legend starts at 18, +5 for header, +legendHeight for items
      const topMargin = Math.max(minTopMargin, legendEndY + 3); // At least 3mm gap after legend

      const rowHeight = 7;
      const maxCellWidth = isLandscape ? 8 : 5; // Wider cells in landscape
      const cellWidth = Math.min(maxCellWidth, (pageWidth - leftMargin - 10) / allDays.length);
      const headerHeight = 11;

      // English day names (abbreviated)
      const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      // Helper function to convert hex color to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ] : [100, 180, 255]; // Default blue
      };

      // Map Tailwind color names to RGB values
      const tailwindColorMap = {
        'blue': [59, 130, 246],
        'green': [34, 197, 94],
        'red': [239, 68, 68],
        'yellow': [234, 179, 8],
        'purple': [168, 85, 247],
        'pink': [236, 72, 153],
        'indigo': [99, 102, 241],
        'teal': [20, 184, 166],
        'orange': [249, 115, 22],
        'cyan': [6, 182, 212],
        'lime': [132, 204, 22],
        'emerald': [16, 185, 129],
        'violet': [139, 92, 246],
        'fuchsia': [217, 70, 239],
        'rose': [244, 63, 94],
        'sky': [14, 165, 233],
        'amber': [245, 158, 11],
        'gray': [107, 114, 128],
        'slate': [100, 116, 139],
        'zinc': [113, 113, 122],
        'neutral': [115, 115, 115],
        'stone': [120, 113, 108]
      };

      // Helper function to get project color as RGB array
      const getProjectColorRGB = (project) => {
        if (!project) return [100, 180, 255]; // Default blue

        // If project has a hex color
        if (project.color && project.color.startsWith('#')) {
          return hexToRgb(project.color);
        }

        // If project has a Tailwind color name
        if (project.color) {
          // Extract color name from various formats
          let colorName = project.color;

          // Handle 'bg-brand-100' format - extract 'blue'
          const match = project.color.match(/(?:bg-|text-|border-)(\w+)-\d+/);
          if (match) {
            colorName = match[1];
          }

          // Return the RGB value
          return tailwindColorMap[colorName] || [100, 180, 246];
        }

        // Default fallback
        return [100, 180, 255];
      };

      // ========== LEGEND (Top Right) ==========
      if (currentUniqueProjectIds.size > 0 || currentHasCheckInOrOut || currentHasTraining) {
        let legendY = 18; // Start position for legend (moved down one field)
        const legendX = pageWidth - 80; // Right side position (moved slightly left for more space)
        const colorBoxSize = 3;
        // Dynamic column width based on orientation
        const colWidth = isLandscape ? 45 : 38;
        // itemsPerRow and legendItemHeight are now defined earlier for topMargin calculation

        // Legend Header
        doc.setFontSize(8);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text('Legend', legendX, legendY);
        legendY += 5;

        // Draw project legend items in multiple columns
        doc.setFontSize(6);
        const projectArray = Array.from(currentUniqueProjectIds);

        projectArray.forEach((projectId, index) => {
          const project = (Array.isArray(projects) ? projects : []).find(p => p.id === projectId);
          if (!project) return;

          const [r, g, b] = getProjectColorRGB(project);

          const col = index % itemsPerRow;
          const row = Math.floor(index / itemsPerRow);
          const xPos = legendX + col * colWidth;
          const yPos = legendY + row * legendItemHeight;

          // Draw color box
          doc.setFillColor(r, g, b);
          doc.roundedRect(xPos, yPos - 2.5, colorBoxSize, colorBoxSize, 0.5, 0.5, 'F');

          // Draw project name
          doc.setTextColor(0, 0, 0);
          const projectName = project.name || `Project ${projectId}`;
          // Truncate long names - longer for landscape
          const maxLength = isLandscape ? 18 : 15;
          const truncatedName = projectName.length > maxLength ? projectName.substring(0, maxLength - 2) + '...' : projectName;
          doc.text(truncatedName, xPos + colorBoxSize + 1.5, yPos);
        });

        // Update Y position after projects
        const projectRows = Math.ceil(projectArray.length / itemsPerRow);
        legendY += projectRows * legendItemHeight;

        // Add check-in/check-out to legend if they exist (in one row, side by side)
        if (currentHasCheckInOrOut) {
          legendY += 1; // Small spacing

          // Travel In (left side) - green square
          doc.setFillColor(34, 197, 94); // green-500
          doc.rect(legendX, legendY - colorBoxSize, colorBoxSize, colorBoxSize, 'F');
          doc.setTextColor(0, 0, 0);
          doc.text('Travel In', legendX + colorBoxSize + 1.5, legendY);

          // Travel Out (right side, same row) - red square
          doc.setFillColor(239, 68, 68); // red-500
          doc.rect(legendX + colWidth, legendY - colorBoxSize, colorBoxSize, colorBoxSize, 'F');
          doc.setTextColor(0, 0, 0);
          doc.text('Travel Out', legendX + colWidth + colorBoxSize + 1.5, legendY);

          legendY += legendItemHeight; // Move to next row
        }

        // Add training to legend if exists
        if (currentHasTraining) {
          legendY += 1; // Small spacing

          // Training - purple box with "T"
          doc.setFillColor(168, 85, 247); // purple-500
          doc.roundedRect(legendX, legendY - colorBoxSize, colorBoxSize, colorBoxSize, 0.5, 0.5, 'F');
          doc.setFontSize(5);
          doc.setTextColor(255, 255, 255);
          doc.setFont('Roboto', 'bold');
          doc.text('T', legendX + colorBoxSize / 2, legendY - colorBoxSize / 2 + 0.8, { align: 'center' });

          // Training label
          doc.setFontSize(6);
          doc.setTextColor(0, 0, 0);
          doc.setFont('Roboto', 'normal');
          doc.text('Training', legendX + colorBoxSize + 1.5, legendY);
        }
      }

      let yPosition = topMargin;

      // Helper function to get ISO week number (used by both People and Cars sections)
      const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      };

      // ========== PEOPLE SECTION ==========
      if (pdfExtractConfig.includeUsers && Object.keys(currentUserAssignments).length > 0) {
        // Section Header
        doc.setFontSize(11);
        doc.setFont('Roboto', 'normal');
        doc.text('People Assignments', 10, yPosition);
        yPosition += 5;

        // Timeline Header (Dates)
        doc.setFontSize(6);
        doc.setFont('Roboto', 'normal');

        // Draw header background
        doc.setFillColor(240, 240, 240);
        doc.rect(leftMargin, yPosition, allDays.length * cellWidth, headerHeight, 'F');

        // Draw week numbers above days (grouped by week)
        let currentWeek = null;
        let weekStartX = null;
        let weekStartDay = null;
        allDays.forEach((day, index) => {
          const weekNum = getWeekNumber(day);
          const x = leftMargin + index * cellWidth;

          if (weekNum !== currentWeek) {
            // New week started
            if (currentWeek !== null && weekStartX !== null && weekStartDay !== null) {
              // Draw the previous week label
              const weekWidth = x - weekStartX;
              const monthName = weekStartDay.toLocaleDateString('en-US', { month: 'long' });
              doc.setFillColor(220, 220, 240);
              doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'F');
              doc.setDrawColor(150, 150, 180);
              doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'S');
              doc.setFontSize(5);
              doc.setTextColor(60, 60, 100);
              doc.text(`${monthName} - W${currentWeek}`, weekStartX + weekWidth / 2, yPosition - 1.5, { align: 'center' });
            }
            currentWeek = weekNum;
            weekStartX = x;
            weekStartDay = day;
          }
        });

        // Draw the last week label
        if (currentWeek !== null && weekStartX !== null && weekStartDay !== null) {
          const weekWidth = (leftMargin + allDays.length * cellWidth) - weekStartX;
          const monthName = weekStartDay.toLocaleDateString('en-US', { month: 'long' });
          doc.setFillColor(220, 220, 240);
          doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'F');
          doc.setDrawColor(150, 150, 180);
          doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'S');
          doc.setFontSize(5);
          doc.setTextColor(60, 60, 100);
          doc.text(`${monthName} - W${currentWeek}`, weekStartX + weekWidth / 2, yPosition - 1.5, { align: 'center' });
        }

        // Draw date labels
        allDays.forEach((day, index) => {
          const x = leftMargin + index * cellWidth;
          const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday

          // Highlight weekends (Saturday + Sunday)
          if (isWeekend) {
            doc.setFillColor(255, 230, 230);
            doc.rect(x, yPosition, cellWidth, headerHeight, 'F');
          }

          // Draw vertical grid line
          doc.setDrawColor(200, 200, 200);
          doc.line(x, yPosition, x, yPosition + headerHeight);

          // Month (only on first day of month or first day) - AT TOP
          if (day.getDate() === 1 || index === 0) {
            doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
            doc.setFontSize(5);
            const monthName = day.toLocaleDateString('en-US', { month: 'short' });
            doc.text(monthName, x + cellWidth / 2, yPosition + 3, { align: 'center' });
          }

          // Day name
          doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
          doc.setFontSize(5);
          doc.text(dayNamesShort[day.getDay()], x + cellWidth / 2, yPosition + 7, { align: 'center' });

          // Day number
          doc.setFontSize(6);
          doc.text(day.getDate().toString(), x + cellWidth / 2, yPosition + 10, { align: 'center' });
        });

        // Draw right border of header
        doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);

        // Draw bottom border of header
        doc.setLineWidth(0.5);
        doc.line(leftMargin, yPosition + headerHeight, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
        doc.setLineWidth(0.2);

        yPosition += headerHeight + 2;

        // Determine if we should group by project
        const shouldGroupByProject = pdfExtractConfig.multiProjectLayout === 'by-project' &&
          Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 1;

        // Get list of projects to iterate (or null for single iteration)
        const projectsToRender = shouldGroupByProject
          ? pdfExtractConfig.projectIds.map(id => projects.find(p => p.id === id)).filter(p => p)
          : [null]; // null means render all users together

        // Iterate through projects (or once if not grouping by project)
        projectsToRender.forEach((currentProject, projectLoopIndex) => {
          // If grouping by project, add project header
          if (shouldGroupByProject && currentProject) {
            // Check if we need a new page for project header
            if (yPosition > pageHeight - 30) {
              doc.addPage();
              yPosition = 20;
            }

            // Draw project section header
            doc.setFontSize(9);
            doc.setFont('Roboto', 'normal');
            doc.setTextColor(0, 0, 0);
            const projectHeaderText = 'Project: ' + currentProject.name;
            doc.text(projectHeaderText, 10, yPosition);
            yPosition += 6;
          }

          // Get users for this project (or all users if not grouping)
          let userIdsForThisSection;
          if (shouldGroupByProject && currentProject) {
            // Filter users who have assignments for this specific project
            userIdsForThisSection = Object.keys(currentUserAssignments).filter(userId => {
              return currentUserAssignments[userId].some(assignment => {
                const projectId = assignment.projectId || assignment.project_id || assignment.project;
                return projectId === currentProject.id;
              });
            });
          } else {
            // All users
            userIdsForThisSection = Object.keys(currentUserAssignments);
          }

          // Sort user list
          const sortedUserIds = userIdsForThisSection.sort((a, b) => {
            const userA = users.find(u => u.id === parseInt(a));
            const userB = users.find(u => u.id === parseInt(b));
            const nameA = userA ? `${userA.last_name || ''} ${userA.first_name || ''}`.trim() : `User ${a}`;
            const nameB = userB ? `${userB.last_name || ''} ${userB.first_name || ''}`.trim() : `User ${b}`;

            if (pdfExtractConfig.sortBy === 'alphabet') {
              return nameA.localeCompare(nameB);
            } else {
              // Sort by earliest start date
              const earliestA = Math.min(...currentUserAssignments[a].map(a => new Date(a.startDate || a.start_date)));
              const earliestB = Math.min(...currentUserAssignments[b].map(a => new Date(a.startDate || a.start_date)));
              return earliestA - earliestB;
            }
          });

          // Draw each person's row
          sortedUserIds.forEach((userId, personIndex) => {
          const user = users.find(u => u.id === parseInt(userId));
          const userName = user ? `${user.last_name || ''} ${user.first_name || ''}`.trim() : `User ${userId}`;

          // Check if we need a new page
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;

            // Redraw header on new page
            doc.setFillColor(240, 240, 240);
            doc.rect(leftMargin, yPosition, allDays.length * cellWidth, headerHeight, 'F');
            allDays.forEach((day, index) => {
              const x = leftMargin + index * cellWidth;
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              if (isWeekend) {
                doc.setFillColor(255, 230, 230);
                doc.rect(x, yPosition, cellWidth, headerHeight, 'F');
              }
              doc.setDrawColor(200, 200, 200);
              doc.line(x, yPosition, x, yPosition + headerHeight);
              // Month (only on first day of month or first day) - AT TOP
              if (day.getDate() === 1 || index === 0) {
                doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
                doc.setFontSize(5);
                const monthName = day.toLocaleDateString('en-US', { month: 'short' });
                doc.text(monthName, x + cellWidth / 2, yPosition + 3, { align: 'center' });
              }

              doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
              doc.setFontSize(5);
              doc.text(dayNamesShort[day.getDay()], x + cellWidth / 2, yPosition + 7, { align: 'center' });
              doc.setFontSize(6);
              doc.text(day.getDate().toString(), x + cellWidth / 2, yPosition + 10, { align: 'center' });
            });
            doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
            doc.setLineWidth(0.5);
            doc.line(leftMargin, yPosition + headerHeight, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
            doc.setLineWidth(0.2);
            yPosition += headerHeight + 2;
          }

          // Calculate number of days this person has assignments
          const assigns = currentUserAssignments[userId];
          const assignmentDaysSet = new Set();
          assigns.forEach(assignment => {
            const assignStart = new Date(assignment.startDate || assignment.start_date);
            const assignEnd = new Date(assignment.endDate || assignment.end_date);

            // Count each day between start and end
            const currentDay = new Date(assignStart);
            while (currentDay <= assignEnd) {
              // Only count days within our PDF range
              if (currentDay >= startDate && currentDay <= endDate) {
                assignmentDaysSet.add(currentDay.toDateString());
              }
              currentDay.setDate(currentDay.getDate() + 1);
            }
          });
          const totalDays = assignmentDaysSet.size;

          // Draw person name (left column) with day count
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(7);
          doc.setFont('Roboto', 'normal');
          doc.text(`${userName} (${totalDays}d)`, 10, yPosition + rowHeight / 2 + 1, { align: 'left' });

          // Draw row background (alternating colors)
          if (personIndex % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(leftMargin, yPosition, allDays.length * cellWidth, rowHeight, 'F');
          }

          // Draw grid lines for each day
          allDays.forEach((day, dayIndex) => {
            const x = leftMargin + dayIndex * cellWidth;
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            // Weekend background
            if (isWeekend) {
              doc.setFillColor(255, 245, 245);
              doc.rect(x, yPosition, cellWidth, rowHeight, 'F');
            }

            // Vertical grid line
            doc.setDrawColor(220, 220, 220);
            doc.line(x, yPosition, x, yPosition + rowHeight);
          });

          // Draw assignment bars (using assigns from above)
          assigns.forEach(assignment => {
            // DEBUG: Log assignment structure for special tiles
            console.log('PDF Assignment:', {
              id: assignment.id,
              projectId: assignment.projectId,
              project_id: assignment.project_id,
              project: assignment.project,
              isSpecialTile: assignment.isSpecialTile,
              is_special_tile: assignment.is_special_tile,
              projectName: assignment.projectName,
              custom_name: assignment.custom_name,
              hexColor: assignment.hexColor,
              hex_color: assignment.hex_color,
              allKeys: Object.keys(assignment)
            });

            // Get project ID for this assignment
            const projectId = assignment.projectId || assignment.project_id || assignment.project;

            // Check if this is a special tile (quick tag) - need to check early for grouping logic
            const isSpecialTileEarly = assignment.isSpecialTile || assignment.is_special_tile;

            // If grouping by project, only show assignments for current project
            // But always show special tiles (quick tags) regardless of project grouping
            if (shouldGroupByProject && currentProject && !isSpecialTileEarly) {
              if (projectId !== currentProject.id) {
                return; // Skip this assignment, it's not for the current project
              }
            }

            const assignStart = new Date(assignment.startDate || assignment.start_date);
            const assignEnd = new Date(assignment.endDate || assignment.end_date);

            // Clamp to visible range
            const visibleStart = new Date(Math.max(assignStart, startDate));
            const visibleEnd = new Date(Math.min(assignEnd, endDate));

            // Calculate position and width
            const startDayIndex = Math.floor((visibleStart - startDate) / (1000 * 60 * 60 * 24));
            const endDayIndex = Math.floor((visibleEnd - startDate) / (1000 * 60 * 60 * 24));
            const barWidth = (endDayIndex - startDayIndex + 1) * cellWidth;
            const barX = leftMargin + startDayIndex * cellWidth;
            const project = (Array.isArray(projects) ? projects : []).find(p => p.id === projectId);

            // Check if this is a special tile (quick tag)
            // It's a special tile if: marked as such, OR has no project and has hexColor
            const isSpecialTileMarked = assignment.isSpecialTile || assignment.is_special_tile;
            const isSpecialTile = isSpecialTileMarked || (!project && (assignment.hexColor || assignment.hex_color));
            const customHexColor = assignment.hexColor || assignment.hex_color;

            // Try to get custom name from multiple sources
            let customName = assignment.projectName || assignment.custom_name;

            // If no custom name but it's a special tile, try to find it in customTiles by ID
            if (!customName && isSpecialTile && projectId) {
              // Try various ID matching patterns
              const matchingTile = customTiles.find(t =>
                t.id === projectId ||
                t.id == projectId || // loose comparison for number/string
                `custom-${t.id}` === projectId ||
                t.id === `custom-${projectId}` ||
                String(t.id) === String(projectId)
              );
              if (matchingTile) {
                customName = matchingTile.name;
                console.log('Found matching tile for name:', matchingTile);
              }
            }

            // Also check if projectId itself contains the name (for string IDs like "custom-Vacation")
            if (!customName && isSpecialTile && typeof projectId === 'string' && projectId.startsWith('custom-')) {
              // Try to extract name from projectId pattern
              const tileName = projectId.replace('custom-', '').replace(/_/g, ' ');
              // Check if it's not just a number
              if (isNaN(tileName)) {
                customName = tileName;
              }
            }

            // DEBUG: Log special tile detection
            if (isSpecialTile || !project) {
              console.log('Special tile or no project:', {
                isSpecialTile,
                isSpecialTileMarked,
                customName,
                customHexColor,
                projectId,
                assignmentProjectName: assignment.projectName,
                assignmentCustomName: assignment.custom_name,
                assignmentKeys: Object.keys(assignment),
                customTilesCount: customTiles.length
              });
            }

            // Use custom hex color for special tiles, otherwise use project color
            let r, g, b;
            if (isSpecialTile && customHexColor) {
              [r, g, b] = hexToRgb(customHexColor);
            } else {
              [r, g, b] = getProjectColorRGB(project);
            }

            // Draw colored bar
            doc.setFillColor(r, g, b);
            doc.roundedRect(barX + 0.3, yPosition + 1, barWidth - 0.6, rowHeight - 2, 1, 1, 'F');

            // Draw project name if bar is wide enough (lowered threshold for special tiles)
            const minWidthForName = isSpecialTile ? 8 : 15;
            if (barWidth > minWidthForName) {
              doc.setFontSize(5);
              doc.setTextColor(255, 255, 255);
              doc.setFont('Roboto', 'normal');
              // Use custom name for special tiles, project name otherwise
              const displayName = isSpecialTile ? (customName || 'Quick Tag') : (project?.name || `P${projectId}`);
              console.log('Drawing bar with name:', { displayName, isSpecialTile, customName, barWidth, projectName: project?.name });
              doc.text(displayName, barX + barWidth / 2, yPosition + rowHeight / 2 + 1, { align: 'center' });
            } else {
              console.log('Bar too narrow for name:', { barWidth, minWidthForName, isSpecialTile, customName });
            }
          });

          // Draw check-in/check-out markers if they exist for this user
          const userTravels = (Array.isArray(travelData) ? travelData : []).filter(travel => {
            const travelUserId = travel.user_id || travel.user; // FIX: Check user_id (integer) first, not user (string)
            const isUserMatch = parseInt(travelUserId) === parseInt(userId);
            const isCheckInOrOut = travel.travel_type === 'check_in' || travel.travel_type === 'check_out';

            if (!isUserMatch || !isCheckInOrOut) return false;

            // Check if travel date is within our range
            const travelDate = new Date(travel.start_date || travel.departure_date || travel.travel_date || travel.date);
            return travelDate >= startDate && travelDate <= endDate;
          });

          console.log(`User ${userId} has ${userTravels.length} check-in/check-out travels in PDF range`);
          userTravels.forEach(travel => console.log('Travel:', travel));

          userTravels.forEach(travel => {
            const travelDate = new Date(travel.start_date || travel.departure_date || travel.travel_date || travel.date);
            const dayIndex = Math.floor((travelDate - startDate) / (1000 * 60 * 60 * 24));

            console.log(`Drawing marker for ${travel.travel_type} on day ${dayIndex}, date: ${travelDate.toLocaleDateString()}`);

            if (dayIndex >= 0 && dayIndex < allDays.length) {
              const squareSize = 2; // Size of the square marker
              const markerX = leftMargin + dayIndex * cellWidth + (cellWidth - squareSize) / 2; // Center horizontally

              if (travel.travel_type === 'check_in') {
                // Draw green square for check-in at top of cell
                doc.setFillColor(34, 197, 94); // green-500
                const topY = yPosition + 0.5;
                doc.rect(markerX, topY, squareSize, squareSize, 'F');
              } else if (travel.travel_type === 'check_out') {
                // Draw red square for check-out at bottom of cell
                doc.setFillColor(239, 68, 68); // red-500
                const bottomY = yPosition + rowHeight - squareSize - 0.5;
                doc.rect(markerX, bottomY, squareSize, squareSize, 'F');
              }
            }
          });

          // Draw training indicators for this user
          const userTrainings = (Array.isArray(trainingData) ? trainingData : []).filter(training => {
            // Check if this training includes this user
            if (training.users_data) {
              return training.users_data.some(user => parseInt(user.id) === parseInt(userId));
            }
            return false;
          });

          userTrainings.forEach(training => {
            const trainingStart = new Date(training.start_date || training.training_date || training.date);
            const trainingEnd = training.end_date ? new Date(training.end_date) : trainingStart;

            // Check each day in our range for training overlap
            allDays.forEach((day, dayIndex) => {
              const checkDate = new Date(day);
              checkDate.setHours(12, 0, 0, 0);

              const tStart = new Date(trainingStart);
              tStart.setHours(0, 0, 0, 0);
              const tEnd = new Date(trainingEnd);
              tEnd.setHours(23, 59, 59, 999);

              if (checkDate >= tStart && checkDate <= tEnd) {
                // Draw purple background and "T" for training
                const x = leftMargin + dayIndex * cellWidth;
                // Draw small purple background
                doc.setFillColor(168, 85, 247); // purple-500 for training
                doc.roundedRect(x + 0.5, yPosition + rowHeight - 4, cellWidth - 1, 3.5, 0.5, 0.5, 'F');
                // Draw "T" text
                doc.setFontSize(4);
                doc.setTextColor(255, 255, 255);
                doc.setFont('Roboto', 'bold');
                doc.text('T', x + cellWidth / 2, yPosition + rowHeight - 1.5, { align: 'center' });
                // Reset font
                doc.setFont('Roboto', 'normal');
              }
            });
          });

          // Draw right border
          doc.setDrawColor(200, 200, 200);
          doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + rowHeight);

          // Draw bottom border of row
          doc.setDrawColor(220, 220, 220);
          doc.line(leftMargin, yPosition + rowHeight, leftMargin + allDays.length * cellWidth, yPosition + rowHeight);

          yPosition += rowHeight;
        });

          // Add spacing between projects when grouping by project
          if (shouldGroupByProject && projectLoopIndex < projectsToRender.length - 1) {
            yPosition += 8; // Extra spacing between projects
          }
        }); // End of projectsToRender.forEach

        yPosition += 5;
      }

      // ========== CARS SECTION ==========
      if (pdfExtractConfig.includeCars && Object.keys(carAssignmentsByCar).length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        // Section Header
        doc.setFontSize(11);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text('Cars & Equipment Assignments', 10, yPosition);
        yPosition += 5;

        // Timeline Header (Dates) - Same as people section
        doc.setFontSize(6);
        doc.setFont('Roboto', 'normal');

        doc.setFillColor(240, 240, 240);
        doc.rect(leftMargin, yPosition, allDays.length * cellWidth, headerHeight, 'F');

        // Draw week numbers above days (grouped by week) - same as people section
        let currentWeek = null;
        let weekStartX = null;
        let weekStartDay = null;
        allDays.forEach((day, index) => {
          const weekNum = getWeekNumber(day);
          const x = leftMargin + index * cellWidth;

          if (weekNum !== currentWeek) {
            // New week started
            if (currentWeek !== null && weekStartX !== null && weekStartDay !== null) {
              // Draw the previous week label
              const weekWidth = x - weekStartX;
              const monthName = weekStartDay.toLocaleDateString('en-US', { month: 'long' });
              doc.setFillColor(220, 220, 240);
              doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'F');
              doc.setDrawColor(150, 150, 180);
              doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'S');
              doc.setFontSize(5);
              doc.setTextColor(60, 60, 100);
              doc.text(`${monthName} - W${currentWeek}`, weekStartX + weekWidth / 2, yPosition - 1.5, { align: 'center' });
            }
            currentWeek = weekNum;
            weekStartX = x;
            weekStartDay = day;
          }
        });

        // Draw the last week label
        if (currentWeek !== null && weekStartX !== null && weekStartDay !== null) {
          const weekWidth = (leftMargin + allDays.length * cellWidth) - weekStartX;
          const monthName = weekStartDay.toLocaleDateString('en-US', { month: 'long' });
          doc.setFillColor(220, 220, 240);
          doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'F');
          doc.setDrawColor(150, 150, 180);
          doc.rect(weekStartX, yPosition - 4, weekWidth, 3.5, 'S');
          doc.setFontSize(5);
          doc.setTextColor(60, 60, 100);
          doc.text(`${monthName} - W${currentWeek}`, weekStartX + weekWidth / 2, yPosition - 1.5, { align: 'center' });
        }

        // Draw date labels
        allDays.forEach((day, index) => {
          const x = leftMargin + index * cellWidth;
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;

          if (isWeekend) {
            doc.setFillColor(255, 230, 230);
            doc.rect(x, yPosition, cellWidth, headerHeight, 'F');
          }

          doc.setDrawColor(200, 200, 200);
          doc.line(x, yPosition, x, yPosition + headerHeight);

          // Month (only on first day of month or first day) - AT TOP
          if (day.getDate() === 1 || index === 0) {
            doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
            doc.setFontSize(5);
            const monthName = day.toLocaleDateString('en-US', { month: 'short' });
            doc.text(monthName, x + cellWidth / 2, yPosition + 3, { align: 'center' });
          }

          doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
          doc.setFontSize(5);
          doc.text(dayNamesShort[day.getDay()], x + cellWidth / 2, yPosition + 7, { align: 'center' });

          doc.setFontSize(6);
          doc.text(day.getDate().toString(), x + cellWidth / 2, yPosition + 10, { align: 'center' });

        });

        doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
        doc.setLineWidth(0.5);
        doc.line(leftMargin, yPosition + headerHeight, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
        doc.setLineWidth(0.2);

        yPosition += headerHeight + 2;

        // Get sorted car list
        const sortedCarIds = Object.keys(carAssignmentsByCar).sort((a, b) => {
          const carA = carsData.find(c => c.id === parseInt(a));
          const carB = carsData.find(c => c.id === parseInt(b));
          const nameA = carA ? `${carA.car_brand || ''} ${carA.car_model || ''}`.trim() : `Car ${a}`;
          const nameB = carB ? `${carB.car_brand || ''} ${carB.car_model || ''}`.trim() : `Car ${b}`;
          return nameA.localeCompare(nameB);
        });

        // Draw each car's row
        sortedCarIds.forEach((carId, carIndex) => {
          const car = carsData.find(c => c.id === parseInt(carId));
          const carName = car
            ? `${car.car_brand || ''} ${car.car_model || ''} (${car.plate_number || ''})`.trim()
            : `Vehicle ${carId}`;

          // Check if we need a new page
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;

            // Redraw header on new page
            doc.setFillColor(240, 240, 240);
            doc.rect(leftMargin, yPosition, allDays.length * cellWidth, headerHeight, 'F');
            allDays.forEach((day, index) => {
              const x = leftMargin + index * cellWidth;
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              if (isWeekend) {
                doc.setFillColor(255, 230, 230);
                doc.rect(x, yPosition, cellWidth, headerHeight, 'F');
              }
              doc.setDrawColor(200, 200, 200);
              doc.line(x, yPosition, x, yPosition + headerHeight);
              // Month (only on first day of month or first day) - AT TOP
              if (day.getDate() === 1 || index === 0) {
                doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
                doc.setFontSize(5);
                const monthName = day.toLocaleDateString('en-US', { month: 'short' });
                doc.text(monthName, x + cellWidth / 2, yPosition + 3, { align: 'center' });
              }

              doc.setTextColor(isWeekend ? 180 : 60, 60, 60);
              doc.setFontSize(5);
              doc.text(dayNamesShort[day.getDay()], x + cellWidth / 2, yPosition + 7, { align: 'center' });
              doc.setFontSize(6);
              doc.text(day.getDate().toString(), x + cellWidth / 2, yPosition + 10, { align: 'center' });
            });
            doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
            doc.setLineWidth(0.5);
            doc.line(leftMargin, yPosition + headerHeight, leftMargin + allDays.length * cellWidth, yPosition + headerHeight);
            doc.setLineWidth(0.2);
            yPosition += headerHeight + 2;
          }

          // Draw car name
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(7);
          doc.setFont('Roboto', 'normal');
          doc.text(carName, 10, yPosition + rowHeight / 2 + 1, { align: 'left' });

          // Draw row background
          if (carIndex % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(leftMargin, yPosition, allDays.length * cellWidth, rowHeight, 'F');
          }

          // Draw grid
          allDays.forEach((day, dayIndex) => {
            const x = leftMargin + dayIndex * cellWidth;
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            if (isWeekend) {
              doc.setFillColor(255, 245, 245);
              doc.rect(x, yPosition, cellWidth, rowHeight, 'F');
            }

            doc.setDrawColor(220, 220, 220);
            doc.line(x, yPosition, x, yPosition + rowHeight);
          });

          // Draw car assignment bars
          const assigns = carAssignmentsByCar[carId];
          assigns.forEach(assignment => {
            const assignStart = new Date(assignment.startDate);
            const assignEnd = new Date(assignment.endDate);

            const visibleStart = new Date(Math.max(assignStart, startDate));
            const visibleEnd = new Date(Math.min(assignEnd, endDate));

            const startDayIndex = Math.floor((visibleStart - startDate) / (1000 * 60 * 60 * 24));
            const endDayIndex = Math.floor((visibleEnd - startDate) / (1000 * 60 * 60 * 24));
            const barWidth = (endDayIndex - startDayIndex + 1) * cellWidth;
            const barX = leftMargin + startDayIndex * cellWidth;

            const project = (Array.isArray(projects) ? projects : []).find(p =>
              p.id === assignment.projectId
            );

            // Use real project color (or orange fallback for cars)
            const [r, g, b] = project ? getProjectColorRGB(project) : [255, 160, 80]; // Orange fallback
            doc.setFillColor(r, g, b);
            doc.roundedRect(barX + 0.3, yPosition + 1, barWidth - 0.6, rowHeight - 2, 1, 1, 'F');

            if (barWidth > 15) {
              doc.setFontSize(5);
              doc.setTextColor(255, 255, 255);
              doc.setFont('Roboto', 'normal');
              const projectName = project?.name || `P${assignment.projectId}`;
              doc.text(projectName, barX + barWidth / 2, yPosition + rowHeight / 2 + 1, { align: 'center' });
            }
          });

          doc.setDrawColor(200, 200, 200);
          doc.line(leftMargin + allDays.length * cellWidth, yPosition, leftMargin + allDays.length * cellWidth, yPosition + rowHeight);

          doc.setDrawColor(220, 220, 220);
          doc.line(leftMargin, yPosition + rowHeight, leftMargin + allDays.length * cellWidth, yPosition + rowHeight);

          yPosition += rowHeight;
        });
      }

      // For bulk email mode, collect this PDF and continue to next user
      if (isBulkEmailMode && targetUserId !== null) {
        const user = users.find(u => u.id === targetUserId);
        const userEmail = user?.company_email || user?.email;
        if (user && userEmail) {
          const pdfBase64 = doc.output('datauristring').split(',')[1];
          // Build filename with username: Resource{Username}_Timeline_{Projects}_{period}.pdf
          const userName = (user.username || user.last_name || 'User').replace(/[^\w]/g, '');
          let userProjectPart;
          if (Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 0) {
            const selectedProjects = projects.filter(p => pdfExtractConfig.projectIds.includes(p.id));
            userProjectPart = selectedProjects.length === 1
              ? selectedProjects[0].name.replace(/[^\w\s-]/g, '_')
              : `${selectedProjects.length}_Projects`;
          } else {
            userProjectPart = 'All_Projects';
          }
          const userFileName = `Resource_${userName}_Timeline_${userProjectPart}_${periodText.replace(/[^\w\s-]/g, '_')}.pdf`;
          bulkPDFData.push({
            user_id: targetUserId,
            pdf_base64: pdfBase64,
            filename: userFileName
          });
          console.log(`[BULK PDF] Generated PDF for user ${targetUserId} (${user.first_name} ${user.last_name})`);
        } else {
          console.log(`[BULK PDF] Skipping user ${targetUserId} - no email address`);
        }
      }

      } // End of for loop (userIdsForPDFGeneration)

      // Save or send PDF - build filename
      let fileName;
      let projectPart;
      if (Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 0) {
        const selectedProjects = projects.filter(p => pdfExtractConfig.projectIds.includes(p.id));
        projectPart = selectedProjects.length === 1
          ? selectedProjects[0].name.replace(/[^\w\s-]/g, '_')
          : `${selectedProjects.length}_Projects`;
      } else {
        projectPart = 'All_Projects';
      }

      // Include username in filename if single user is selected
      if (pdfExtractConfig.selectedUserIds.length === 1) {
        const selectedUser = users.find(u => u.id === pdfExtractConfig.selectedUserIds[0]);
        const userName = (selectedUser?.username || selectedUser?.last_name || 'User').replace(/[^\w]/g, '');
        fileName = `Resource_${userName}_Timeline_${projectPart}_${periodText.replace(/[^\w\s-]/g, '_')}.pdf`;
      } else {
        fileName = `Resource_Timeline_${projectPart}_${periodText.replace(/[^\w\s-]/g, '_')}.pdf`;
      }

      // If sendEmail is true, send via email
      if (pdfExtractConfig.sendEmail && pdfExtractConfig.selectedUserIds.length > 0) {
        try {
          const token = localStorage.getItem('access_token');

          if (pdfExtractConfig.selectedUserIds.length === 1) {
            // Single user - use simple endpoint
            const pdfBlob = doc.output('blob');
            const selectedUser = users.find(u => u.id === pdfExtractConfig.selectedUserIds[0]);
            const recipientEmail = selectedUser?.company_email || selectedUser?.email || '';

            const formData = new FormData();
            formData.append('pdf_file', pdfBlob, fileName);
            formData.append('user_id', pdfExtractConfig.selectedUserIds[0]);
            formData.append('email', recipientEmail);
            formData.append('subject', `Resource Plan - ${selectedUser?.first_name} ${selectedUser?.last_name}`);

            const response = await axios.post(
              `${API_BASE_URL}/api/send-resource-plan-pdf/`,
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );

            if (response.status === 200) {
              alert(`PDF has been sent to ${recipientEmail || 'selected user'} successfully!`);
            } else {
              throw new Error('Failed to send email');
            }
          } else {
            // Multiple users - PDFs already generated in loop above, use bulkPDFData
            if (bulkPDFData.length === 0) {
              alert('No users with valid email addresses or assignments found.');
              return;
            }

            alert(`Sending ${bulkPDFData.length} individual PDFs via email. This may take a moment...`);

            // Send bulk request with pre-generated PDFs
            const response = await axios.post(
              `${API_BASE_URL}/api/send-resource-plan-pdf-bulk/`,
              {
                users: bulkPDFData,
                subject: `Resource Plan - ${periodText}`
              },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.data.success) {
              const { results } = response.data;
              const successful = results.filter(r => r.success).length;
              const failed = results.filter(r => !r.success);

              let message = `Successfully sent ${successful} email(s).`;
              if (failed.length > 0) {
                message += `\n\nFailed (${failed.length}):\n`;
                failed.forEach(f => {
                  const user = users.find(u => u.id === f.user_id);
                  message += `- ${user?.first_name} ${user?.last_name}: ${f.error}\n`;
                });
              }
              alert(message);
            } else {
              throw new Error(response.data.error || 'Failed to send emails');
            }
          }
        } catch (emailError) {
          console.error('Error sending email:', emailError);
          alert('Failed to send email(s). PDF will be downloaded instead.');
          doc.save(fileName);
        }
      } else {
        // Normal save - download to computer
        doc.save(fileName);
        alert('PDF Timeline generated successfully!');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleQuickAssignmentSubmit = async (data) => {
    try {
      const newAssignment = await resourceBoardService.createAssignment({
        user: quickAssignmentData.userId,
        project: data.projectId,
        start_date: formatDateForAPI(data.startDate),
        end_date: formatDateForAPI(data.endDate),
        role: 'Team Member'
      });

      setAssignments(prev => [...prev, {
        id: newAssignment.id,
        userId: quickAssignmentData.userId,
        projectId: data.projectId,
        startDate: data.startDate,
        endDate: data.endDate,
        role: 'Team Member'
      }]);

      // Add to undo stack
      addToUndoStack({
        type: 'CREATE_ASSIGNMENT',
        data: {
          id: newAssignment.id,
          userId: quickAssignmentData.userId,
          projectId: data.projectId,
          startDate: data.startDate,
          endDate: data.endDate,
          role: 'Team Member'
        }
      });

      setCurrentStatus('Assignment created (Ctrl+Z to undo)');
      setShowQuickAssignmentModal(false);
      setQuickAssignmentData(null);
    } catch (error) {
      logger.error('Error creating quick assignment:', error);
      alert(`Failed to create assignment: ${error.response?.data?.detail || error.message}`);
    }
  };

  const copyAssignmentToUsers = async () => {
    if (!selectedAssignment || selectedCopyUsers.length === 0) return;

    try {
      const newAssignments = [];

      // Use custom dates if provided, otherwise use original dates
      const startDate = copyStartDate || formatDateForAPI(selectedAssignment.startDate);
      const endDate = copyEndDate || formatDateForAPI(selectedAssignment.endDate);

      for (const targetUserId of selectedCopyUsers) {
        const response = await resourceBoardService.createAssignment({
          user: targetUserId,
          project: selectedAssignment.projectId,
          start_date: startDate,
          end_date: endDate,
          role: selectedAssignment.role
        });

        const newAssignment = {
          id: response.id,
          userId: targetUserId,
          projectId: selectedAssignment.projectId,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          role: selectedAssignment.role
        };

        newAssignments.push(newAssignment);

        // Add to undo stack for each created assignment
        addToUndoStack({
          type: 'CREATE_ASSIGNMENT',
          assignmentId: response.id,
          data: newAssignment
        });
      }

      setAssignments(prev => [...prev, ...newAssignments]);
      setSelectedCopyUsers([]);
      setUserSearchQuery('');
      setShowAssignmentActions(false);
      setSelectedAssignment(null);
    } catch (error) {
      logger.error('Error copying assignment:', error);
    }
  };

  const moveAssignmentToUser = async (targetUserId) => {
    if (!selectedAssignment) return;

    try {
      // Store original data for undo
      const originalUserId = selectedAssignment.userId;

      await resourceBoardService.patchAssignment(
        selectedAssignment.id,
        {
          user: targetUserId
        }
      );

      setAssignments(prev => prev.map(assignment =>
        assignment.id === selectedAssignment.id
          ? { ...assignment, userId: targetUserId }
          : assignment
      ));

      // Add to undo stack
      addToUndoStack({
        type: 'MOVE_ASSIGNMENT',
        moveType: 'USER_CHANGE',
        assignmentId: selectedAssignment.id,
        originalData: { ...selectedAssignment, userId: originalUserId },
        newData: { ...selectedAssignment, userId: targetUserId }
      });

      setShowAssignmentActions(false);
      setSelectedAssignment(null);
    } catch (error) {
      logger.error('Error moving assignment:', error);
    }
  };

  const saveAssignmentComment = async () => {
    if (selectedAssignment && editingComment) {
      const comment = commentInput.trim();

      // Update local state immediately
      setAssignmentComments(prev => ({
        ...prev,
        [selectedAssignment.id]: comment
      }));

      // Save to API
      try {
        const token = localStorage.getItem('access_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        await axios.patch(
          `${API_BASE_URL}/api/resource-planning-assignments/${selectedAssignment.id}/`,
          { comment: comment || null },
          { headers }
        );
      } catch (error) {
        console.error('Error saving comment to API:', error);
        alert('Failed to save comment. Please try again.');
      }

      setEditingComment(null);
      setCommentInput('');
    }
  };

  const cancelCommentEdit = () => {
    setEditingComment(null);
    setCommentInput('');
    setSelectedCopyUsers([]);
    setUserSearchQuery('');
    setCopyStartDate('');
    setCopyEndDate('');
    setShowAssignmentActions(false);
    setSelectedAssignment(null);
  };

  const toggleUserSelection = (userId) => {
    setSelectedCopyUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getFilteredUsersForCopy = () => {
    return users
      .filter(user => user.id !== selectedAssignment?.userId)
      .filter(user => {
        if (!userSearchQuery.trim()) return true;
        const query = userSearchQuery.toLowerCase().trim();
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();

        // Search by full name
        if (fullName.includes(query)) return true;

        // Search by initials (e.g., "JK" matches "Jan Kowalski")
        const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toLowerCase();
        if (initials === query) return true;

        // Search by first name initial + last name (e.g., "J Kowalski")
        const firstInitialLastName = `${user.first_name?.[0] || ''} ${user.last_name}`.toLowerCase();
        if (firstInitialLastName.includes(query)) return true;

        return false;
      });
  };

  const handleQuickTravel = async (travelType) => {
    if (!contextMenuData) return;

    const { user, car, date, type } = contextMenuData;

    // For cars, create travel directly without form
    if (type === 'car') {
      setShowTravelContextMenu(false);
      setContextMenuData(null);

      try {
        // Create simple check-in/check-out travel for car
        const travelDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Create travel record for car
        const carFormData = new FormData();
        carFormData.append('car_id', car.id);
        carFormData.append('start_date', travelDate.toISOString());
        carFormData.append('end_date', travelDate.toISOString());
        carFormData.append('company', 1);
        carFormData.append('address', `${travelType === 'check_in' ? 'Check-in' : 'Check-out'} - ${car.car_brand} ${car.car_model}`);
        carFormData.append('description', `${travelType === 'check_in' ? 'Check-in' : 'Check-out'} - ${car.car_brand} ${car.car_model}`);
        carFormData.append('travel_type', travelType);

        await axios.post(`${API_BASE_URL}/api/travel/`, carFormData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        logger.info(`${travelType === 'check_in' ? 'Check-in' : 'Check-out'} created for ${car.car_brand} ${car.car_model}`);

        // Refresh travel data without showing loading spinner
        await refreshTravelData();
      } catch (error) {
        logger.error('Error creating car travel:', error);
        alert('Failed to create car travel. Please try again.');
      }
      return;
    }

    // For users, open travel modal with default values
    // Normalize dates to local midnight to avoid timezone issues
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Set default times and values based on travel type
    const defaultData = {
      startDate: startDate,
      endDate: endDate,
      travelType: travelType,
      selectedUsers: [user.id] // Pre-select the clicked user
    };

    // Set travel planning data with initial values
    setTravelPlanningData({
      user: user,
      date: date,
      initialData: defaultData,
      allUsers: users // Pass all users for selection
    });

    // Open travel modal
    setShowTravelModal(true);

    // Close context menu
    setShowTravelContextMenu(false);
    setContextMenuData(null);
  };

  const handleAddNote = () => {
    if (!contextMenuData) return;

    setNoteModalData({
      user: contextMenuData.user,
      date: contextMenuData.date
    });
    setNoteInput('');
    setShowNoteModal(true);
    setShowTravelContextMenu(false);
    setContextMenuData(null);
  };

  const handleSaveNote = () => {
    if (!noteInput.trim()) {
      return;
    }

    if (!noteModalData.user || !noteModalData.date) {
      return;
    }

    const newNote = {
      id: Date.now(),
      userId: noteModalData.user.id,
      date: noteModalData.date.toDateString(),
      content: noteInput.trim(),
      createdAt: new Date()
    };

    setNotes(prev => {
      const updatedNotes = [...prev, newNote];
      return updatedNotes;
    });

    setShowNoteModal(false);
    setNoteInput('');
    setNoteModalData({ user: null, date: null });
  };

  const handleDeleteNote = (noteId) => {
    setNotes(prev => {
      const updatedNotes = prev.filter(note => note.id !== noteId);
      return updatedNotes;
    });

    // Update selectedNoteData to reflect the deletion
    setSelectedNoteData(prev => {
      if (!prev) return null;
      const updatedNotes = prev.notes.filter(n => n.id !== noteId);
      if (updatedNotes.length === 0) {
        // If no notes left, close tooltip
        setShowNoteTooltip(false);
        setShowConfirmNoteDelete(false);
        setNoteToDelete(null);
        return null;
      }
      return {
        ...prev,
        notes: updatedNotes
      };
    });

    setShowConfirmNoteDelete(false);
    setNoteToDelete(null);
  };

  const handleConfirmDeleteNote = (note) => {
    setNoteToDelete(note);
    setShowConfirmNoteDelete(true);
  };

  const getNotesForUserDate = (userId, date) => {
    const dateString = date.toDateString();
    const userNotes = notes.filter(note =>
      note.userId === userId &&
      note.date === dateString
    );
    return userNotes;
  };

  const handlePlanTravel = async (travelData) => {
    if (!travelPlanningData) return;

    const { user, date } = travelPlanningData;

    // Use dates from form data if provided, otherwise use default date
    const startDate = travelData.startDate ? new Date(travelData.startDate) : new Date(date);
    const endDate = travelData.endDate ? new Date(travelData.endDate) : new Date(date);

    // Set times from form data
    const [startHours, startMinutes] = (travelData.startTime || '08:00').split(':');
    const [endHours, endMinutes] = (travelData.endTime || '18:00').split(':');

    startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
    endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

    // Use travel type from form data if provided, otherwise auto-detect
    let travelType = travelData.travelType || null;

    const selectedUserIds = travelData.selectedUsers || [];

    if (selectedUserIds.length === 0) {
      alert('Please select at least one person');
      return;
    }

    // Auto-detect travel type and find matching projects if not provided
    // Use startDate from form (not original clicked date) for project matching
    const travelDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

    // Collect all matching projects for all selected users
    let matchingCheckInProjects = [];
    let matchingCheckOutProjects = [];

    selectedUserIds.forEach(selectedUserId => {
      // Find user's project assignments around this date
      // Note: assignments use camelCase (userId, projectId, startDate, isSpecialTile)
      const userProjectAssignments = (Array.isArray(assignments) ? assignments : []).filter(a =>
        a.userId === selectedUserId &&
        a.projectId &&
        !a.isSpecialTile
      );

      // Find projects starting 0-2 days after travel date (check-in candidates)
      userProjectAssignments.forEach(assignment => {
        const projectStart = new Date(assignment.startDate);
        const projectStartOnly = new Date(projectStart.getFullYear(), projectStart.getMonth(), projectStart.getDate());
        const daysBefore = Math.floor((projectStartOnly - travelDateOnly) / (1000 * 60 * 60 * 24));

        // Get project details from projects array
        const projectData = projects.find(p => p.id === assignment.projectId);

        if (daysBefore >= 0 && daysBefore <= 2) {
          // Check if project not already in list
          if (!matchingCheckInProjects.find(p => p.id === assignment.projectId)) {
            matchingCheckInProjects.push({
              id: assignment.projectId,
              name: projectData?.name || assignment.projectName || 'Unknown',
              start_date: assignment.startDate,
              end_date: assignment.endDate,
              street_address: projectData?.street_address,
              city: projectData?.city,
              country: projectData?.country
            });
          }
        }
      });

      // Find projects ending 0-2 days before travel date (check-out candidates)
      userProjectAssignments.forEach(assignment => {
        const projectEnd = new Date(assignment.endDate);
        const projectEndOnly = new Date(projectEnd.getFullYear(), projectEnd.getMonth(), projectEnd.getDate());
        const daysAfter = Math.floor((travelDateOnly - projectEndOnly) / (1000 * 60 * 60 * 24));

        // Get project details from projects array
        const projectData = projects.find(p => p.id === assignment.projectId);

        if (daysAfter >= 0 && daysAfter <= 2) {
          // Check if project not already in list
          if (!matchingCheckOutProjects.find(p => p.id === assignment.projectId)) {
            matchingCheckOutProjects.push({
              id: assignment.projectId,
              name: projectData?.name || assignment.projectName || 'Unknown',
              start_date: assignment.startDate,
              end_date: assignment.endDate,
              street_address: projectData?.street_address,
              city: projectData?.city,
              country: projectData?.country
            });
          }
        }
      });
    });

    // Determine travel type if not provided
    if (!travelType) {
      if (matchingCheckInProjects.length > 0) {
        travelType = 'check_in';
      } else if (matchingCheckOutProjects.length > 0) {
        travelType = 'check_out';
      }
    }

    // Get matching projects based on travel type
    const matchingProjects = travelType === 'check_in' ? matchingCheckInProjects :
                             travelType === 'check_out' ? matchingCheckOutProjects : [];

    // Prepare pending travel data
    const pendingTravelData = {
      selectedUserIds,
      startDate,
      endDate,
      travelType: travelType || 'check_in'
    };

    // Handle based on number of matching projects
    if (matchingProjects.length === 0) {
      // No matching projects - show warning and ask for confirmation via custom modal
      setTravelAlertModal({
        show: true,
        type: 'confirm',
        title: 'No Matching Project Found',
        message: `No matching project found for this ${travelType || 'travel'}.\n\nFor check-in: Looking for projects starting today, tomorrow, or day after tomorrow.\nFor check-out: Looking for projects ending today, yesterday, or day before yesterday.\n\nDo you want to create the travel without a project assignment?`,
        onConfirm: async () => {
          setTravelAlertModal(prev => ({ ...prev, show: false }));
          await createTravelWithProject(pendingTravelData, null);
        },
        onCancel: () => setTravelAlertModal(prev => ({ ...prev, show: false }))
      });
    } else if (matchingProjects.length === 1) {
      // Exactly one matching project - auto-assign
      await createTravelWithProject(pendingTravelData, matchingProjects[0]);
    } else {
      // Multiple matching projects - show selection modal
      setTravelProjectSelectionData({
        matchingProjects,
        pendingTravelData,
        travelType: pendingTravelData.travelType
      });
      setShowTravelProjectModal(true);
    }
  };

  // Create travel with optional project assignment
  const createTravelWithProject = async (pendingTravelData, project) => {
    try {
      const { selectedUserIds, startDate, endDate, travelType } = pendingTravelData;

      // Check for duplicate travels before creating
      const travelDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const duplicateUsers = [];

      selectedUserIds.forEach(userId => {
        const existingTravel = (Array.isArray(travelData) ? travelData : []).find(travel => {
          // Check if same user
          const travelUserId = travel.user_id || travel.user;
          if (parseInt(travelUserId) !== parseInt(userId)) return false;

          // Check if same travel type
          if (travel.travel_type !== travelType) return false;

          // Check if same day
          const existingTravelDate = new Date(travel.start_date || travel.departure_date);
          const existingDateOnly = new Date(existingTravelDate.getFullYear(), existingTravelDate.getMonth(), existingTravelDate.getDate());
          if (existingDateOnly.getTime() !== travelDateOnly.getTime()) return false;

          // Check if same project (or both without project)
          const existingProjectId = travel.project_id || travel.project?.id || travel.project_data?.id;
          const newProjectId = project?.id;
          if (existingProjectId !== newProjectId && !(existingProjectId == null && newProjectId == null)) return false;

          return true;
        });

        if (existingTravel) {
          const user = users.find(u => u.id === parseInt(userId));
          duplicateUsers.push(user ? `${user.first_name} ${user.last_name}` : `User ${userId}`);
        }
      });

      if (duplicateUsers.length > 0) {
        const projectInfo = project ? ` for project "${project.name}"` : '';
        setTravelAlertModal({
          show: true,
          type: 'alert',
          title: 'Duplicate Travel Detected',
          message: `The following users already have a ${travelType.replace('_', '-')}${projectInfo} on ${travelDateOnly.toLocaleDateString()}:\n\n${duplicateUsers.join('\n')}\n\nTravel was not created to avoid duplicates.`,
          onConfirm: () => setTravelAlertModal(prev => ({ ...prev, show: false })),
          onCancel: null
        });
        return;
      }

      const createPromises = selectedUserIds.map(async (selectedUserId) => {
        const userFormData = new FormData();
        userFormData.append('user_id', selectedUserId);
        userFormData.append('start_date', startDate.toISOString());
        userFormData.append('end_date', endDate.toISOString());
        userFormData.append('company', 1);

        // Set travel type
        userFormData.append('travel_type', travelType);

        // Set address - include project name if project is assigned
        if (project) {
          userFormData.append('project_id', project.id);
          const addressParts = [project.name];
          if (project.street_address) addressParts.push(project.street_address);
          if (project.city) addressParts.push(project.city);
          if (project.country) addressParts.push(project.country);
          userFormData.append('address', addressParts.join(' - '));
          userFormData.append('description', `${travelType.replace('_', '-')} for ${project.name}`);
        } else {
          userFormData.append('address', `Travel ${travelType.replace('_', '-')}`);
          userFormData.append('description', `Travel ${travelType.replace('_', '-')}`);
        }

        return axios.post(
          `${API_BASE_URL}/api/travel/`,
          userFormData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      });

      // Wait for all travels to be created
      await Promise.all(createPromises);

      // Refresh travel data without showing loading spinner
      await refreshTravelData();
      setShowTravelModal(false);
      setTravelPlanningData(null);
      setShowTravelProjectModal(false);
      setTravelProjectSelectionData(null);

      // Success - travel created (no alert needed)

    } catch (error) {
      logger.error('Error creating travel:', error);
      logger.error('Error details:', error.response?.data);
      alert('Failed to create travel. Please try again.');
    }
  };

  // Handle project selection from modal
  const handleTravelProjectSelected = (project) => {
    if (travelProjectSelectionData) {
      createTravelWithProject(travelProjectSelectionData.pendingTravelData, project);
    }
  };

  // Handle creating travels from assignment travel modal
  const handleCreateAssignmentTravels = async () => {
    const { assignment, checkInOption, checkOutOption } = assignmentTravelModal;
    if (!assignment) return;

    const assignmentStart = new Date(assignment.startDate);
    const assignmentEnd = new Date(assignment.endDate);

    // Get project data
    const projectData = projects.find(p => p.id === assignment.projectId);
    const project = projectData ? {
      id: projectData.id,
      name: projectData.name,
      street_address: projectData.street_address,
      city: projectData.city,
      country: projectData.country
    } : null;

    const travelsToCreate = [];

    // Calculate check-in date based on option
    if (checkInOption !== 'none') {
      let checkInDate;
      if (checkInOption === 'day_before') {
        checkInDate = new Date(assignmentStart);
        checkInDate.setDate(checkInDate.getDate() - 1);
      } else { // first_day
        checkInDate = new Date(assignmentStart);
      }
      travelsToCreate.push({
        userId: assignment.userId,
        date: checkInDate,
        travelType: 'check_in',
        project
      });
    }

    // Calculate check-out date based on option
    if (checkOutOption !== 'none') {
      let checkOutDate;
      if (checkOutOption === 'day_after') {
        checkOutDate = new Date(assignmentEnd);
        checkOutDate.setDate(checkOutDate.getDate() + 1);
      } else { // last_day
        checkOutDate = new Date(assignmentEnd);
      }
      travelsToCreate.push({
        userId: assignment.userId,
        date: checkOutDate,
        travelType: 'check_out',
        project
      });
    }

    if (travelsToCreate.length === 0) {
      setAssignmentTravelModal(prev => ({ ...prev, show: false }));
      return;
    }

    // Check for duplicates before creating
    const duplicates = [];
    travelsToCreate.forEach(travel => {
      const travelDateOnly = new Date(travel.date.getFullYear(), travel.date.getMonth(), travel.date.getDate());
      const existingTravel = (Array.isArray(travelData) ? travelData : []).find(t => {
        const travelUserId = t.user_id || t.user;
        if (parseInt(travelUserId) !== parseInt(travel.userId)) return false;
        if (t.travel_type !== travel.travelType) return false;

        const existingTravelDate = new Date(t.start_date || t.departure_date);
        const existingDateOnly = new Date(existingTravelDate.getFullYear(), existingTravelDate.getMonth(), existingTravelDate.getDate());
        if (existingDateOnly.getTime() !== travelDateOnly.getTime()) return false;

        const existingProjectId = t.project_id || t.project?.id || t.project_data?.id;
        if (existingProjectId !== travel.project?.id) return false;

        return true;
      });

      if (existingTravel) {
        duplicates.push(`${travel.travelType.replace('_', '-')} on ${travel.date.toLocaleDateString()}`);
      }
    });

    if (duplicates.length > 0) {
      setTravelAlertModal({
        show: true,
        type: 'alert',
        title: 'Duplicate Travel Detected',
        message: `The following travels already exist:\n\n${duplicates.join('\n')}\n\nDuplicate travels were skipped.`,
        onConfirm: () => setTravelAlertModal(prev => ({ ...prev, show: false })),
        onCancel: null
      });
      // Filter out duplicates and continue with non-duplicates
      const nonDuplicateTravels = travelsToCreate.filter(travel => {
        const travelDateOnly = new Date(travel.date.getFullYear(), travel.date.getMonth(), travel.date.getDate());
        return !(Array.isArray(travelData) ? travelData : []).find(t => {
          const travelUserId = t.user_id || t.user;
          if (parseInt(travelUserId) !== parseInt(travel.userId)) return false;
          if (t.travel_type !== travel.travelType) return false;
          const existingTravelDate = new Date(t.start_date || t.departure_date);
          const existingDateOnly = new Date(existingTravelDate.getFullYear(), existingTravelDate.getMonth(), existingTravelDate.getDate());
          if (existingDateOnly.getTime() !== travelDateOnly.getTime()) return false;
          const existingProjectId = t.project_id || t.project?.id || t.project_data?.id;
          return existingProjectId === travel.project?.id;
        });
      });
      if (nonDuplicateTravels.length === 0) {
        setAssignmentTravelModal(prev => ({ ...prev, show: false }));
        return;
      }
      travelsToCreate.length = 0;
      travelsToCreate.push(...nonDuplicateTravels);
    }

    try {
      // Create all travels
      const createPromises = travelsToCreate.map(async (travel) => {
        const formData = new FormData();
        formData.append('user_id', travel.userId);
        formData.append('start_date', travel.date.toISOString());
        formData.append('end_date', travel.date.toISOString());
        formData.append('company', 1);
        formData.append('travel_type', travel.travelType);

        if (travel.project) {
          formData.append('project_id', travel.project.id);
          const addressParts = [travel.project.name];
          if (travel.project.street_address) addressParts.push(travel.project.street_address);
          if (travel.project.city) addressParts.push(travel.project.city);
          if (travel.project.country) addressParts.push(travel.project.country);
          formData.append('address', addressParts.join(' - '));
          formData.append('description', `${travel.travelType.replace('_', '-')} for ${travel.project.name}`);
        } else {
          formData.append('address', `Travel ${travel.travelType.replace('_', '-')}`);
          formData.append('description', `Travel ${travel.travelType.replace('_', '-')}`);
        }

        return axios.post(`${API_BASE_URL}/api/travel/`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      });

      await Promise.all(createPromises);
      await refreshTravelData();
      setAssignmentTravelModal(prev => ({ ...prev, show: false }));
    } catch (error) {
      logger.error('Error creating assignment travels:', error);
      alert('Failed to create travels. Please try again.');
    }
  };

  // Calculate days worked for an assignment
  const calculateDaysWorked = (assignment) => {
    const startDate = new Date(assignment.startDate);
    const endDate = new Date(assignment.endDate);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
    return diffDays;
  };

  // Check alert status for assignment days
  const getAlertStatus = (assignment, projectId) => {
    const days = calculateDaysWorked(assignment);
    const threshold = projectThresholds[projectId];
    
    if (!threshold) return null;
    
    if (days >= threshold.critical) return 'critical';
    if (days >= threshold.warning) return 'warning';
    return null;
  };

  // Set threshold for a project
  const setProjectThreshold = (projectId, warning, critical) => {
    setProjectThresholds(prev => ({
      ...prev,
      [projectId]: { warning, critical }
    }));
  };

  // Create car assignment - now with project selection modal
  const createCarAssignment = async (carId, date, userId = null, projectId = null) => {
    if (projectId) {
      // Direct assignment with specific project (user optional)
      try {
        const payload = {
          car: carId,
          project: projectId,
          assigned_date: date.toISOString(),
          expected_return_date: date.toISOString(), // Same day by default
          status: 'assigned',
          assignment_reason: 'Resource planning assignment'
        };

        // Only add user if provided
        if (userId) {
          payload.user = userId;
        }

        const response = await resourceBoardService.createVehicleAssignment(payload);

        // Add to local state
        const newAssignment = {
          id: response.id,
          carId: carId,
          userId: userId || null,
          projectId: projectId,
          startDate: date,
          endDate: date,
          originalAssignment: response
        };
        
        setCarAssignments(prev => [...prev, newAssignment]);
      } catch (error) {
        logger.error('Error creating car assignment:', error);
        alert('Failed to create car assignment. Please try again.');
      }
    } else {
      // Show project selection modal
      setCarAssignmentData({
        carId: carId,
        date: date
      });
      setShowCarProjectModal(true);
    }
  };

  // Get car assignments for a specific car and day
  // ⚡ CRITICAL OPTIMIZATION: Create indexed map of car assignments for O(1) lookups
  // This prevents filtering the entire array for every cell in the calendar
  const carAssignmentsByCarId = useMemo(() => {
    // Early return when loading or no data
    if (loading || !carAssignments || carAssignments.length === 0) return new Map();

    const map = new Map();

    (Array.isArray(carAssignments) ? carAssignments : []).forEach(assignment => {
      if (!map.has(assignment.carId)) {
        map.set(assignment.carId, []);
      }
      map.get(assignment.carId).push(assignment);
    });

    return map;
  }, [loading, carAssignments]);

  // ⚡ OPTIMIZED: Now uses the indexed map instead of filtering entire array
  const getCarAssignmentForDay = (carId, date) => {
    const carAssignmentsForCar = carAssignmentsByCarId.get(carId);
    if (!carAssignmentsForCar) return undefined;

    return carAssignmentsForCar.find(assignment => {
      // Handle both single date and date range assignments
      if (assignment.startDate && assignment.endDate) {
        const checkDate = new Date(date);
        const startDate = new Date(assignment.startDate);
        const endDate = new Date(assignment.endDate);

        return checkDate >= startDate && checkDate <= endDate;
      } else if (assignment.date) {
        // Fallback to single date check
        const assignmentDate = new Date(assignment.date).toDateString();
        return date.toDateString() === assignmentDate;
      }

      return false;
    });
  };

  // Handle car project assignment confirmation
  // Handle car rental edit
  const openCarRentalModal = (car) => {
    setSelectedCarForEdit(car);
    setCarRentalFormData({
      ownership_type: car.ownership_type || 'own',
      rental_start_date: car.rental_start_date || '',
      rental_end_date: car.rental_end_date || '',
      daily_rental_cost: car.daily_rental_cost || ''
    });
    setShowCarRentalModal(true);
  };

  const handleCarRentalUpdate = async () => {
    if (!selectedCarForEdit) return;

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/cars/${selectedCarForEdit.id}/`,
        carRentalFormData,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      // Update the car in the local state
      setCarsData(prev => prev.map(car => 
        car.id === selectedCarForEdit.id 
          ? { ...car, ...response.data }
          : car
      ));

      setShowCarRentalModal(false);
      setSelectedCarForEdit(null);
    } catch (error) {
      logger.error('Error updating car rental data:', error);
      alert('Nie udało się zaktualizować danych wynajmu samochodu.');
    }
  };

  const handleCarProjectAssignment = async () => {
    const projectId = selectedCarProject || carAssignmentData.projectId;
    
    if (!projectId || !carAssignmentData.carId || !carAssignmentData.date) {
      alert('Please select a project');
      return;
    }

    const startDate = carAssignmentData.date;
    const endDate = carAssignmentData.endDate || carAssignmentData.date; // Use endDate if provided

    try {
      const payload = {
        car: carAssignmentData.carId,
        project: parseInt(projectId),
        assigned_date: startDate.toISOString(),
        expected_return_date: endDate.toISOString(),
        status: 'assigned',
        assignment_reason: 'Resource planning assignment'
      };

      // Only add user if one is selected
      if (selectedCarUser) {
        payload.user = parseInt(selectedCarUser);
      }

      const response = await resourceBoardService.createVehicleAssignment(payload);

      // Add to local state
      const newAssignment = {
        id: response.id,
        carId: carAssignmentData.carId,
        userId: selectedCarUser ? parseInt(selectedCarUser) : null,
        projectId: parseInt(projectId),
        startDate: startDate,
        endDate: endDate,
        originalAssignment: response
      };

      setCarAssignments(prev => [...prev, newAssignment]);

      // Reset and close modal
      setShowCarProjectModal(false);
      setCarAssignmentData({ carId: null, date: null });
      setSelectedCarProject('');
      setSelectedCarUser('');
    } catch (error) {
      logger.error('Error creating car assignment:', error);
      alert('Failed to create car assignment. Please try again.');
    }
  };

  // Handle car drop - similar to user drop but for cars  
  const handleCarDrop = async (e, car, dayIndex) => {
    e.preventDefault();
    
    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      
      if (!dragDataString) {
        return;
      }
      
      const dragData = JSON.parse(dragDataString);
      
      if (dragData.type === 'project' || dragData.type === 'role') {
        const days = getTimeRangeDays;
        const startDate = days[Math.min(dayIndex, days.length - 1)] || new Date();
        const endDate = new Date(startDate); // Default 1-day assignment

        // Need to select a user for this car assignment
        setCarAssignmentData({
          carId: car.id,
          date: startDate,
          endDate: endDate,
          projectId: dragData.project.id,
          projectName: dragData.project.name
        });
        setSelectedCarProject(dragData.project.id.toString());
        setShowCarProjectModal(true);
      }
    } catch (error) {
      logger.error('Error handling car drop:', error);
    }
  };

  // Handle car drag over - similar to user drag over
  const handleCarDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // Get car assignment style - similar to getAssignmentStyle but for cars
  const getCarAssignmentStyle = (assignment, projectIndex) => {
    const days = getTimeRangeDays;
    const dayWidth = getDayWidth;

    if (days.length === 0) {
      return { display: 'none' };
    }

    const assignmentStart = new Date(assignment.startDate);
    const assignmentEnd = new Date(assignment.endDate);
    const visibleStart = new Date(days[0]);
    const visibleEnd = new Date(days[days.length - 1]);

    // Hide only if assignment is completely outside visible range
    if (assignmentEnd < visibleStart || assignmentStart > visibleEnd) {
      return { display: 'none' };
    }

    const startDay = days.findIndex(day =>
      day.toDateString() === new Date(assignment.startDate).toDateString()
    );
    let endDay = days.findIndex(day =>
      day.toDateString() === new Date(assignment.endDate).toDateString()
    );

    // If assignment starts before visible range, clip to first day
    const effectiveStartDay = startDay === -1 ? 0 : startDay;

    // If assignment ends after visible range, clip to last day
    const effectiveEndDay = endDay === -1 ? days.length - 1 : endDay;
    
    let left = effectiveStartDay * dayWidth;
    let width = (effectiveEndDay - effectiveStartDay + 1) * dayWidth;

    // Apply drag state for visual feedback during drag
    if (dragState && dragState.assignmentId === assignment.id) {
      left += dragState.deltaX;
    }

    // Apply resize state for visual feedback during resize
    if (resizeState && resizeState.assignmentId === assignment.id) {
      if (resizeState.handle === 'left') {
        left += resizeState.deltaX;
        width -= resizeState.deltaX;
      } else if (resizeState.handle === 'right') {
        width += resizeState.deltaX;
      }
    }

    const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
    const color = project ? getProjectColor(project, projectIndex) : projectColors[projectIndex % projectColors.length];
    const inlineStyles = project ? getProjectInlineStyles(project) : {};
    
    return {
      left: `${left}px`,
      width: `${width}px`,
      position: 'absolute',
      top: '2px',
      height: '28px',
      className: color ? `${color} rounded border-l-4 px-2 text-xs flex items-center cursor-pointer select-none relative group` : `rounded border-l-4 px-2 text-xs flex items-center cursor-pointer select-none relative group`,
      style: color ? {} : inlineStyles
    };
  };

  // Car assignments use only modal edit - no mouse down handlers needed

  const handleDeleteTravel = async (travelId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/travel/${travelId}/`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      // Refresh travel data without showing loading spinner
      await refreshTravelData();

    } catch (error) {
      logger.error('Error deleting travel:', error);
      logger.error('Error details:', error.response?.data);
      alert('Failed to delete travel. Please try again.');
    }
  };

  const handleDeleteAssignmentFromConflicts = (assignmentId) => {
    // Get assignment data before deletion
    const assignmentToDelete = (Array.isArray(assignments) ? assignments : []).find(a => a.id === assignmentId);
    if (!assignmentToDelete) {
      logger.error('Assignment not found for deletion:', assignmentId);
      return;
    }

    // Use pending changes system (will be deleted on Save)
    addPendingDelete(assignmentId);
    setCurrentStatus('Assignment marked for deletion (click Save to confirm)');
  };

  const handleDeleteAssignment = async () => {
    if (!selectedAssignment) return;

    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    // Use pending changes system instead of immediate API call
    addPendingDelete(selectedAssignment.id);
    setCurrentStatus('Assignment deleted (not saved yet)');

    // Close modal
    cancelCommentEdit();
  };

  const addRoleToProject = async (projectId, roleName) => {
    if (!roleName.trim()) return;

    const roleColors = [
      'bg-brand-100 text-brand-800 border-brand-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-red-100 text-red-800 border-red-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200'
    ];

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/project-roles/`,
        {
          project: projectId,
          name: roleName.trim(),
          color: roleColors[Math.floor(Math.random() * roleColors.length)]
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      const newRole = {
        id: response.data.id,
        name: response.data.name,
        color: response.data.color
      };

      setProjectRoles(prev => ({
        ...prev,
        [projectId]: [...(prev[projectId] || []), newRole]
      }));

      setNewRoleInputs(prev => ({
        ...prev,
        [projectId]: ''
      }));
    } catch (error) {
      logger.error('Error adding role to project:', error);
    }
  };

  const removeRoleFromProject = async (projectId, roleId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/project-roles/${roleId}/`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      setProjectRoles(prev => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter(role => role.id !== roleId)
      }));
    } catch (error) {
      logger.error('Error removing role from project:', error);
    }
  };

  const addGlobalRole = async (roleName) => {
    if (!roleName.trim()) return;

    // Use the same dark colors (shade 800) that are displayed in personnel badges
    const roleColors = [
      '#1E40AF', // blue-800
      '#065F46', // green-800
      '#92400E', // yellow-800
      '#991B1B', // red-800
      '#6B21A8', // purple-800
      '#9F1239', // pink-800
      '#3730A3', // indigo-800
      '#9A3412', // orange-800
      '#115E59', // teal-800
      '#1F2937'  // gray-800
    ];

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/global-roles/`,
        {
          name: roleName.trim(),
          color: roleColors[globalRoles.length % roleColors.length]
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      const newRole = {
        id: response.data.id,
        name: response.data.name,
        color: response.data.color
      };

      setGlobalRoles(prev => [...prev, newRole]);
      setNewGlobalRoleInput('');
    } catch (error) {
      logger.error('Error adding global role:', error);
    }
  };

  const removeGlobalRole = async (roleId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/global-roles/${roleId}/`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      
      setGlobalRoles(prev => prev.filter(role => role.id !== roleId));
      setUserRoles(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(userId => {
          const roleToRemove = globalRoles.find(r => r.id === roleId);
          if (updated[userId]?.role === roleToRemove?.name) {
            delete updated[userId];
          }
        });
        return updated;
      });
    } catch (error) {
      logger.error('Error removing global role:', error);
    }
  };

  const handleGlobalRoleDragStart = (e, role) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'global-role',
      role: role.name
    }));
  };

  const removeAssignment = (assignmentId) => {
    // Show confirmation modal instead of window.confirm
    setPendingDeleteAssignmentId(assignmentId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteAssignment = async () => {
    if (!pendingDeleteAssignmentId) return;

    try {
      // Get assignment data before deletion for undo
      const assignmentToDelete = (Array.isArray(assignments) ? assignments : []).find(a => a.id === pendingDeleteAssignmentId);
      if (!assignmentToDelete) {
        logger.error('Assignment not found for deletion:', pendingDeleteAssignmentId);
        return;
      }

      // Check if this is a new assignment (temporary ID or _isNew flag)
      const isNewAssignment = pendingDeleteAssignmentId?.toString().startsWith('temp_') || assignmentToDelete._isNew;

      if (isNewAssignment) {
        // For new assignments, just remove from local state and pending changes
        setAssignments(prev => prev.filter(assignment => assignment.id !== pendingDeleteAssignmentId));
        setPendingChanges(prev => ({
          ...prev,
          created: prev.created.filter(a => a.id !== pendingDeleteAssignmentId)
        }));

        // Check if there are still pending changes
        const remainingChanges = pendingChanges.created.filter(a => a.id !== pendingDeleteAssignmentId).length +
                                 pendingChanges.updated.length +
                                 pendingChanges.deleted.length +
                                 pendingCarChanges.created.length +
                                 pendingCarChanges.updated.length +
                                 pendingCarChanges.deleted.length;
        if (remainingChanges === 0) {
          setHasUnsavedChanges(false);
        }

        setCurrentStatus('Unsaved assignment removed');
      } else {
        // For existing assignments, use pending changes system (will be deleted on Save)
        addPendingDelete(pendingDeleteAssignmentId);
        setCurrentStatus('Assignment marked for deletion (click Save to confirm)');
      }

      // Close modal
      setShowDeleteConfirmModal(false);
      setPendingDeleteAssignmentId(null);
    } catch (error) {
      logger.error('Error removing assignment:', error);
      setShowDeleteConfirmModal(false);
      setPendingDeleteAssignmentId(null);
    }
  };

  const cancelDeleteAssignment = () => {
    setShowDeleteConfirmModal(false);
    setPendingDeleteAssignmentId(null);
  };

  const toggleUserEnabled = (userId) => {
    setDisabledUsers(prev => {
      if (prev.includes(userId)) {
        // Enable user (remove from disabled list)
        return prev.filter(id => id !== userId);
      } else {
        // Disable user (add to disabled list)
        return [...prev, userId];
      }
    });
  };

  // Holiday approval functions
  const handleHolidayClick = (holiday) => {
    setSelectedHoliday(holiday);
    setHolidayRejectionReason('');
    setShowHolidayModal(true);
  };

  const handleApproveHoliday = async () => {
    if (!selectedHoliday) return;

    try {
      await resourceBoardService.approveHoliday(selectedHoliday.id);

      // Update local state
      setHolidays(prev => prev.map(h =>
        h.id === selectedHoliday.id
          ? { ...h, status: 'approved' }
          : h
      ));

      setShowHolidayModal(false);
      setSelectedHoliday(null);
      setCurrentStatus('Holiday approved');
      logger.info('Holiday approved:', selectedHoliday.id);
    } catch (error) {
      logger.error('Error approving holiday:', error);
      setCurrentStatus('Error approving holiday');
    }
  };

  const handleRejectHoliday = async () => {
    if (!selectedHoliday) return;

    try {
      await resourceBoardService.rejectHoliday(selectedHoliday.id, holidayRejectionReason);

      // Update local state
      setHolidays(prev => prev.map(h =>
        h.id === selectedHoliday.id
          ? { ...h, status: 'rejected' }
          : h
      ));

      setShowHolidayModal(false);
      setSelectedHoliday(null);
      setHolidayRejectionReason('');
      setCurrentStatus('Holiday rejected');
      logger.info('Holiday rejected:', selectedHoliday.id);
    } catch (error) {
      logger.error('Error rejecting holiday:', error);
      setCurrentStatus('Error rejecting holiday');
    }
  };

  const handleSaveUserManagement = async ({ userId, isActive, roleIds }) => {
    try {
      // Update user visibility on board (does NOT affect login access)
      // isActive=true means visible, so hidden_on_board=false
      await resourceBoardService.updateUser(userId, { hidden_on_board: !isActive });

      // Update local disabled users list
      setDisabledUsers(prev => {
        if (isActive) {
          // Show user on board (remove from disabled list)
          return prev.filter(id => id !== userId);
        } else {
          // Hide user on board (add to disabled list if not already there)
          return prev.includes(userId) ? prev : [...prev, userId];
        }
      });

      // Handle multiple role assignments
      // Convert userId to number for consistent comparison
      const userIdNum = Number(userId);

      const currentAssignments = Array.isArray(userGlobalRoles) ? userGlobalRoles.filter(
        ugr => Number(ugr.user) === userIdNum || Number(ugr.user_id) === userIdNum
      ) : [];

      // Convert all role IDs to numbers for consistent comparison
      const currentRoleIds = currentAssignments.map(a => Number(a.role || a.role_id));
      const newRoleIds = (roleIds || []).map(id => Number(id));

      // Find roles to remove (in current but not in new)
      const rolesToRemove = currentAssignments.filter(
        assignment => !newRoleIds.includes(Number(assignment.role || assignment.role_id))
      );

      // Find roles to add (in new but not in current)
      const rolesToAdd = newRoleIds.filter(roleId => !currentRoleIds.includes(roleId));

      // Remove old role assignments
      for (const assignment of rolesToRemove) {
        if (assignment.id) {
          await resourceBoardService.removeUserGlobalRole(assignment.id);
        }
      }

      // Add new role assignments
      for (const roleId of rolesToAdd) {
        await resourceBoardService.assignUserGlobalRole(userId, roleId);
      }

      // Directly reload userGlobalRoles to ensure immediate state update
      const updatedUserGlobalRoles = await resourceBoardService.getUserGlobalRoles().catch(() => []);
      if (Array.isArray(updatedUserGlobalRoles)) {
        setUserGlobalRoles(updatedUserGlobalRoles);

        // Also update userRoles map for UI display
        const globalRolesData = await resourceBoardService.getGlobalRoles().catch(() => []);
        const userRolesMap = {};
        updatedUserGlobalRoles.forEach(userRole => {
          const globalRole = Array.isArray(globalRolesData)
            ? globalRolesData.find(r => r.id === userRole.role)
            : null;
          if (globalRole) {
            const uId = userRole.user || userRole.user_id;
            if (!userRolesMap[uId]) {
              userRolesMap[uId] = { roles: [], colors: [] };
            }
            userRolesMap[uId].roles.push(globalRole.name);
            userRolesMap[uId].colors.push(globalRole.color);
            if (!userRolesMap[uId].role) {
              userRolesMap[uId].role = globalRole.name;
              userRolesMap[uId].color = globalRole.color;
            }
          }
        });
        setUserRoles(userRolesMap);
      }

      setCurrentStatus('User settings saved successfully');
    } catch (error) {
      logger.error('Error saving user management settings:', error);
      throw error;
    }
  };

  // ⚡ CRITICAL OPTIMIZATION: Create indexed map of user travels for O(1) lookups
  const userTravelsByUserId = useMemo(() => {
    // Early return when loading or no data
    if (loading || !travelData || travelData.length === 0) return new Map();

    const map = new Map();

    (Array.isArray(travelData) ? travelData : []).forEach(travel => {
      const userId = travel.user_id || travel.user;  // FIX: Check user_id (integer) first, not user (string name)
      if (userId) {
        const normalizedUserId = parseInt(userId);
        if (!map.has(normalizedUserId)) {
          map.set(normalizedUserId, []);
        }
        map.get(normalizedUserId).push(travel);
      }
    });

    return map;
  }, [loading, travelData]);

  // ⚡ OPTIMIZED: Now uses the indexed map instead of filtering entire array
  const getUserTravelForDay = (userId, date) => {
    const normalizedUserId = parseInt(userId);
    const travelsForUser = userTravelsByUserId.get(normalizedUserId);

    if (!travelsForUser) {
      return [];
    }

    return travelsForUser.filter(travel => {
      const travelStart = new Date(travel.start_date || travel.departure_date);
      const travelEnd = new Date(travel.end_date || travel.return_date);
      const checkDate = new Date(date);

      travelStart.setHours(0, 0, 0, 0);
      travelEnd.setHours(23, 59, 59, 999);
      checkDate.setHours(12, 0, 0, 0);

      return checkDate >= travelStart && checkDate <= travelEnd;
    });
  };

  // ⚡ CRITICAL OPTIMIZATION: Create indexed map of car travels for O(1) lookups
  // This prevents filtering the entire travelData array for every cell
  const carTravelsByCarId = useMemo(() => {
    // Early return when loading or no data
    if (loading || !travelData || travelData.length === 0) return new Map();

    const map = new Map();

    (Array.isArray(travelData) ? travelData : []).forEach(travel => {
      const carId = travel.car?.id || travel.car_id || travel.car;
      if (carId) {
        if (!map.has(carId)) {
          map.set(carId, []);
        }
        map.get(carId).push(travel);
      }
    });

    return map;
  }, [loading, travelData]);

  // ⚡ OPTIMIZED: Now uses the indexed map instead of filtering entire array
  const getCarTravelForDay = (carId, date) => {
    const travelsForCar = carTravelsByCarId.get(parseInt(carId));
    if (!travelsForCar) return [];

    return travelsForCar.filter(travel => {
      const travelStart = new Date(travel.start_date || travel.departure_date);
      const travelEnd = new Date(travel.end_date || travel.return_date);
      const checkDate = new Date(date);

      travelStart.setHours(0, 0, 0, 0);
      travelEnd.setHours(23, 59, 59, 999);
      checkDate.setHours(12, 0, 0, 0);

      const isInRange = checkDate >= travelStart && checkDate <= travelEnd;

      return isInRange;
    });
  };

  // ⚡ CRITICAL OPTIMIZATION: Create indexed map of user trainings for O(1) lookups
  const userTrainingsByUserId = useMemo(() => {
    // Early return when loading or no data
    if (loading || !trainingData || trainingData.length === 0) return new Map();

    const map = new Map();

    trainingData.forEach(training => {
      if (training.users_data) {
        training.users_data.forEach(user => {
          const userId = parseInt(user.id);
          if (!map.has(userId)) {
            map.set(userId, []);
          }
          map.get(userId).push(training);
        });
      }
    });

    return map;
  }, [loading, trainingData]);

  // ⚡ OPTIMIZED: Now uses the indexed map instead of filtering entire array
  const getUserTrainingForDay = (userId, date) => {
    const trainingsForUser = userTrainingsByUserId.get(parseInt(userId));
    if (!trainingsForUser) return [];

    const userTrainings = trainingsForUser.filter(training => {
      if (training.start_date || training.training_date || training.date) {
        const trainingStart = new Date(training.start_date || training.training_date || training.date);
        const trainingEnd = training.end_date ? new Date(training.end_date) : trainingStart;
        const checkDate = new Date(date);

        trainingStart.setHours(0, 0, 0, 0);
        trainingEnd.setHours(23, 59, 59, 999);
        checkDate.setHours(12, 0, 0, 0);

        const isInRange = checkDate >= trainingStart && checkDate <= trainingEnd;

        return isInRange;
      }

      return false;
    });
    
    return userTrainings;
  };

  const createTravelStyle = (travel, userIndex, dayIndex, travelIndex = 0) => {
    const dayWidth = getDayWidth;

    // Determine color based on travel type
    let className = 'rounded-full opacity-80';
    if (travel.travel_type === 'check_in') {
      className += ' bg-yellow-300'; // Light yellow for check-in (arrival)
    } else if (travel.travel_type === 'check_out') {
      className += ' bg-orange-600'; // Dark orange for check-out (departure)
    } else {
      className += ' bg-orange-400'; // Orange for regular travel
    }

    // Offset each travel indicator vertically to prevent overlap
    const baseTop = 20;
    const travelHeight = 6;
    const travelGap = 2;
    const topOffset = baseTop + (travelIndex * (travelHeight + travelGap));

    return {
      left: `${dayIndex * dayWidth + 2}px`,
      width: `${dayWidth - 4}px`,
      position: 'absolute',
      top: `${topOffset}px`,
      height: `${travelHeight}px`,
      className
    };
  };

  const createTrainingStyle = (training, userIndex, dayIndex) => {
    const dayWidth = getDayWidth;
    return {
      left: `${dayIndex * dayWidth + 2}px`,
      width: `${dayWidth - 4}px`,
      position: 'absolute',
      top: '26px',
      height: '6px',
      className: 'bg-green-400 rounded-full opacity-80'
    };
  };

  const hasConflict = (assignment, otherAssignments = assignments) => {
    return otherAssignments.some(other => {
      if (other.id === assignment.id || other.userId !== assignment.userId) return false;
      
      const assignmentStart = new Date(assignment.startDate);
      const assignmentEnd = new Date(assignment.endDate);
      const otherStart = new Date(other.startDate);
      const otherEnd = new Date(other.endDate);
      
      return (assignmentStart <= otherEnd && assignmentEnd >= otherStart);
    });
  };

  const getConflictingAssignments = (assignment) => {
    return (Array.isArray(assignments) ? assignments : []).filter(other => {
      if (other.id === assignment.id || other.userId !== assignment.userId) return false;

      const assignmentStart = new Date(assignment.startDate);
      const assignmentEnd = new Date(assignment.endDate);
      const otherStart = new Date(other.startDate);
      const otherEnd = new Date(other.endDate);
      
      return (assignmentStart <= otherEnd && assignmentEnd >= otherStart);
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  const days = getTimeRangeDays;
  const dayWidth = getDayWidth;

  return (
    <DndProvider backend={HTML5Backend}>
      <Suspense fallback={<div className="h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div></div>}>
      <style>{`
        .hide-horizontal-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-horizontal-scrollbar::-webkit-scrollbar-track {
          display: none;
        }
        .hide-horizontal-scrollbar::-webkit-scrollbar-thumb {
          display: none;
        }
      `}</style>
      <div className="h-screen flex flex-col bg-steel-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-4 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-shrink-0">
            <h1 className="text-xl font-bold text-slate-900 flex items-center">
              <Users className="h-5 w-5 mr-2 text-brand-600" />
              Resource Planning Board
            </h1>
          </div>

          {/* Quick User Search - Multi-select with tags */}
          <div className="flex-shrink-0 relative">
            <div className="relative">
              {/* Input container */}
              <div className="flex items-center gap-1 border border-steel-300 rounded bg-white px-2 py-1 min-w-[360px] max-w-[540px] focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 h-8">
                <Search className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />

                {/* Selected users as compact tags */}
                {selectedQuickUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center gap-0.5 bg-brand-500 text-white px-1.5 py-0.5 rounded text-xs font-medium"
                  >
                    <span className="text-[10px]">{user.first_name?.[0]}{user.last_name?.[0]}</span>
                    <button
                      onClick={() => setSelectedQuickUsers(prev => prev.filter(u => u.id !== user.id))}
                      className="hover:bg-brand-500 rounded"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}

                {/* Input */}
                <input
                  type="text"
                  placeholder={selectedQuickUsers.length === 0 ? "Filter users..." : ""}
                  value={quickUserSearchInput}
                  onChange={(e) => {
                    setQuickUserSearchInput(e.target.value);
                    setShowQuickUserDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => quickUserSearchInput.length > 0 && setShowQuickUserDropdown(true)}
                  onBlur={() => setTimeout(() => setShowQuickUserDropdown(false), 200)}
                  className="flex-1 min-w-[80px] outline-none border-0 text-xs bg-transparent"
                />

                {/* Clear all */}
                {selectedQuickUsers.length > 0 && (
                  <button
                    onClick={() => setSelectedQuickUsers([])}
                    className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Dropdown */}
              {showQuickUserDropdown && quickUserSearchInput.trim() && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-steel-300 rounded shadow-lg max-h-56 overflow-y-auto">
                  {users
                    .filter(user => {
                      if (selectedQuickUsers.some(u => u.id === user.id)) return false;
                      const query = quickUserSearchInput.toLowerCase().trim();
                      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                      const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toLowerCase();
                      return fullName.includes(query) || initials === query;
                    })
                    .slice(0, 8)
                    .map(user => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedQuickUsers(prev => [...prev, user]);
                          setQuickUserSearchInput('');
                          setShowQuickUserDropdown(false);
                        }}
                        className="px-2.5 py-1 hover:bg-brand-50 cursor-pointer border-b border-steel-100 last:border-0"
                      >
                        <div className="text-xs font-medium text-slate-900">{user.first_name} {user.last_name}</div>
                        <div className="text-[10px] text-slate-500">{user.email}</div>
                      </div>
                    ))}
                  {users.filter(user => {
                    if (selectedQuickUsers.some(u => u.id === user.id)) return false;
                    const query = quickUserSearchInput.toLowerCase().trim();
                    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                    const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toLowerCase();
                    return fullName.includes(query) || initials === query;
                  }).length === 0 && (
                    <div className="px-2.5 py-1.5 text-xs text-slate-500">No users found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Time Controls and Actions */}
          <div className="flex items-center gap-2">
            {/* Undo/Redo Buttons */}
            <div className="flex items-center gap-1 border-r border-steel-200 pr-2">
              <button
                onClick={performUndo}
                disabled={undoStack.length === 0}
                className={`p-1.5 rounded transition-colors ${
                  undoStack.length > 0
                    ? 'text-slate-700 hover:bg-steel-100 hover:text-brand-600'
                    : 'text-slate-300 cursor-not-allowed'
                }`}
                title={`Undo (Ctrl+Z) - ${undoStack.length} action(s) available`}
              >
                <Undo className="w-4 h-4" />
              </button>
              <button
                onClick={performRedo}
                disabled={redoStack.length === 0}
                className={`p-1.5 rounded transition-colors ${
                  redoStack.length > 0
                    ? 'text-slate-700 hover:bg-steel-100 hover:text-brand-600'
                    : 'text-slate-300 cursor-not-allowed'
                }`}
                title={`Redo (Ctrl+Y) - ${redoStack.length} action(s) available`}
              >
                <Redo className="w-4 h-4" />
              </button>
            </div>

            <label className="text-xs font-medium text-slate-600">View:</label>
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-3 py-1.5 border border-steel-300 rounded-xl text-xs font-medium focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="half-year">Half Year</option>
              <option value="custom">Custom Range</option>
            </select>

            {/* Custom Date Range Inputs */}
            {selectedTimeRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-steel-300 rounded-xl text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <span className="text-xs text-slate-400">-</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-steel-300 rounded-xl text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </>
            )}

            {/* Legend */}
            <div className="flex items-center gap-2 text-xs text-slate-600 border-x border-steel-200 px-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-2 bg-orange-400 rounded"></div>
                <span>Travel</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-2 bg-green-400 rounded"></div>
                <span>Training</span>
              </div>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 border rounded-xl text-xs font-medium flex items-center transition-colors ${
                showFilters ? 'bg-brand-500 text-white border-brand-500 shadow-lg shadow-brand-500/20' : 'text-slate-600 border-steel-300 hover:bg-steel-50'
              }`}
            >
              <Filter className="h-3.5 w-3.5 mr-1" />
              Filters
              {(selectedUsers.length > 0 || selectedProjects.length > 0 || selectedCars.length > 0 || selectedCustomers.length > 0) && (
                <span className="ml-1 bg-white text-brand-600 text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                  {selectedUsers.length + selectedProjects.length + selectedCars.length + selectedCustomers.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsSidebarVisible(!isSidebarVisible)}
              className="px-3 py-1.5 text-slate-600 border border-steel-300 rounded-xl hover:bg-steel-50 text-xs font-medium flex items-center transition-colors"
            >
              {isSidebarVisible ? '←' : '→'}
              <span className="ml-1">{isSidebarVisible ? 'Hide' : 'Show'} Projects</span>
            </button>

            {!hasUnsavedChanges && (
              <button
                onClick={() => setShowPublishModal(true)}
                className="px-3 py-1.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 text-xs font-medium flex items-center shadow-lg shadow-brand-500/20 transition-colors"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Publish
              </button>
            )}

            <button
              onClick={() => setShowPdfExtractModal(true)}
              className="px-3 py-1.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 text-xs font-medium flex items-center shadow-lg shadow-brand-500/20 transition-colors"
            >
              <FileText className="h-3.5 w-3.5 mr-1" />
              PDF Extract
            </button>
          </div>
        </div>

        {/* Last Save and Publish Info */}
        <div className="mt-3 flex items-center space-x-4 text-xs text-slate-600">
          {/* New changes notification */}
          {hasNewChanges && (
            <div className="flex items-center space-x-2 bg-brand-50 px-3 py-1.5 rounded border border-brand-200 animate-pulse">
              <AlertTriangle className="h-3 w-3 text-brand-600" />
              <span className="text-brand-700 font-medium">New changes available!</span>
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="ml-2 px-2 py-0.5 bg-brand-500 text-white text-xs rounded hover:bg-brand-600 transition-colors"
              >
                Refresh
              </button>
              <button
                onClick={() => setHasNewChanges(false)}
                className="ml-1 text-brand-400 hover:text-brand-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          <button
            onClick={() => lastSavedAt && setShowSaveHistoryModal(true)}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-xl border transition-colors ${
              lastSavedAt
                ? 'bg-brand-50 border-brand-200 hover:bg-brand-100 cursor-pointer'
                : 'bg-steel-50 border-steel-200 cursor-default'
            }`}
          >
            <Clock className={`h-3 w-3 ${lastSavedAt ? 'text-brand-600' : 'text-slate-400'}`} />
            <span className="text-slate-700">
              <strong>Last saved:</strong> {lastSavedAt ? formatDateTime(lastSavedAt) : 'No saves yet'}
              {lastSaveInfo && <span className="ml-1 text-brand-600">by {lastSaveInfo.user}</span>}
            </span>
          </button>

          <button
            onClick={() => publishedDateRange && setShowPublishHistoryModal(true)}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-xl border transition-colors ${
              publishedDateRange
                ? 'bg-brand-50 border-brand-200 hover:bg-brand-100 cursor-pointer'
                : 'bg-steel-50 border-steel-200 cursor-default'
            }`}
          >
            <Calendar className={`h-3 w-3 ${publishedDateRange ? 'text-brand-600' : 'text-slate-400'}`} />
            <span className="text-slate-700">
              <strong>Published:</strong> {publishedDateRange ? `${publishedDateRange.start_date} - ${publishedDateRange.end_date}` : 'Not published'}
              {publishedDateRange?.published_at && (
                <span className="ml-1 text-brand-500">
                  ({formatDateTime(publishedDateRange.published_at)})
                </span>
              )}
            </span>
          </button>

          <button
            onClick={() => navigate('/daily-task-coordination/travel-coordination')}
            className="flex items-center space-x-2 px-4 py-1.5 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors ml-auto shadow-lg shadow-brand-500/20"
            title="Travel Coordination"
          >
            <ClipboardList className="h-4 w-4" />
            <span className="font-medium text-sm">Travel Coordination</span>
          </button>

          {/* Save/Cancel buttons - only show when there are unsaved changes */}
          {hasUnsavedChanges && (
            <>
              <div className="flex items-center space-x-1.5 px-2.5 py-1 bg-amber-50 rounded-xl border border-amber-300 ml-3">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs font-bold text-amber-700">
                  {pendingChanges.updated.length + pendingChanges.deleted.length + pendingChanges.created.length +
                   pendingCarChanges.updated.length + pendingCarChanges.deleted.length + pendingCarChanges.created.length}
                </span>
              </div>
              <button
                onClick={saveAllChanges}
                className="flex items-center space-x-1 px-2.5 py-1 bg-green-600 text-white rounded-xl border border-green-700 hover:bg-green-700 transition-all shadow-sm hover:shadow"
                title="Save all changes"
              >
                <Check className="h-3 w-3" />
                <span className="font-medium text-xs">Save</span>
              </button>
              <button
                onClick={cancelAllChanges}
                className="flex items-center space-x-1 px-2.5 py-1 bg-white text-slate-700 rounded-xl border border-steel-300 hover:bg-steel-50 transition-all"
                title="Cancel all changes"
              >
                <X className="h-3 w-3" />
                <span className="font-medium text-xs">Cancel</span>
              </button>
            </>
          )}

          {/* Active user avatars */}
          {activeUsers && activeUsers.length > 0 && (
            <div className="flex items-center space-x-1 ml-20">
                {activeUsers.slice(0, 6).map((activity) => {
                  const user = activity.user_data;
                  const isActive = activity.is_active;
                  const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() || user.username?.charAt(0).toUpperCase() || 'U';
                  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email || 'Unknown User';

                  return (
                    <div
                      key={user.id}
                      className="group relative"
                      title={fullName}
                    >
                      <div
                        className={`w-7 h-7 rounded-full ${isActive ? 'bg-gradient-to-br from-green-400 to-green-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'} flex items-center justify-center text-white text-xs font-semibold shadow-sm border-2 border-white hover:scale-110 transition-transform cursor-pointer relative`}
                        onClick={() => {
                          // Don't open chat with yourself
                          const currentUserId = getCurrentUserId();
                          if (currentUserId && parseInt(user.id) === parseInt(currentUserId)) {
                            return;
                          }

                          // Check if chat is already open
                          if (!openChats.find(chat => chat.user_data.id === user.id)) {
                            setOpenChats([...openChats, activity]);
                          }
                        }}
                      >
                        {initials}
                        {/* Active indicator dot - only show if currently active */}
                        {isActive && (
                          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border border-white animate-pulse"></div>
                        )}
                      </div>
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {fullName} • {isActive ? 'Online now' : 'Was active today'}
                      </div>
                    </div>
                  );
                })}
                {activeUsers.length > 6 && (
                  <div className="w-7 h-7 rounded-full bg-steel-300 flex items-center justify-center text-slate-600 text-xs font-semibold shadow-sm border-2 border-white" title={`+${activeUsers.length - 6} more users today`}>
                    +{activeUsers.length - 6}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Rental Expiry Alerts */}
      {!rentalAlertDismissed && (Array.isArray(carsData) ? carsData : []).filter(car => car.is_rental_expiring_soon).length > 0 && (
        <div className="bg-orange-100 border-l-4 border-orange-500 p-4 mb-4 mx-6">
          <div className="flex justify-between">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-700">
                  <span className="font-medium">Warning!</span> Cars with expiring rental detected:
                </p>
                <div className="mt-2 text-sm text-orange-600">
                  <ul className="list-disc list-inside">
                    {carsData
                      .filter(car => car.is_rental_expiring_soon)
                      .map(car => (
                        <li key={car.id}>
                          {car.car_brand} {car.car_model} ({car.plate_number}) - expires in {car.rental_days_remaining} days
                          {car.daily_rental_cost && ` (${car.daily_rental_cost} PLN/day)`}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
            <button
              onClick={() => setRentalAlertDismissed(true)}
              className="flex-shrink-0 text-orange-500 hover:text-orange-700 transition-colors"
              title="Dismiss alert"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filters Sidebar - Slide from right */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          showFilters ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50">
          <h3 className="text-base font-black text-slate-900 uppercase tracking-wide">Quick Filters</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedUsers([]);
                setSelectedProjects([]);
                setSelectedCars([]);
                setSelectedCustomers([]);
                setSelectedRoles([]);
                setPeopleSearch('');
                setProjectsSearch('');
                setCarsSearch('');
                setCustomersSearch('');
                setRolesSearch('');
              }}
              className="text-xs font-bold text-brand-600 hover:text-white px-3 py-1.5 rounded-xl hover:bg-brand-500 border border-brand-300 hover:border-brand-500 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="p-1.5 text-slate-500 hover:text-white hover:bg-brand-500 rounded-xl transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Filter Content */}
        <div className="overflow-y-auto h-[calc(100%-60px)] p-4 space-y-5">
          {/* People Filter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">People</label>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search people..."
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {users
                .filter(user => {
                  if (!peopleSearch.trim()) return true;
                  const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                  return fullName.includes(peopleSearch.toLowerCase());
                })
                .map(user => (
                <label key={user.id} className="flex items-center text-sm py-1 px-2 rounded hover:bg-steel-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(prev => [...prev, user.id]);
                      } else {
                        setSelectedUsers(prev => prev.filter(id => id !== user.id));
                      }
                    }}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded mr-2"
                  />
                  <span className="truncate">
                    {user.last_name} {user.first_name?.charAt(0)}.
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Roles Filter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Roles</label>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search roles..."
                value={rolesSearch}
                onChange={(e) => setRolesSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {globalRoles
                .filter(role => {
                  if (!rolesSearch.trim()) return true;
                  return role.name.toLowerCase().includes(rolesSearch.toLowerCase());
                })
                .map((role) => (
                <label key={role.id} className="flex items-center text-sm py-1 px-2 rounded hover:bg-steel-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRoles(prev => [...prev, role.id]);
                      } else {
                        setSelectedRoles(prev => prev.filter(id => id !== role.id));
                      }
                    }}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded mr-2"
                  />
                  <div
                    className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                    style={{ backgroundColor: role.color || '#6B7280' }}
                  ></div>
                  <span className="truncate">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Projects Filter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Projects</label>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectsSearch}
                onChange={(e) => setProjectsSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {projects
                .filter(project => {
                  if (project.is_active === false) return false;
                  if (!projectsSearch.trim()) return true;
                  return project.name.toLowerCase().includes(projectsSearch.toLowerCase());
                })
                .map((project, index) => {
                const colorClass = getProjectColor(project, index);
                return (
                  <label key={project.id} className="flex items-center text-sm py-1 px-2 rounded hover:bg-steel-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjects(prev => [...prev, project.id]);
                        } else {
                          setSelectedProjects(prev => prev.filter(id => id !== project.id));
                        }
                      }}
                      className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded mr-2"
                    />
                    <div
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                      style={colorClass ? {} : getProjectInlineStyles(project)}
                    ></div>
                    <span className="truncate">{project.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Cars Filter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Cars</label>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search cars..."
                value={carsSearch}
                onChange={(e) => setCarsSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {carsData
                .filter(car => {
                  if (!carsSearch.trim()) return true;
                  const searchableText = `${car.plate_number} ${car.car_brand} ${car.car_model}`.toLowerCase();
                  return searchableText.includes(carsSearch.toLowerCase());
                })
                .map((car) => (
                <label key={car.id} className="flex items-center text-sm py-1 px-2 rounded hover:bg-steel-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCars.includes(car.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCars(prev => [...prev, car.id]);
                      } else {
                        setSelectedCars(prev => prev.filter(id => id !== car.id));
                      }
                    }}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded mr-2"
                  />
                  <div className="w-3 h-3 rounded-full mr-2 bg-steel-400 flex-shrink-0"></div>
                  <span className="truncate">
                    {car.plate_number} - {car.car_brand} {car.car_model}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Customers Filter */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">Customers</label>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={customersSearch}
                onChange={(e) => setCustomersSearch(e.target.value)}
                className="w-full text-sm pl-9 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(() => {
                const uniqueCustomers = [...new Set(
                  projects
                    .filter(project => project.customer && project.customer.trim() !== '')
                    .map(project => project.customer)
                )].sort();

                return uniqueCustomers
                  .filter(customer => {
                    if (!customersSearch.trim()) return true;
                    return customer.toLowerCase().includes(customersSearch.toLowerCase());
                  })
                  .map((customer) => (
                    <label key={customer} className="flex items-center text-sm py-1 px-2 rounded hover:bg-steel-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCustomers(prev => [...prev, customer]);
                          } else {
                            setSelectedCustomers(prev => prev.filter(c => c !== customer));
                          }
                        }}
                        className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded mr-2"
                      />
                      <div className="w-3 h-3 rounded-full mr-2 bg-green-400 flex-shrink-0"></div>
                      <span className="truncate" title={customer}>
                        {customer}
                      </span>
                    </label>
                  ));
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay when filters are open */}
      {showFilters && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={() => setShowFilters(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Projects */}
        {isSidebarVisible && (
          <div className="w-80 bg-white border-r border-steel-200 flex flex-col relative">
            <div className="p-4 border-b border-steel-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Projects</h3>
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-steel-300 rounded focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pb-16">
              {/* Special Tiles Section */}
              <div className="p-2 border-b border-steel-200 bg-steel-50">
                <h4 className="text-xs font-semibold text-slate-700 mb-2 px-1">Quick Tags</h4>

                {/* Existing tiles */}
                <div className="space-y-1 mb-2">
                  {customTiles.map(tile => (
                    <div
                      key={tile.id}
                      draggable
                      onDragStart={(e) => {
                        const dragData = {
                          type: 'special-tile',
                          project: { id: tile.id, name: tile.name, hexColor: tile.hexColor },
                          role: 'N/A'
                        };
                        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                      }}
                      className="flex items-center justify-between p-2 rounded text-xs border cursor-move hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: `${tile.hexColor}20`,
                        borderColor: tile.hexColor,
                        color: tile.hexColor
                      }}
                    >
                      <div className="flex items-center pointer-events-none">
                        <div
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: tile.hexColor }}
                        ></div>
                        <span className="font-medium" style={{ color: '#000' }}>{tile.name}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCustomTile(tile.id);
                        }}
                        className="hover:opacity-70 text-sm pointer-events-auto"
                        style={{ color: '#000' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new tile */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-1">
                    <input
                      type="text"
                      placeholder="New tag name..."
                      value={newTileInput}
                      onChange={(e) => setNewTileInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addCustomTile(newTileInput);
                        }
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                    />
                    <button
                      onClick={() => addCustomTile(newTileInput)}
                      className="px-2 py-1 bg-brand-500 text-white rounded text-xs hover:bg-brand-500"
                    >
                      +
                    </button>
                  </div>

                  {/* Custom hex color picker */}
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-600">Color:</label>
                    <input
                      type="color"
                      value={customColorHex}
                      onChange={(e) => setCustomColorHex(e.target.value)}
                      className="w-8 h-8 rounded border border-steel-300 cursor-pointer"
                      title="Pick color"
                    />
                    <span className="text-[10px] text-slate-500 font-mono">{customColorHex}</span>
                  </div>
                </div>
              </div>

              {/* Projects Section */}
              {projects.length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No projects found</p>
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {getFilteredProjects.map((project, index) => {
                    const isExpanded = expandedProjects[project.id];
                    const projectAssignments = (Array.isArray(assignments) ? assignments : []).filter(a => a.projectId === project.id);
                    const colorClass = getProjectColor(project, index);

                    return (
                      <div key={project.id} className="border rounded-xl">
                        <div
                          draggable
                          onDragStart={(e) => handleProjectDragStart(e, project)}
                          onClick={() => toggleProject(project.id)}
                          className="w-full p-3 text-left hover:bg-steel-50 flex items-center justify-between cursor-pointer"
                        >
                          <div className="flex items-center">
                            <div
                              className={colorClass ? `w-3 h-3 rounded-full mr-3 ${colorClass.split(' ')[0]}` : 'w-3 h-3 rounded-full mr-3'}
                              style={colorClass ? {} : getProjectInlineStyles(project)}
                            ></div>
                            <div>
                              <div className="font-medium text-slate-900 text-sm">{project.name}</div>
                            </div>
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="border-t bg-steel-50 p-2">
                            {/* Project Roles Section */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-medium text-slate-700">Roles</h4>
                              </div>
                              
                              {/* Existing Roles */}
                              <div className="space-y-1 mb-2">
                                {(projectRoles[project.id] || []).map(role => (
                                  <div 
                                    key={role.id} 
                                    className={`flex items-center justify-between p-2 rounded text-xs border cursor-move ${role.color}`}
                                    draggable="true"
                                    onDragStart={(e) => handleRoleDragStart(e, project, role)}
                                  >
                                    <span className="font-medium pointer-events-none">{role.name}</span>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeRoleFromProject(project.id, role.id);
                                      }}
                                      className="text-brand-600 hover:text-red-800 text-xs pointer-events-auto"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Add New Role */}
                              <div className="flex items-center space-x-1">
                                <input
                                  type="text"
                                  placeholder="Role name..."
                                  value={newRoleInputs[project.id] || ''}
                                  onChange={(e) => setNewRoleInputs(prev => ({
                                    ...prev,
                                    [project.id]: e.target.value
                                  }))}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      addRoleToProject(project.id, newRoleInputs[project.id]);
                                    }
                                  }}
                                  className="flex-1 px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                                />
                                <button 
                                  onClick={() => addRoleToProject(project.id, newRoleInputs[project.id])}
                                  className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-600"
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            {/* Alert Thresholds */}
                            <div className="border-t pt-2 mb-2">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-medium text-slate-700">Alert Thresholds</h4>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-slate-600">Warning (days):</label>
                                  <input
                                    type="number"
                                    placeholder="30"
                                    value={projectThresholds[project.id]?.warning || ''}
                                    onChange={(e) => {
                                      const warning = parseInt(e.target.value) || 0;
                                      const critical = projectThresholds[project.id]?.critical || warning + 10;
                                      setProjectThreshold(project.id, warning, critical);
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-slate-600">Critical (days):</label>
                                  <input
                                    type="number"
                                    placeholder="45"
                                    value={projectThresholds[project.id]?.critical || ''}
                                    onChange={(e) => {
                                      const critical = parseInt(e.target.value) || 0;
                                      const warning = projectThresholds[project.id]?.warning || Math.max(critical - 10, 0);
                                      setProjectThreshold(project.id, warning, critical);
                                    }}
                                    className="w-full px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Current Assignments */}
                            <div className="border-t pt-2">
                              <h4 className="text-xs font-medium text-slate-700 mb-2">Current Assignments</h4>
                              
                              {(() => {
                                const roleCount = {};
                                projectAssignments.forEach(assignment => {
                                  const user = (Array.isArray(users) ? users : []).find(u => u.id === assignment.userId);
                                  const globalRole = userRoles[assignment.userId]?.role;
                                  if (globalRole) {
                                    roleCount[globalRole] = (roleCount[globalRole] || 0) + 1;
                                  }
                                });
                                
                                return Object.keys(roleCount).length > 0 && (
                                  <div className="mb-2">
                                    <div className="text-xs text-slate-600 mb-1">Roles in project:</div>
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries(roleCount).map(([role, count]) => (
                                        <span key={role} className="text-xs bg-steel-100 px-2 py-1 rounded">
                                          {role}: {count}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {projectAssignments.length === 0 ? (
                                <p className="text-xs text-slate-500 text-center py-2">No assignments yet</p>
                              ) : (
                                <div className="space-y-1">
                                  {projectAssignments.map(assignment => {
                                    const user = (Array.isArray(users) ? users : []).find(u => u.id === assignment.userId);
                                    const globalRole = userRoles[assignment.userId]?.role;
                                    const days = calculateDaysWorked(assignment);
                                    const alertStatus = getAlertStatus(assignment, project.id);
                                    
                                    return (
                                      <div key={assignment.id} className={`flex items-center text-xs p-2 rounded border ${
                                        alertStatus === 'critical' ? 'bg-red-50 border-red-200' :
                                        alertStatus === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                                        'bg-white border-steel-200'
                                      }`}>
                                        <User className="h-3 w-3 mr-2 text-slate-400" />
                                        <div className="flex-1">
                                          <span className="font-medium">
                                            {user?.first_name} {user?.last_name} ({days} days)
                                          </span>
                                          {alertStatus && (
                                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                              alertStatus === 'critical' 
                                                ? 'bg-red-100 text-red-800' 
                                                : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                              {alertStatus === 'critical' ? '🚨 CRITICAL' : '⚠️ WARNING'}
                                            </span>
                                          )}
                                          {globalRole && (
                                            <div className={`text-xs px-1 py-0.5 rounded border ${userRoles[assignment.userId].color} inline-block ml-2`}>
                                              {globalRole}
                                            </div>
                                          )}
                                        </div>
                                        <span className="text-slate-500">{assignment.role}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Global Roles Section - Collapsible */}
            <div className="border-t border-steel-200 p-4">
              <div 
                className="flex items-center justify-between mb-3 cursor-pointer hover:bg-steel-50 p-2 rounded"
                onClick={() => setShowGlobalRoles(!showGlobalRoles)}
              >
                <div className="flex items-center">
                  {showGlobalRoles ? (
                    <ChevronDown className="h-4 w-4 mr-2 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2 text-slate-400" />
                  )}
                  <h3 className="font-semibold text-slate-900 text-sm">Global Roles</h3>
                </div>
                <span className="text-xs bg-steel-100 px-2 py-1 rounded">{globalRoles.length}</span>
              </div>
              
              {showGlobalRoles && (
                <>
                  {/* Existing Global Roles */}
                  <div className="space-y-1 mb-3">
                    {globalRoles.map(role => {
                      // Check if color is hex or Tailwind class
                      const isHexColor = role.color && role.color.startsWith('#');

                      return (
                        <div
                          key={role.id}
                          className={`flex items-center justify-between p-2 rounded text-xs border cursor-move ${isHexColor ? '' : role.color}`}
                          style={isHexColor ? {
                            backgroundColor: role.color,
                            color: '#FFFFFF',
                            borderColor: role.color
                          } : {}}
                          draggable="true"
                          onDragStart={(e) => handleGlobalRoleDragStart(e, role)}
                        >
                          <span className="font-medium pointer-events-none">{role.name}</span>
                          <button
                            onClick={() => removeGlobalRole(role.id)}
                            className="text-white hover:text-slate-200 text-xs pointer-events-auto"
                            style={{ opacity: 0.8 }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Add New Global Role */}
                  <div className="flex items-center space-x-1">
                    <input
                      type="text"
                      placeholder="Role name..."
                      value={newGlobalRoleInput}
                      onChange={(e) => setNewGlobalRoleInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addGlobalRole(newGlobalRoleInput);
                        }
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-steel-300 rounded focus:ring-1 focus:ring-brand-500"
                    />
                    <button 
                      onClick={() => addGlobalRole(newGlobalRoleInput)}
                      className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-600"
                    >
                      +
                    </button>
                  </div>
                  
                  <div className="text-xs text-slate-500 mt-2">
                    Drag roles to personnel to assign them to users
                  </div>
                </>
              )}
            </div>
          </div>
        )}


        {/* Right Side - Timeline */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Timeline Header */}
          <div className="bg-white border-b border-steel-200">
            <div className="flex">
              <PersonnelSection
                isSidebarVisible={isSidebarVisible}
                showPeople={showPeople}
                showCars={showCars}
                showEquipment={showEquipment}
                showDisabledUsers={showDisabledUsers}
                globalRoles={globalRoles}
                selectedRoleSort={selectedRoleSort}
                onShowPeopleChange={setShowPeople}
                onShowCarsChange={setShowCars}
                onShowEquipmentChange={setShowEquipment}
                onShowDisabledUsersChange={setShowDisabledUsers}
                onSelectedRoleSortChange={setSelectedRoleSort}
                onOpenRoleOrderModal={() => setShowRoleOrderModal(true)}
              />
              <div className="flex-1 overflow-x-hidden overflow-y-hidden" ref={timelineRef}>
                <TimelineHeader
                  days={days}
                  dayWidth={dayWidth}
                />
              </div>
            </div>
          </div>

          {/* Timeline Body */}
          <div className="flex-1 overflow-hidden flex">
            {/* Personnel Column - scrolls only vertically with timeline */}
            <div className={`${isSidebarVisible ? 'w-48' : 'w-56'} bg-steel-50 overflow-y-scroll overflow-x-hidden relative hide-horizontal-scrollbar pb-16`}
                 ref={personnelColumnRef}
                 onScroll={handlePersonnelScroll}
                 style={{
                   maxHeight: 'calc(100vh - 280px)',
                   scrollbarWidth: 'thin', // Show thin scrollbar in Firefox for personnel
                   msOverflowStyle: 'auto', // Show scrollbar in IE/Edge for personnel
                   boxSizing: 'border-box',
                   padding: 0,
                   margin: 0,
                   overscrollBehavior: 'contain' // Prevent overscroll bounce in all directions
                 }}>
                {showPeople && getFilteredUsers.map((user, userIndex) => {
                  // Convert Tailwind color class to hex color
                  const tailwindToHex = (tailwindClass) => {
                    if (!tailwindClass) return '#6B7280'; // Default gray

                    // If already a hex color, return it
                    if (tailwindClass.startsWith('#')) return tailwindClass;

                    // Extract bg-color from Tailwind classes (e.g., "bg-brand-100 text-brand-800" -> "bg-brand-100")
                    const bgMatch = tailwindClass.match(/bg-(\w+)-(\d+)/);
                    if (!bgMatch) return '#6B7280';

                    const [, colorName, shade] = bgMatch;

                    // Map Tailwind colors to hex values for badge colors (using darker shades for visibility)
                    const colorMap = {
                      'blue': { '100': '#DBEAFE', '500': '#3B82F6', '800': '#1E40AF' },
                      'green': { '100': '#D1FAE5', '500': '#10B981', '800': '#065F46' },
                      'yellow': { '100': '#FEF3C7', '500': '#F59E0B', '800': '#92400E' },
                      'red': { '100': '#FEE2E2', '500': '#EF4444', '800': '#991B1B' },
                      'purple': { '100': '#EDE9FE', '500': '#A855F7', '800': '#6B21A8' },
                      'pink': { '100': '#FCE7F3', '500': '#EC4899', '800': '#9F1239' },
                      'indigo': { '100': '#E0E7FF', '500': '#6366F1', '800': '#3730A3' },
                      'orange': { '100': '#FFEDD5', '500': '#F97316', '800': '#9A3412' },
                      'teal': { '100': '#CCFBF1', '500': '#14B8A6', '800': '#115E59' },
                      'gray': { '100': '#F3F4F6', '500': '#6B7280', '800': '#1F2937' }
                    };

                    // Use 800 shade for better visibility in small badges
                    return colorMap[colorName]?.[800] || colorMap[colorName]?.['500'] || '#6B7280';
                  };

                  // Get all roles for user (support multiple roles)
                  const getUserRoles = () => {
                    if (!userRoles[user.id]) return [];
                    const roleData = userRoles[user.id];

                    // If user has multiple roles, return array of all roles
                    if (roleData.roles && Array.isArray(roleData.roles)) {
                      return roleData.roles.map((roleName, index) => {
                        // If role name is short (2-3 chars), use it directly, otherwise take first 2-3 chars
                        let abbrev;
                        if (roleName.length <= 3) {
                          abbrev = roleName.toUpperCase();
                        } else {
                          abbrev = roleName.substring(0, 3).toUpperCase();
                        }

                        // Convert Tailwind class to hex color
                        const color = tailwindToHex(roleData.colors[index]);

                        return { abbrev, color, fullName: roleName };
                      });
                    }

                    // Fallback to single role (backward compatibility)
                    const roleName = roleData.role || '';
                    if (!roleName) return [];

                    let abbrev;
                    if (roleName.length <= 3) {
                      abbrev = roleName.toUpperCase();
                    } else {
                      abbrev = roleName.substring(0, 3).toUpperCase();
                    }

                    const color = tailwindToHex(roleData.color);
                    return [{ abbrev, color, fullName: roleName }];
                  };

                  const roleInfos = getUserRoles();
                  const maxTracks = getMaxTracksForUser(user.id);
                  const rowHeight = Math.max(32, 4 + (maxTracks * 25)); // Same calculation as timeline
                  const cityAbbrev = getCityAbbreviation(user);
                  const tooltipText = 'Double-click to manage user';

                  const isDisabled = disabledUsers.includes(user.id);

                  return (
                    <div
                      key={user.id}
                      className={`px-2 py-1 border-r border-b border-steel-200 flex items-center transition-colors cursor-pointer ${
                        isDisabled
                          ? 'bg-red-50 hover:bg-red-100 opacity-70'
                          : highlightedRowId === user.id
                          ? 'bg-rose-100 border-l-4 border-l-rose-600'
                          : 'hover:bg-brand-50'
                      }`}
                      style={{ height: `${rowHeight}px`, boxSizing: 'border-box' }}
                      onDrop={(e) => handleUserRoleDrop(e, user)}
                      onDragOver={handleUserDragOver}
                      onClick={(e) => {
                        // Single click to highlight row
                        e.stopPropagation();
                        setHighlightedRowId(prev => prev === user.id ? null : user.id);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setSelectedUserForManagement(user);
                        setShowUserManagementModal(true);
                      }}
                      title={tooltipText}
                    >
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-900 truncate flex items-center">
                          {roleInfos.map((roleInfo, idx) => (
                            <div
                              key={idx}
                              className="w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold mr-1 flex-shrink-0"
                              style={{
                                fontSize: '8px',
                                lineHeight: '1',
                                backgroundColor: roleInfo.color || '#6B7280' // Use color from DB or default gray
                              }}
                              title={roleInfo.fullName}
                            >
                              {roleInfo.abbrev}
                            </div>
                          ))}
                          {user.last_name} {user.first_name}
                        </div>
                      </div>

                      {/* Hidden Badge */}
                      {isDisabled && (
                        <div
                          className="ml-1 px-1.5 py-0.5 bg-brand-500 text-white rounded text-xs font-bold"
                          title="User is hidden on Resource Board"
                        >
                          HIDDEN
                        </div>
                      )}

                      {/* City Badge */}
                      {cityAbbrev && (
                        <div
                          className="ml-1 px-1.5 py-0.5 bg-brand-100 text-brand-700 rounded text-xs font-medium"
                          title={user.personal_address || cityAbbrev}
                        >
                          {cityAbbrev}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Cars Section - Only vehicles with equipment_type='car' */}
                {showCars && carsData
                  .filter(car => car.equipment_type === 'car')
                  .map((car) => (
                  <div
                    key={car.id}
                    className={`h-12 p-3 border-r border-b border-steel-200 flex items-center transition-colors cursor-pointer ${
                      car.is_rental_expiring_soon ? 'bg-orange-50 border-orange-200' :
                      highlightedRowId === `car_${car.id}` ? 'bg-rose-100 border-l-4 border-l-rose-600' :
                      'hover:bg-green-50'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHighlightedRowId(prev => prev === `car_${car.id}` ? null : `car_${car.id}`);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      openCarRentalModal(car);
                    }}
                    title="Double-click to edit rental data"
                    style={{ boxSizing: 'border-box' }}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      {(() => {
                        const { Icon, color, bgColor } = getEquipmentIcon(car.equipment_type);
                        return (
                          <div className={`w-6 h-6 ${car.is_rental_expiring_soon ? 'bg-orange-100' : bgColor} rounded-full flex items-center justify-center ${car.is_rental_expiring_soon ? 'text-orange-600' : color} mr-2 flex-shrink-0`}>
                            <Icon className="w-3 h-3" />
                          </div>
                        );
                      })()}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate mb-1 flex items-center">
                          {car.car_brand} {car.car_model}
                          {car.is_rental_expiring_soon && (
                            <AlertTriangle className="w-3 h-3 text-orange-500 ml-1 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {car.plate_number} •
                          {car.ownership_type === 'rented' ? (
                            <span className={car.is_rental_expiring_soon ? 'text-orange-600 font-medium' : ''}>
                              Rented {car.rental_days_remaining !== null ? `(${car.rental_days_remaining} days)` : ''}
                            </span>
                          ) : (
                            <span>Owned</span>
                          )}
                          {car.side_number && ` • Fleet: ${car.side_number}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Other Equipment Section - Machines, lifts, lighting towers, cherry pickers, etc. */}
                {showEquipment && carsData
                  .filter(car => car.equipment_type !== 'car')
                  .map((car) => (
                  <div
                    key={car.id}
                    className={`h-12 p-3 border-r border-b border-steel-200 flex items-center transition-colors cursor-pointer ${
                      car.is_rental_expiring_soon ? 'bg-orange-50 border-orange-200' :
                      highlightedRowId === `car_${car.id}` ? 'bg-rose-100 border-l-4 border-l-rose-600' :
                      'hover:bg-green-50'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHighlightedRowId(prev => prev === `car_${car.id}` ? null : `car_${car.id}`);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      openCarRentalModal(car);
                    }}
                    title="Double-click to edit rental data"
                    style={{ boxSizing: 'border-box' }}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      {(() => {
                        const { Icon, color, bgColor } = getEquipmentIcon(car.equipment_type);
                        return (
                          <div className={`w-6 h-6 ${car.is_rental_expiring_soon ? 'bg-orange-100' : bgColor} rounded-full flex items-center justify-center ${car.is_rental_expiring_soon ? 'text-orange-600' : color} mr-2 flex-shrink-0`}>
                            <Icon className="w-3 h-3" />
                          </div>
                        );
                      })()}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate mb-1 flex items-center">
                          {car.car_brand} {car.car_model}
                          {car.is_rental_expiring_soon && (
                            <AlertTriangle className="w-3 h-3 text-orange-500 ml-1 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {car.plate_number} •
                          {car.ownership_type === 'rented' ? (
                            <span className={car.is_rental_expiring_soon ? 'text-orange-600 font-medium' : ''}>
                              Rented {car.rental_days_remaining !== null ? `(${car.rental_days_remaining} days)` : ''}
                            </span>
                          ) : (
                            <span>Owned</span>
                          )}
                          {car.side_number && ` • Fleet: ${car.side_number}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Timeline Content - scrolls both horizontally and vertically */}
            <div className="flex-1 overflow-x-auto overflow-y-scroll hide-horizontal-scrollbar pb-16" ref={bodyTimelineRef} onScroll={handleBodyScroll}
                 style={{
                   maxHeight: 'calc(100vh - 280px)',
                   scrollbarWidth: 'none', // Firefox
                   msOverflowStyle: 'none', // IE/Edge
                   WebkitOverflowScrolling: 'touch',
                   boxSizing: 'border-box',
                   padding: 0,
                   margin: 0,
                   overscrollBehavior: 'contain' // Prevent overscroll bounce in all directions
                 }}>
              <div className="relative" style={{ width: days.length * dayWidth, minHeight: '100%' }}>
                {/* Grid Lines */}
                {days.map((day, index) => (
                  <div
                    key={index}
                    className="absolute top-0 bottom-0 border-r border-steel-200"
                    style={{ left: index * dayWidth, width: 1 }}
                  />
                ))}

                {/* Today Indicator Line */}
                {(() => {
                  const today = new Date();
                  const todayIndex = days.findIndex(day => {
                    const dayDate = new Date(day);
                    return dayDate.toDateString() === today.toDateString();
                  });

                  if (todayIndex !== -1) {
                    return (
                      <div
                        className="absolute top-0 bottom-0 bg-brand-500 opacity-20 pointer-events-none"
                        style={{
                          left: todayIndex * dayWidth,
                          width: dayWidth,
                          zIndex: 1
                        }}
                      />
                    );
                  }
                  return null;
                })()}

                {/* User Rows */}
                {showPeople && getFilteredUsers.map((user, userIndex) => {
                  const maxTracks = getMaxTracksForUser(user.id);
                  const rowHeight = Math.max(32, 4 + (maxTracks * 25)); // 32px minimum, dynamic based on tracks
                  const tracks = calculateAssignmentTracks(user.id);

                  return (
                  <div
                    key={user.id}
                    className={`border-b border-steel-200 relative ${
                      highlightedRowId === user.id
                        ? 'bg-rose-50 border-l-4 border-l-rose-600'
                        : dropTargetUser === user.id && assignmentDragState?.isDragging
                        ? 'border-l-4 border-l-brand-400'
                        : ''
                    }`}
                    style={{
                      height: `${rowHeight}px`,
                      width: Math.max(days.length * dayWidth, window.innerWidth - 400),
                      minWidth: days.length * dayWidth,
                      boxSizing: 'border-box',
                      contain: 'layout style paint',
                      willChange: dropTargetUser === user.id ? 'border-color' : 'auto'
                    }}
                    data-user-row="true"
                    data-user-id={user.id}
                    onDrop={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relativeX = e.clientX - rect.left;
                      const dayWidth = getDayWidth;
                      const rawDayIndex = relativeX / dayWidth;
                      const flooredIndex = Math.floor(rawDayIndex);
                      const finalDayIndex = Math.min(flooredIndex, days.length - 1);

                      if (relativeX > 0) {
                        handleUserDrop(e, user, finalDayIndex);
                      }
                    }}
                    onDragOver={handleUserDragOver}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relativeX = e.clientX - rect.left;
                      if (relativeX > 0) {
                        const dayIndex = Math.min(Math.floor(relativeX / getDayWidth), days.length - 1);
                        const selectedDate = days[dayIndex];
                        if (selectedDate) {
                          // Check if this date is before/after a project for smart context menu
                          const userProjectAssignments = (Array.isArray(assignments) ? assignments : []).filter(a =>
                            a.user === user.id &&
                            a.project_data && 
                            a.project_data.project_type === 'project'
                          );
                          
                          setContextMenuData({ 
                            user, 
                            date: selectedDate,
                            userProjectAssignments
                          });
                          setContextMenuPosition({ x: e.clientX, y: e.clientY });
                          setShowTravelContextMenu(true);
                        }
                      }
                    }}
                  >
                    {/* Day cells - merged single layer */}
                    {days.map((day, dayIndex) => {
                      const isSunday = day.getDay() === 0;
                      return (
                        <div
                          key={`day-cell-${dayIndex}`}
                          className={`absolute top-0 h-full border-r border-steel-200 ${
                            isSunday ? 'bg-red-50 opacity-40' : ''
                          }`}
                          style={{
                            left: `${dayIndex * dayWidth}px`,
                            width: `${dayWidth}px`,
                            zIndex: 0,
                            contain: 'paint'
                          }}
                        />
                      );
                    })}
                    {/* User Assignments */}
                    {getFilteredAssignments
                      .filter(assignment => assignment.userId === user.id)
                      .map(assignment => {
                        const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
                        const projectIndex = (Array.isArray(projects) ? projects : []).findIndex(p => p.id === assignment.projectId);
                        const trackNumber = tracks[assignment.id] || 0;
                        const style = getAssignmentStyle(assignment, projectIndex, trackNumber);
                        const conflicts = getConflictingAssignments(assignment);
                        const hasConflicts = conflicts.length > 0;

                        // Check if this assignment should be visually muted (not in selected project filter)
                        const isMuted = isAssignmentMuted(assignment);

                        if (style.display === 'none') return null;

                        // For special tiles, use projectName instead of project.name
                        const displayName = assignment.isSpecialTile ? assignment.projectName : project?.name;
                        const displayRole = assignment.isSpecialTile ? '' : ` - ${assignment.role}`;
                        let titleText = `${displayName}${displayRole}`;
                        if (isMuted) {
                          titleText += '\n(Other allocation)';
                        }
                        if (hasConflicts) {
                          const conflictProjects = conflicts.map(c => (Array.isArray(projects) ? projects : []).find(p => p.id === c.projectId)?.name).join(', ');
                          titleText += `\n⚠️ CONFLICT: Overlaps with ${conflictProjects}`;
                        }
                        
                        return (
                          <div
                            key={assignment.id}
                            data-assignment-id={assignment.id}
                            data-original-width={style.width}
                            data-is-muted={isMuted}
                            className={`${style.className} group ${
                              assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging
                                ? 'opacity-30'
                                : ''
                            } ${isMuted ? 'ring-1 ring-dashed ring-slate-400' : ''}`}
                            style={{
                              left: style.left,
                              width: style.width,
                              position: style.position,
                              top: style.top,
                              height: style.height,
                              pointerEvents: assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging ? 'none' : 'auto',
                              willChange: 'transform, left, width',
                              touchAction: 'none',
                              transform: 'translate3d(0,0,0)', // GPU acceleration
                              backfaceVisibility: 'hidden', // Better rendering
                              // Muted assignments: reduced opacity and grayscale filter for "faded" look
                              opacity: isMuted ? 0.4 : 1,
                              filter: isMuted ? 'grayscale(50%) saturate(60%)' : 'none',
                              // Transform is now controlled by DOM manipulation in handleMouseMove
                              ...style.style
                            }}
                            title={titleText}
                            onMouseDown={(e) => {
                              // Ignore right-click (context menu)
                              if (e.button !== 0) {
                                return;
                              }

                              // Check if clicking on specific handles
                              const deleteBtn = e.target.closest('.delete-btn');
                              const travelBtn = e.target.closest('.travel-btn');
                              const resizeHandle = e.target.closest('.resize-handle');
                              const moveHandle = e.target.closest('.move-handle');

                              if (deleteBtn || travelBtn || resizeHandle) {
                                return; // Let these handles work normally
                              }

                              if (moveHandle) {
                                handleMouseDown(e, assignment, 'move-user');
                                return;
                              }

                              // Default drag behavior (timeline drag)
                              handleMouseDown(e, assignment, 'drag');
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Open double-click modal on right-click
                              handleAssignmentDoubleClick(e, assignment);
                            }}
                            onDoubleClick={(e) => handleAssignmentDoubleClick(e, assignment)}
                          >
                            <div
                              className="resize-handle absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 hover:opacity-100 bg-steel-700 rounded-l"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleMouseDown(e, assignment, 'resize', 'left');
                              }}
                              style={{ zIndex: 15, touchAction: 'none' }}
                            />
                            
                            <div
                              className="move-handle absolute left-1 top-1/2 transform -translate-y-1/2 opacity-80 group-hover:opacity-100 cursor-move"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('Grip clicked - starting vertical drag');
                                handleMouseDown(e, assignment, 'move-user');
                              }}
                              style={{ zIndex: 25 }}
                              title="Drag to move to another person (or hold ALT to copy)"
                            >
                              <GripVertical className="h-4 w-4 text-white drop-shadow-lg" strokeWidth={3} />
                            </div>
                            
                            <span className="truncate pointer-events-none flex items-center pl-6 pr-6">
                              {hasConflicts && (
                                <AlertTriangle className="h-3 w-3 text-brand-600 mr-1 flex-shrink-0" />
                              )}
                              {assignment.isSpecialTile ?
                                (assignment.projectName || assignment.projectId) :
                                (assignment.role === 'Team Member' ? project?.name : `${project?.name} (${assignment.role})`)
                              }
                              {assignmentComments[assignment.id] && (
                                <span className="ml-1 text-white"> - {assignmentComments[assignment.id]}</span>
                              )}
                            </span>
                            
                            <button
                              className="travel-btn absolute right-9 top-1 w-4 h-4 bg-brand-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 hover:bg-brand-500 flex items-center justify-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignmentTravelModal({
                                  show: true,
                                  assignment: assignment,
                                  checkInOption: 'day_before',
                                  checkOutOption: 'day_after'
                                });
                              }}
                              style={{ zIndex: 20 }}
                              title="Plan check-in/check-out"
                            >
                              <Plane className="h-2.5 w-2.5" />
                            </button>

                            <button
                              className="delete-btn absolute right-4 top-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 hover:bg-brand-500 flex items-center justify-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeAssignment(assignment.id);
                              }}
                              style={{ zIndex: 20 }}
                            >
                              ×
                            </button>

                            <div
                              className="resize-handle absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 hover:opacity-100 bg-steel-700 rounded-r"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleMouseDown(e, assignment, 'resize', 'right');
                              }}
                              style={{ zIndex: 15, touchAction: 'none' }}
                            />
                          </div>
                        );
                      })
                    }

                    {/* User Holidays */}
                    {holidays
                      .filter(holiday => holiday.userId === user.id)
                      .map((holiday, idx) => {
                        const trackNumber = idx; // Simple stacking for now
                        const style = getHolidayStyle(holiday, trackNumber);

                        if (style.display === 'none') return null;

                        // Format dates for display
                        const formatDate = (date) => {
                          const d = new Date(date);
                          return `${d.getDate()}/${d.getMonth() + 1}`;
                        };

                        const statusText = holiday.status === 'approved' ? 'Approved' :
                                         holiday.status === 'pending' ? 'Pending' : 'Rejected';

                        return (
                          <div
                            key={`holiday-${holiday.id}`}
                            className={`${style.className} cursor-pointer hover:opacity-90 transition-opacity`}
                            style={{
                              left: style.left,
                              width: style.width,
                              position: style.position,
                              top: style.top,
                              height: style.height,
                              ...style.style
                            }}
                            title={`Holiday: ${formatDate(holiday.startDate)} - ${formatDate(holiday.endDate)}\nStatus: ${statusText}${holiday.notes ? `\nNotes: ${holiday.notes}` : ''}\nClick to approve/reject`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHolidayClick(holiday);
                            }}
                          >
                            <span className="truncate flex items-center">
                              <span className="font-semibold mr-1">🏖️</span>
                              Holiday ({statusText})
                            </span>
                          </div>
                        );
                      })
                    }

                    {/* Travel Indicators */}
                    {days.map((day, dayIndex) => {
                      const dayTravels = getUserTravelForDay(user.id, day);
                      if (user.id === 17 && dayTravels.length > 0) {
                      }
                      
                      return dayTravels.map((travel, travelIndex) => {
                        const style = createTravelStyle(travel, userIndex, dayIndex, travelIndex);
                        
                        
                        return (
                          <div
                            key={`travel-${travel.id}-${dayIndex}`}
                            className={style.className}
                            style={{
                              left: style.left,
                              width: style.width,
                              position: style.position,
                              top: style.top,
                              height: style.height,
                              pointerEvents: 'auto'
                            }}
                            onMouseEnter={(e) => {
                              // Clear any existing timeout
                              if (tooltipTimeoutRef.current) {
                                clearTimeout(tooltipTimeoutRef.current);
                                tooltipTimeoutRef.current = null;
                              }
                              
                              setSelectedTravel(travel);
                              const viewportWidth = window.innerWidth;
                              const viewportHeight = window.innerHeight;
                              
                              // Smart positioning to avoid going off-screen
                              let x = e.clientX + 10;
                              let y = e.clientY - 10;
                              
                              // If tooltip would go off right edge, position it to the left
                              if (x + 300 > viewportWidth) {
                                x = e.clientX - 310;
                              }
                              
                              // If tooltip would go off bottom edge, position it above
                              if (y + 200 > viewportHeight) {
                                y = e.clientY - 220;
                              }
                              
                              // If tooltip would go off top edge, position it below
                              if (y < 0) {
                                y = e.clientY + 20;
                              }
                              
                              // If tooltip would go off left edge, position it to the right
                              if (x < 0) {
                                x = 10;
                              }
                              
                              setTooltipPosition({ x, y });
                              setShowTravelTooltip(true);
                            }}
                            onMouseLeave={() => {
                              // Add delay to allow moving to tooltip
                              tooltipTimeoutRef.current = setTimeout(() => {
                                setShowTravelTooltip(false);
                                setSelectedTravel(null);
                              }, 300);
                            }}
                            title={`🧳 Travel: ${travel.destination || travel.location || 'Travel'}`}
                          />
                        );
                      });
                    }).flat()}

                    {/* Training Indicators */}
                    {days.map((day, dayIndex) => {
                      const dayTrainings = getUserTrainingForDay(user.id, day);
                      return dayTrainings.map((training, trainingIndex) => {
                        const style = createTrainingStyle(training, userIndex, dayIndex);
                        return (
                          <div
                            key={`training-${training.id}-${dayIndex}`}
                            className={style.className}
                            style={{
                              left: style.left,
                              width: style.width,
                              position: style.position,
                              top: style.top,
                              height: style.height,
                              pointerEvents: 'auto'
                            }}
                            title={`📚 Training: ${training.name || training.training_center_qualification_data?.qualification_type?.qualification_type_data?.name || training.qualification_name || 'Training'}\nCenter: ${training.training_center_qualification_data?.training_center?.name || 'Unknown'}\nDate: ${training.start_date}`}
                          />
                        );
                      });
                    }).flat()}

                    {/* Note Indicators */}
                    {days.map((day, dayIndex) => {
                      const dayNotes = getNotesForUserDate(user.id, day);
                      if (dayNotes.length === 0) return null;

                      return (
                        <div
                          key={`note-${dayIndex}`}
                          className="absolute bg-red-500 cursor-pointer hover:bg-brand-500"
                          style={{
                            left: `${dayIndex * dayWidth}px`,
                            top: '0px',
                            width: `${dayWidth}px`,
                            height: '3px',
                            zIndex: 10,
                            pointerEvents: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.target.getBoundingClientRect();
                            const x = rect.left + rect.width / 2;
                            const y = rect.top - 10;
                            setSelectedNoteData({ notes: dayNotes, user, date: day });
                            setNoteTooltipPosition({ x, y });
                            setShowNoteTooltip(true);
                          }}
                          onMouseLeave={() => {
                            // Add delay to allow moving to tooltip
                            noteTooltipTimeoutRef.current = setTimeout(() => {
                              setShowNoteTooltip(false);
                              setSelectedNoteData(null);
                            }, 300);
                          }}
                        />
                      );
                    }).filter(Boolean)}
                  </div>
                );
                })}

                {/* Car Rows - Only vehicles with equipment_type='car' */}
                {showCars && getFilteredCars.filter(car => car.equipment_type === 'car').map((car, carIndex) => (
                    <div
                      key={car.id}
                      className={`h-12 border-b border-steel-200 relative hover:bg-green-50 ${
                        car.is_rental_expiring_soon ? 'bg-orange-50 border-orange-200' : ''
                      } ${
                        highlightedRowId === `car_${car.id}`
                          ? 'bg-rose-50 border-l-4 border-l-rose-600'
                          : dropTargetCar === car.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car'
                          ? 'border-l-4 border-l-brand-400'
                          : ''
                      }`}
                      style={{ width: days.length * dayWidth, boxSizing: 'border-box' }}
                      data-car-row="true"
                      data-car-id={car.id}
                      title={car.ownership_type === 'rented' && car.is_rental_expiring_soon ? 
                        `🚨 Rental expires in ${car.rental_days_remaining} days!` : 
                        `Car: ${car.car_brand} ${car.car_model} (${car.ownership_type === 'rented' ? 'rented' : 'owned'})`}
                      onDrop={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relativeX = e.clientX - rect.left;
                        if (relativeX > 0) {
                          const dayIndex = Math.min(Math.floor(relativeX / getDayWidth), days.length - 1);
                          handleCarDrop(e, car, dayIndex);
                        }
                      }}
                      onDragOver={handleCarDragOver}
                    >
                        {/* Car assignment slots - will be implemented later */}
                        {days.map((day, dayIndex) => {
                          const isSunday = day.getDay() === 0;
                          return (
                            <div
                              key={`car-${car.id}-day-${dayIndex}`}
                              className={`absolute top-0 h-full border-r border-steel-100 ${
                                isSunday ? 'bg-red-50' : 'hover:bg-green-100'
                              } transition-colors cursor-pointer`}
                              style={{
                                left: `${dayIndex * dayWidth}px`,
                                width: `${dayWidth}px`
                              }}
                              title={`Car: ${car.car_brand} ${car.car_model} (${car.plate_number})${
                                car.is_rental_expiring_soon ? ` - 🚨 Rental expires in ${car.rental_days_remaining} days!` : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Create car assignment for this day
                                createCarAssignment(car.id, day);
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                // Create context menu data for car
                                setContextMenuData({
                                  car: car,
                                  date: day,
                                  type: 'car'
                                });
                                setContextMenuPosition({ x: e.clientX, y: e.clientY });
                                setShowTravelContextMenu(true);
                              }}
                            />
                          );
                        })}

                        {/* Car Assignment Bars */}
                        {getFilteredCarAssignments
                          .filter(assignment => assignment.carId === car.id)
                          .map(assignment => {
                            const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
                            const projectIndex = (Array.isArray(projects) ? projects : []).findIndex(p => p.id === assignment.projectId);
                            const style = getCarAssignmentStyle(assignment, projectIndex);

                            // Check if this car assignment should be visually muted
                            const isMuted = isCarAssignmentMuted(assignment);

                            if (style.display === 'none') return null;

                            const startDate = new Date(assignment.startDate);
                            const endDate = new Date(assignment.endDate);
                            const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

                            // Get ALL users assigned to this car in overlapping date ranges
                            const overlappingAssignments = getFilteredCarAssignments.filter(a => {
                              if (a.carId !== assignment.carId || a.projectId !== assignment.projectId) return false;
                              const aStart = new Date(a.startDate);
                              const aEnd = new Date(a.endDate);
                              // Check if date ranges overlap
                              return (aStart <= endDate && aEnd >= startDate);
                            });

                            const assignedUsers = overlappingAssignments
                              .map(a => a.userId ? (Array.isArray(users) ? users : []).find(u => u.id === a.userId) : null)
                              .filter(u => u !== null);

                            const uniqueUsers = Array.from(new Map(assignedUsers.map(u => [u.id, u])).values());

                            const userInfo = uniqueUsers.length > 0
                              ? `\nUsers: ${uniqueUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ')}`
                              : '\nUsers: Not assigned';

                            const titleText = `Car: ${car.plate_number} | Project: ${project?.name || 'Unknown'}${isMuted ? ' (Other allocation)' : ''}${userInfo}\n${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()} (${duration} days)`;

                            return (
                              <div
                                key={`car-assignment-${assignment.id}`}
                                data-assignment-id={assignment.id}
                                data-original-width={style.width}
                                data-is-muted={isMuted}
                                className={`${style.className} group ${
                                  assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car'
                                    ? 'opacity-30'
                                    : ''
                                } ${isMuted ? 'ring-1 ring-dashed ring-slate-400' : ''}`}
                                title={titleText}
                                onMouseDown={(e) => {
                                  // Ignore right-click (context menu)
                                  if (e.button !== 0) {
                                    return;
                                  }

                                  // Check if clicking on delete button, resize handle, or move handle
                                  const deleteBtn = e.target.closest('.delete-btn');
                                  const resizeHandle = e.target.closest('.resize-handle');
                                  const moveHandle = e.target.closest('.move-handle');

                                  if (deleteBtn || resizeHandle) {
                                    return; // Let these handles work normally
                                  }

                                  if (moveHandle) {
                                    // For now, car assignments can't move between cars, but we can implement timeline drag
                                    handleMouseDown(e, assignment, 'drag');
                                    return;
                                  }

                                  // Default drag behavior (timeline drag)
                                  handleMouseDown(e, assignment, 'drag');
                                }}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Open double-click modal on right-click
                                  handleCarAssignmentDoubleClick(e, assignment);
                                }}
                                onDoubleClick={(e) => handleCarAssignmentDoubleClick(e, assignment)}
                                style={{
                                  left: style.left,
                                  width: style.width,
                                  height: style.height,
                                  top: style.top,
                                  position: style.position,
                                  pointerEvents: assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car' ? 'none' : 'auto',
                                  // Muted car assignments: reduced opacity and grayscale filter
                                  opacity: isMuted ? 0.4 : 1,
                                  filter: isMuted ? 'grayscale(50%) saturate(60%)' : 'none',
                                  ...style.style
                                }}
                              >
                                {/* Content */}
                                <div className="truncate px-1 pl-2 pr-6 pointer-events-none">
                                  {project?.name || 'Project'}
                                </div>
                                
                                {/* Delete button */}
                                <button
                                  className="delete-btn absolute right-1 top-1/2 transform -translate-y-1/2 text-white hover:text-red-200 opacity-0 group-hover:opacity-100 font-bold text-xs leading-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCarAssignmentToDelete(assignment);
                                    setShowCarDeleteConfirm(true);
                                  }}
                                  style={{ zIndex: 25 }}
                                  title="Remove car assignment"
                                >
                                  ×
                                </button>
                                
                                {/* Left resize handle */}
                                <div
                                  className="resize-handle absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 group-hover:opacity-100 bg-steel-600 rounded-l"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, assignment, 'resize', 'left');
                                  }}
                                  style={{ zIndex: 15, touchAction: 'none', willChange: 'transform' }}
                                />

                                {/* Move handle - vertical drag between cars */}
                                <div
                                  className="move-handle absolute left-1 top-1/2 transform -translate-y-1/2 opacity-80 group-hover:opacity-100 cursor-move"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    console.log('Car grip clicked - starting vertical drag');
                                    handleMouseDown(e, assignment, 'move-car');
                                  }}
                                  style={{ zIndex: 25, touchAction: 'none', willChange: 'transform' }}
                                  title="Drag to move to another car (or hold ALT to copy)"
                                >
                                  <GripVertical className="h-4 w-4 text-white drop-shadow-lg" strokeWidth={3} />
                                </div>

                                {/* Right resize handle */}
                                <div
                                  className="resize-handle absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 group-hover:opacity-100 bg-steel-600 rounded-r"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, assignment, 'resize', 'right');
                                  }}
                                  style={{ zIndex: 15, touchAction: 'none', willChange: 'transform' }}
                                />
                              </div>
                            );
                          })}

                        {/* Car Travel Indicators */}
                        {days.map((day, dayIndex) => {
                          const dayTravels = getCarTravelForDay(car.id, day);

                          return dayTravels.map((travel, travelIndex) => {
                            const style = createTravelStyle(travel, 0, dayIndex, travelIndex);

                            return (
                              <div
                                key={`car-travel-${travel.id}-${dayIndex}`}
                                className={style.className}
                                style={{
                                  left: style.left,
                                  width: style.width,
                                  position: style.position,
                                  top: style.top,
                                  height: style.height,
                                  pointerEvents: 'auto'
                                }}
                                onMouseEnter={(e) => {
                                  // Clear any existing timeout
                                  if (tooltipTimeoutRef.current) {
                                    clearTimeout(tooltipTimeoutRef.current);
                                    tooltipTimeoutRef.current = null;
                                  }

                                  setSelectedTravel(travel);
                                  const viewportWidth = window.innerWidth;
                                  const viewportHeight = window.innerHeight;

                                  // Smart positioning to avoid going off-screen
                                  let x = e.clientX + 10;
                                  let y = e.clientY - 10;

                                  // If tooltip would go off right edge, position it to the left
                                  if (x + 300 > viewportWidth) {
                                    x = e.clientX - 310;
                                  }

                                  // If tooltip would go off bottom edge, position it above
                                  if (y + 200 > viewportHeight) {
                                    y = e.clientY - 220;
                                  }

                                  // If tooltip would go off top edge, position it below
                                  if (y < 0) {
                                    y = e.clientY + 20;
                                  }

                                  // If tooltip would go off left edge, position it to the right
                                  if (x < 0) {
                                    x = 10;
                                  }

                                  setTooltipPosition({ x, y });
                                  setShowTravelTooltip(true);
                                }}
                                onMouseLeave={() => {
                                  // Add delay to allow moving to tooltip
                                  tooltipTimeoutRef.current = setTimeout(() => {
                                    setShowTravelTooltip(false);
                                    setSelectedTravel(null);
                                  }, 300);
                                }}
                                title={`${travel.travel_type === 'check_in' ? 'Check-in' : travel.travel_type === 'check_out' ? 'Check-out' : 'Travel'} - ${car.car_brand} ${car.car_model}`}
                              />
                            );
                          });
                        })}
                    </div>
                  ))}

                {/* Other Equipment Rows - Machines, lifts, lighting towers, cherry pickers, etc. */}
                {showEquipment && getFilteredCars.filter(car => car.equipment_type !== 'car').map((car, carIndex) => (
                    <div
                      key={car.id}
                      className={`h-12 border-b border-steel-200 relative hover:bg-green-50 ${
                        car.is_rental_expiring_soon ? 'bg-orange-50 border-orange-200' : ''
                      } ${
                        highlightedRowId === `car_${car.id}`
                          ? 'bg-rose-50 border-l-4 border-l-rose-600'
                          : dropTargetCar === car.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car'
                          ? 'border-l-4 border-l-brand-400'
                          : ''
                      }`}
                      style={{ width: days.length * dayWidth, boxSizing: 'border-box' }}
                      data-car-row="true"
                      data-car-id={car.id}
                      title={car.ownership_type === 'rented' && car.is_rental_expiring_soon ?
                        `🚨 Rental expires in ${car.rental_days_remaining} days!` :
                        `Equipment: ${car.car_brand} ${car.car_model} (${car.ownership_type === 'rented' ? 'rented' : 'owned'})`}
                      onDrop={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relativeX = e.clientX - rect.left;
                        if (relativeX > 0) {
                          const dayIndex = Math.min(Math.floor(relativeX / getDayWidth), days.length - 1);
                          handleCarDrop(e, car, dayIndex);
                        }
                      }}
                      onDragOver={handleCarDragOver}
                    >
                        {/* Car assignment slots - will be implemented later */}
                        {days.map((day, dayIndex) => {
                          const isSunday = day.getDay() === 0;
                          return (
                            <div
                              key={`car-${car.id}-day-${dayIndex}`}
                              className={`absolute top-0 h-full border-r border-steel-100 ${
                                isSunday ? 'bg-red-50' : 'hover:bg-green-100'
                              } transition-colors cursor-pointer`}
                              style={{
                                left: `${dayIndex * dayWidth}px`,
                                width: `${dayWidth}px`
                              }}
                              title={`Equipment: ${car.car_brand} ${car.car_model} (${car.plate_number})${
                                car.is_rental_expiring_soon ? ` - 🚨 Rental expires in ${car.rental_days_remaining} days!` : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Create car assignment for this day
                                createCarAssignment(car.id, day);
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                // Create context menu data for car
                                setContextMenuData({
                                  car: car,
                                  date: day,
                                  type: 'car'
                                });
                                setContextMenuPosition({ x: e.clientX, y: e.clientY });
                                setShowTravelContextMenu(true);
                              }}
                            />
                          );
                        })}

                        {/* Car Assignment Bars */}
                        {getFilteredCarAssignments
                          .filter(assignment => assignment.carId === car.id)
                          .map(assignment => {
                            const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
                            const projectIndex = (Array.isArray(projects) ? projects : []).findIndex(p => p.id === assignment.projectId);
                            const style = getCarAssignmentStyle(assignment, projectIndex);

                            // Check if this car assignment should be visually muted
                            const isMuted = isCarAssignmentMuted(assignment);

                            if (style.display === 'none') return null;

                            const startDate = new Date(assignment.startDate);
                            const endDate = new Date(assignment.endDate);
                            const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

                            // Get ALL users assigned to this car in overlapping date ranges
                            const overlappingAssignments = getFilteredCarAssignments.filter(a => {
                              if (a.carId !== assignment.carId || a.projectId !== assignment.projectId) return false;
                              const aStart = new Date(a.startDate);
                              const aEnd = new Date(a.endDate);
                              // Check if date ranges overlap
                              return (aStart <= endDate && aEnd >= startDate);
                            });

                            const assignedUsers = overlappingAssignments
                              .map(a => a.userId ? (Array.isArray(users) ? users : []).find(u => u.id === a.userId) : null)
                              .filter(u => u !== null);

                            const uniqueUsers = Array.from(new Map(assignedUsers.map(u => [u.id, u])).values());

                            const userInfo = uniqueUsers.length > 0
                              ? `\nUsers: ${uniqueUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ')}`
                              : '\nUsers: Not assigned';

                            const titleText = `Equipment: ${car.plate_number} | Project: ${project?.name || 'Unknown'}${isMuted ? ' (Other allocation)' : ''}${userInfo}\n${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()} (${duration} days)`;

                            return (
                              <div
                                key={`car-assignment-${assignment.id}`}
                                data-assignment-id={assignment.id}
                                data-original-width={style.width}
                                data-is-muted={isMuted}
                                className={`${style.className} group ${
                                  assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car'
                                    ? 'opacity-30'
                                    : ''
                                } ${isMuted ? 'ring-1 ring-dashed ring-slate-400' : ''}`}
                                title={titleText}
                                onMouseDown={(e) => {
                                  // Check if clicking on delete button, resize handle, or move handle
                                  const deleteBtn = e.target.closest('.delete-btn');
                                  const resizeHandle = e.target.closest('.resize-handle');
                                  const moveHandle = e.target.closest('.move-handle');

                                  if (deleteBtn || resizeHandle) {
                                    return; // Let these handles work normally
                                  }

                                  if (moveHandle) {
                                    // For now, car assignments can't move between cars, but we can implement timeline drag
                                    console.log('Car assignment move handle clicked');
                                  }
                                }}
                                style={{
                                  left: style.left,
                                  width: style.width,
                                  position: style.position,
                                  top: style.top,
                                  height: style.height,
                                  pointerEvents: assignmentDragState?.assignment.id === assignment.id && assignmentDragState?.isDragging && assignmentDragState?.type === 'car' ? 'none' : 'auto',
                                  // Muted car assignments: reduced opacity and grayscale filter
                                  opacity: isMuted ? 0.4 : 1,
                                  filter: isMuted ? 'grayscale(50%) saturate(60%)' : 'none',
                                  ...style.style
                                }}
                              >
                                {/* Assignment content */}
                                <div className="truncate px-1 pl-2 pr-6 pointer-events-none">
                                  {project?.name || 'Project'}
                                </div>

                                {/* Delete button */}
                                <button
                                  className="delete-btn absolute right-1 top-1/2 transform -translate-y-1/2 text-white hover:text-red-200 opacity-0 group-hover:opacity-100 font-bold text-xs leading-none"
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    if (window.confirm(`Are you sure you want to remove this equipment assignment?`)) {
                                      try {
                                        // Delete from backend
                                        await resourceBoardService.deleteVehicleAssignment(assignment.id);

                                        // Remove from local state
                                        setCarAssignments(prev => prev.filter(a => a.id !== assignment.id));
                                      } catch (error) {
                                        logger.error('Error deleting equipment assignment:', error);
                                        alert('Failed to delete equipment assignment. Please try again.');
                                      }
                                    }
                                  }}
                                  style={{ zIndex: 25 }}
                                  title="Remove equipment assignment"
                                >
                                  ×
                                </button>

                                {/* Left resize handle */}
                                <div
                                  className="resize-handle absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 group-hover:opacity-100 bg-steel-600 rounded-l"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, assignment, 'resize', 'left');
                                  }}
                                  style={{ zIndex: 15, touchAction: 'none', willChange: 'transform' }}
                                />

                                {/* Move handle - vertical drag between equipment */}
                                <div
                                  className="move-handle absolute left-1 top-1/2 transform -translate-y-1/2 opacity-80 group-hover:opacity-100 cursor-move"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    console.log('Equipment grip clicked - starting vertical drag');
                                    handleMouseDown(e, assignment, 'move-car');
                                  }}
                                  style={{ zIndex: 25, touchAction: 'none', willChange: 'transform' }}
                                  title="Drag to move to another equipment (or hold ALT to copy)"
                                >
                                  <GripVertical className="h-4 w-4 text-white drop-shadow-lg" strokeWidth={3} />
                                </div>

                                {/* Right resize handle */}
                                <div
                                  className="resize-handle absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-30 group-hover:opacity-100 bg-steel-600 rounded-r"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleMouseDown(e, assignment, 'resize', 'right');
                                  }}
                                  style={{ zIndex: 15, touchAction: 'none', willChange: 'transform' }}
                                />
                              </div>
                            );
                          })}

                        {/* Car Travel Indicators */}
                        {days.map((day, dayIndex) => {
                          const dayTravels = getCarTravelForDay(car.id, day);

                          return dayTravels.map((travel, travelIndex) => {
                            const style = createTravelStyle(travel, 0, dayIndex, travelIndex);

                            return (
                              <div
                                key={`car-travel-${travel.id}-${dayIndex}`}
                                className={style.className}
                                style={{
                                  left: style.left,
                                  width: style.width,
                                  position: style.position,
                                  top: style.top,
                                  height: style.height,
                                  pointerEvents: 'auto'
                                }}
                                onMouseEnter={(e) => {
                                  // Clear any existing timeout
                                  if (tooltipTimeoutRef.current) {
                                    clearTimeout(tooltipTimeoutRef.current);
                                  }

                                  setSelectedTravel(travel);

                                  // Position tooltip at cursor
                                  const x = e.clientX + 10;
                                  const y = e.clientY - 50;

                                  // Check if tooltip would go off screen
                                  const tooltipWidth = 350;
                                  const tooltipHeight = 200;
                                  const windowWidth = window.innerWidth;
                                  const windowHeight = window.innerHeight;

                                  let adjustedX = x;
                                  let adjustedY = y;

                                  if (x + tooltipWidth > windowWidth) {
                                    adjustedX = e.clientX - tooltipWidth - 10;
                                  }

                                  if (y + tooltipHeight > windowHeight) {
                                    adjustedY = e.clientY - tooltipHeight + 50;
                                  }

                                  setTooltipPosition({ x, y });
                                  setShowTravelTooltip(true);
                                }}
                                onMouseLeave={() => {
                                  // Add delay to allow moving to tooltip
                                  tooltipTimeoutRef.current = setTimeout(() => {
                                    setShowTravelTooltip(false);
                                    setSelectedTravel(null);
                                  }, 300);
                                }}
                                title={`${travel.travel_type === 'check_in' ? 'Check-in' : travel.travel_type === 'check_out' ? 'Check-out' : 'Travel'} - ${car.car_brand} ${car.car_model}`}
                              />
                            );
                          });
                        })}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Assignment Modal */}
      {isNewAssignmentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900">New Assignment</h3>
              <button 
                onClick={() => setIsNewAssignmentModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Project</label>
                <select className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500">
                  <option value="">Select a project...</option>
                  {(Array.isArray(projects) ? projects : [])
                    .filter(project => project.is_active !== false)
                    .map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Person</label>
                <select className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500">
                  <option value="">Select a person...</option>
                  {(Array.isArray(users) ? users : []).map(user => (
                    <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                <input 
                  type="text" 
                  placeholder="e.g. Lead Engineer, Safety Officer..."
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-steel-200">
              <button 
                onClick={() => setIsNewAssignmentModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-steel-300 rounded hover:bg-steel-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 text-sm bg-brand-500 text-white rounded hover:bg-brand-600">
                Create Assignment
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Assignment Actions Modal */}
      <AssignmentActionsModal
        showAssignmentActions={showAssignmentActions}
        selectedAssignment={selectedAssignment}
        editingComment={editingComment}
        commentInput={commentInput}
        setCommentInput={setCommentInput}
        saveAssignmentComment={saveAssignmentComment}
        setEditingComment={setEditingComment}
        selectedCopyUsers={selectedCopyUsers}
        setSelectedCopyUsers={setSelectedCopyUsers}
        userSearchQuery={userSearchQuery}
        setUserSearchQuery={setUserSearchQuery}
        assignmentActionType={assignmentActionType}
        setAssignmentActionType={setAssignmentActionType}
        copyAssignmentToUsers={copyAssignmentToUsers}
        cancelCommentEdit={cancelCommentEdit}
        projects={projects}
        users={users}
        userRoles={userRoles}
        getFilteredUsersForCopy={getFilteredUsersForCopy}
        toggleUserSelection={toggleUserSelection}
        handleDeleteAssignment={handleDeleteAssignment}
        copyStartDate={copyStartDate}
        setCopyStartDate={setCopyStartDate}
        copyEndDate={copyEndDate}
        setCopyEndDate={setCopyEndDate}
      />

      {/* Quick Assignment Modal */}
      {showQuickAssignmentModal && quickAssignmentData && (
        <QuickAssignmentModal
          isOpen={showQuickAssignmentModal}
          onClose={() => {
            setShowQuickAssignmentModal(false);
            setQuickAssignmentData(null);
          }}
          onSubmit={handleQuickAssignmentSubmit}
          projects={(Array.isArray(projects) ? projects : []).filter(p => p.is_active !== false)}
          startDate={quickAssignmentData.date}
          userName={quickAssignmentData.userName}
        />
      )}

      {/* PDF Extract Modal */}
      {showPdfExtractModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50">
              <h2 className="text-xl font-black text-slate-900 flex items-center uppercase tracking-wide">
                <FileText className="w-6 h-6 mr-2 text-brand-600" />
                PDF Extract - Resource Plan
              </h2>
              <button
                onClick={closePdfExtractModal}
                className="p-1.5 text-slate-500 hover:text-white hover:bg-brand-500 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">

            {/* Form - Two Column Layout */}
            <div className="grid grid-cols-2 gap-6">
              {/* LEFT COLUMN */}
              <div className="space-y-4">
                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Projects (optional - select multiple or none for all)
                  </label>
                  {/* Search Input */}
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={pdfExtractConfig.projectSearchQuery}
                    onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, projectSearchQuery: e.target.value }))}
                    className="w-full px-3 py-2 mb-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                  />
                  <div className="border border-steel-300 rounded-xl max-h-48 overflow-y-auto p-2 space-y-2">
                    {(Array.isArray(projects) ? projects : [])
                      .filter(p => p.is_active !== false)
                      .filter(p => {
                        // Filter by search query
                        if (!pdfExtractConfig.projectSearchQuery) return true;
                        return (p.name || '').toLowerCase().includes(pdfExtractConfig.projectSearchQuery.toLowerCase());
                      })
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(project => (
                        <label key={project.id} className="flex items-center space-x-2 hover:bg-steel-50 p-2 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.includes(project.id)}
                            onChange={(e) => {
                              setPdfExtractConfig(prev => ({
                                ...prev,
                                projectIds: e.target.checked
                                  ? [...(prev.projectIds || []), project.id]
                                  : (prev.projectIds || []).filter(id => id !== project.id)
                              }));
                            }}
                            className="w-4 h-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                          />
                          <span className="text-sm text-slate-900">{project.name}</span>
                        </label>
                      ))}
                  </div>
                  {Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 0 && (
                    <div className="mt-2 text-xs text-slate-600">
                      {pdfExtractConfig.projectIds.length} project{pdfExtractConfig.projectIds.length > 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>

                {/* Multi-Project Layout (only show when multiple projects selected) */}
                {Array.isArray(pdfExtractConfig.projectIds) && pdfExtractConfig.projectIds.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Layout for Multiple Projects
                    </label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPdfExtractConfig(prev => ({ ...prev, multiProjectLayout: 'by-name' }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                          pdfExtractConfig.multiProjectLayout === 'by-name'
                            ? 'bg-brand-500 text-white'
                            : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                        }`}
                      >
                        By Name (Alphabetically)
                      </button>
                      <button
                        onClick={() => setPdfExtractConfig(prev => ({ ...prev, multiProjectLayout: 'by-project' }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                          pdfExtractConfig.multiProjectLayout === 'by-project'
                            ? 'bg-brand-500 text-white'
                            : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                        }`}
                      >
                        Project by Project
                      </button>
                    </div>
                  </div>
                )}

                {/* Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Period Selection Mode
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPdfExtractConfig(prev => ({ ...prev, mode: 'weeks' }))}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                        pdfExtractConfig.mode === 'weeks'
                          ? 'bg-brand-500 text-white'
                          : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                      }`}
                    >
                      By Weeks
                    </button>
                    <button
                      onClick={() => setPdfExtractConfig(prev => ({ ...prev, mode: 'dates' }))}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                        pdfExtractConfig.mode === 'dates'
                          ? 'bg-brand-500 text-white'
                          : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                      }`}
                    >
                      By Dates
                    </button>
                  </div>
                </div>

              {/* Week Range (if mode is 'weeks') */}
              {pdfExtractConfig.mode === 'weeks' && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Week */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Start Week *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Year dropdown */}
                      <select
                        value={pdfExtractConfig.startYear}
                        onChange={(e) => {
                          const year = e.target.value;
                          const weekNum = pdfExtractConfig.startWeekNumber;
                          const startWeek = year && weekNum ? `${year}-W${String(weekNum).padStart(2, '0')}` : '';

                          // Auto-calculate end week
                          let endWeek = '';
                          let endYear = '';
                          let endWeekNum = '';
                          if (startWeek) {
                            const weekNumber = parseInt(weekNum);
                            if (weekNumber < 52) {
                              endWeekNum = weekNumber + 1;
                              endYear = year;
                            } else {
                              endWeekNum = 1;
                              endYear = String(parseInt(year) + 1);
                            }
                            endWeek = `${endYear}-W${String(endWeekNum).padStart(2, '0')}`;
                          }

                          setPdfExtractConfig(prev => ({
                            ...prev,
                            startYear: year,
                            startWeek: startWeek,
                            endWeek: endWeek,
                            endYear: endYear,
                            endWeekNumber: endWeekNum
                          }));
                        }}
                        className="px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        required
                      >
                        <option value="">Year...</option>
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          return [currentYear, currentYear + 1].map(year => (
                            <option key={year} value={year}>{year}</option>
                          ));
                        })()}
                      </select>

                      {/* Week number dropdown */}
                      <select
                        value={pdfExtractConfig.startWeekNumber}
                        onChange={(e) => {
                          const weekNum = e.target.value;
                          const year = pdfExtractConfig.startYear;
                          const startWeek = year && weekNum ? `${year}-W${String(weekNum).padStart(2, '0')}` : '';

                          // Auto-calculate end week
                          let endWeek = '';
                          let endYear = '';
                          let endWeekNum = '';
                          if (startWeek) {
                            const weekNumber = parseInt(weekNum);
                            if (weekNumber < 52) {
                              endWeekNum = weekNumber + 1;
                              endYear = year;
                            } else {
                              endWeekNum = 1;
                              endYear = String(parseInt(year) + 1);
                            }
                            endWeek = `${endYear}-W${String(endWeekNum).padStart(2, '0')}`;
                          }

                          setPdfExtractConfig(prev => ({
                            ...prev,
                            startWeekNumber: weekNum,
                            startWeek: startWeek,
                            endWeek: endWeek,
                            endYear: endYear,
                            endWeekNumber: endWeekNum
                          }));
                        }}
                        disabled={!pdfExtractConfig.startYear}
                        className="px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-steel-100 disabled:cursor-not-allowed"
                        required
                      >
                        <option value="">Week...</option>
                        {Array.from({ length: 52 }, (_, i) => i + 1).map(week => (
                          <option key={week} value={week}>Week {week}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* End Week */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      End Week *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Year dropdown */}
                      <select
                        value={pdfExtractConfig.endYear}
                        onChange={(e) => {
                          const year = e.target.value;
                          const weekNum = pdfExtractConfig.endWeekNumber;
                          const endWeek = year && weekNum ? `${year}-W${String(weekNum).padStart(2, '0')}` : '';

                          setPdfExtractConfig(prev => ({
                            ...prev,
                            endYear: year,
                            endWeek: endWeek
                          }));
                        }}
                        className="px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        required
                      >
                        <option value="">Year...</option>
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          return [currentYear, currentYear + 1].map(year => (
                            <option key={year} value={year}>{year}</option>
                          ));
                        })()}
                      </select>

                      {/* Week number dropdown */}
                      <select
                        value={pdfExtractConfig.endWeekNumber}
                        onChange={(e) => {
                          const weekNum = e.target.value;
                          const year = pdfExtractConfig.endYear;
                          const endWeek = year && weekNum ? `${year}-W${String(weekNum).padStart(2, '0')}` : '';

                          setPdfExtractConfig(prev => ({
                            ...prev,
                            endWeekNumber: weekNum,
                            endWeek: endWeek
                          }));
                        }}
                        disabled={!pdfExtractConfig.endYear}
                        className="px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-steel-100 disabled:cursor-not-allowed"
                        required
                      >
                        <option value="">Week...</option>
                        {Array.from({ length: 52 }, (_, i) => i + 1).map(week => (
                          <option key={week} value={week}>Week {week}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Date Range (if mode is 'dates') */}
              {pdfExtractConfig.mode === 'dates' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={pdfExtractConfig.startDate}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={pdfExtractConfig.endDate}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      required
                    />
                  </div>
                </div>
              )}

                {/* Include Options */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Include in PDF
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={pdfExtractConfig.includeUsers}
                        onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, includeUsers: e.target.checked }))}
                        className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-slate-700">People</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={pdfExtractConfig.includeCars}
                        onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, includeCars: e.target.checked }))}
                        className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-slate-700">Cars & Equipment</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-4">
                {/* User Selection (only when includeUsers is checked) */}
              {pdfExtractConfig.includeUsers && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-slate-700">
                      Select Users (optional)
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          const filteredUsers = users
                            .filter(u => u.is_active !== false)
                            .filter(u => {
                              if (!pdfExtractConfig.userSearchQuery) return true;
                              const searchLower = pdfExtractConfig.userSearchQuery.toLowerCase();
                              const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                              return fullName.includes(searchLower);
                            });
                          const allUserIds = filteredUsers.map(u => u.id);
                          setPdfExtractConfig(prev => ({ ...prev, selectedUserIds: allUserIds }));
                        }}
                        className="text-xs text-brand-600 hover:text-brand-800"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setPdfExtractConfig(prev => ({ ...prev, selectedUserIds: [] }))}
                        className="text-xs text-slate-600 hover:text-slate-800"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Search Input */}
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={pdfExtractConfig.userSearchQuery}
                    onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, userSearchQuery: e.target.value }))}
                    className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                  />

                  {/* User List with Checkboxes */}
                  <div className="border border-steel-300 rounded-xl max-h-60 overflow-y-auto">
                    {users
                      .filter(u => u.is_active !== false)
                      .filter(u => {
                        if (!pdfExtractConfig.userSearchQuery) return true;
                        const searchLower = pdfExtractConfig.userSearchQuery.toLowerCase();
                        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
                        return fullName.includes(searchLower);
                      })
                      .sort((a, b) => {
                        const nameA = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
                        const nameB = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map(user => (
                        <label
                          key={user.id}
                          className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer border-b border-steel-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={pdfExtractConfig.selectedUserIds.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPdfExtractConfig(prev => ({
                                  ...prev,
                                  selectedUserIds: [...prev.selectedUserIds, user.id]
                                }));
                              } else {
                                setPdfExtractConfig(prev => ({
                                  ...prev,
                                  selectedUserIds: prev.selectedUserIds.filter(id => id !== user.id)
                                }));
                              }
                            }}
                            className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                          />
                          <span className="ml-2 text-sm text-slate-700">
                            {user.last_name} {user.first_name}
                          </span>
                        </label>
                      ))}
                  </div>

                  <p className="text-xs text-slate-500">
                    {pdfExtractConfig.selectedUserIds.length === 0
                      ? 'All users will be included'
                      : `${pdfExtractConfig.selectedUserIds.length} user(s) selected`}
                  </p>

                  {/* Email option - show when users are selected */}
                  {pdfExtractConfig.selectedUserIds.length > 0 && (
                    <div className="mt-3 p-3 bg-brand-50 border border-brand-200 rounded-xl">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={pdfExtractConfig.sendEmail}
                          onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, sendEmail: e.target.checked }))}
                          className="h-4 w-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                        />
                        <span className="ml-2 text-sm text-slate-700">
                          Send PDF via email to selected user{pdfExtractConfig.selectedUserIds.length > 1 ? 's' : ''}
                        </span>
                      </label>
                      {pdfExtractConfig.selectedUserIds.length === 1 ? (
                        <p className="text-xs text-slate-600 mt-1 ml-6">
                          PDF will be sent to: {(() => {
                            const user = users.find(u => u.id === pdfExtractConfig.selectedUserIds[0]);
                            return user?.company_email || user?.email || 'N/A';
                          })()}
                        </p>
                      ) : (
                        <div className="text-xs text-slate-600 mt-1 ml-6">
                          <p className="font-medium">Individual PDFs will be sent to {pdfExtractConfig.selectedUserIds.length} users:</p>
                          <div className="mt-1 max-h-20 overflow-y-auto">
                            {pdfExtractConfig.selectedUserIds.slice(0, 5).map(userId => {
                              const user = users.find(u => u.id === userId);
                              const userEmail = user?.company_email || user?.email;
                              return user ? (
                                <div key={userId} className="flex items-center text-[10px]">
                                  <span className={`w-2 h-2 rounded-full mr-1 ${userEmail ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                  <span>{user.first_name} {user.last_name}</span>
                                  <span className="text-slate-400 ml-1">({userEmail || 'no email'})</span>
                                </div>
                              ) : null;
                            })}
                            {pdfExtractConfig.selectedUserIds.length > 5 && (
                              <div className="text-slate-400">...and {pdfExtractConfig.selectedUserIds.length - 5} more</div>
                            )}
                          </div>
                          {(() => {
                            const usersWithoutEmail = pdfExtractConfig.selectedUserIds.filter(id => {
                              const user = users.find(u => u.id === id);
                              return !user?.company_email && !user?.email;
                            }).length;
                            return usersWithoutEmail > 0 ? (
                              <p className="text-orange-600 mt-1">
                                ⚠️ {usersWithoutEmail} user(s) have no email and will be skipped
                              </p>
                            ) : null;
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sort Options */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sort By
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sortBy"
                      value="alphabet"
                      checked={pdfExtractConfig.sortBy === 'alphabet'}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, sortBy: e.target.value }))}
                      className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Alphabet (Surname, First Name)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="sortBy"
                      value="date"
                      checked={pdfExtractConfig.sortBy === 'date'}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, sortBy: e.target.value }))}
                      className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Date (Start Date)</span>
                  </label>
                </div>
              </div>

              {/* Orientation Options */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Page Orientation
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="orientation"
                      value="portrait"
                      checked={pdfExtractConfig.orientation === 'portrait'}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, orientation: e.target.value }))}
                      className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Portrait (Vertical)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="orientation"
                      value="landscape"
                      checked={pdfExtractConfig.orientation === 'landscape'}
                      onChange={(e) => setPdfExtractConfig(prev => ({ ...prev, orientation: e.target.value }))}
                      className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Landscape (Horizontal)</span>
                  </label>
                </div>
              </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closePdfExtractModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 hover:bg-steel-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate based on mode
                  if (pdfExtractConfig.mode === 'weeks') {
                    if (!pdfExtractConfig.startWeek || !pdfExtractConfig.endWeek) {
                      alert('Please select start and end weeks');
                      return;
                    }
                  } else {
                    if (!pdfExtractConfig.startDate || !pdfExtractConfig.endDate) {
                      alert('Please select start and end dates');
                      return;
                    }
                  }

                  // Generate PDF
                  await generatePDF();

                  // Reset config and close modal
                  setPdfExtractConfig({
                    projectId: '',
                    mode: 'weeks',
                    startWeek: '',
                    endWeek: '',
                    startDate: '',
                    endDate: '',
                    includeUsers: true,
                    includeCars: false,
                    sortBy: 'alphabet',
                    orientation: 'portrait',
                    selectedUserIds: [],
                    sendEmail: false,
                    userSearchQuery: '' // Clear search query
                  });
                  setShowPdfExtractModal(false);
                }}
                className="px-4 py-2 text-sm font-bold text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-colors flex items-center shadow-lg shadow-brand-500/20"
              >
                <FileText className="w-4 w-4 mr-2" />
                {pdfExtractConfig.sendEmail ? 'Generate & Send PDF' : 'Generate PDF'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Car Project Assignment Modal */}
      <CarProjectModal
        showModal={showCarProjectModal}
        carAssignmentData={carAssignmentData}
        selectedCarProject={selectedCarProject}
        selectedCarUser={selectedCarUser}
        carsData={carsData}
        users={users}
        projects={projects}
        onClose={() => {
          setShowCarProjectModal(false);
          setCarAssignmentData({ carId: null, date: null });
          setSelectedCarProject('');
          setSelectedCarUser('');
        }}
        onProjectChange={setSelectedCarProject}
        onUserChange={setSelectedCarUser}
        onAssign={(newAssignment) => {
          setCarAssignments(prev => [...prev, newAssignment]);
          setSavedCarAssignments(prev => [...prev, newAssignment]);
        }}
      />

      {/* Car Assignment Date Edit Modal */}
      {showCarAssignmentEdit && selectedCarAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Edit Car Assignment Dates
              </h3>
              <button
                onClick={() => {
                  setShowCarAssignmentEdit(false);
                  setSelectedCarAssignment(null);
                  setOriginalCarAssignmentDates(null);
                  setEditCarAssignmentUsers([]);
                  setEditCarUserSearchQuery('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-slate-600 mb-2">
                  Car: <span className="font-medium">
                    {(() => {
                      const car = (Array.isArray(carsData) ? carsData : []).find(c => c.id === selectedCarAssignment.carId);
                      return `${car?.car_brand} ${car?.car_model} (${car?.plate_number})`;
                    })()}
                  </span>
                </div>
                <div className="text-sm text-slate-600 mb-4">
                  Project: <span className="font-medium">
                    {(() => {
                      const project = (Array.isArray(projects) ? projects : []).find(p => p.id === selectedCarAssignment.projectId);
                      return project?.name || 'Unknown Project';
                    })()}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formatDateForAPI(selectedCarAssignment.startDate)}
                  onChange={(e) => {
                    const newStartDate = new Date(e.target.value);
                    setSelectedCarAssignment(prev => ({
                      ...prev,
                      startDate: newStartDate
                    }));
                  }}
                  className="w-full p-3 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={formatDateForAPI(selectedCarAssignment.endDate)}
                  onChange={(e) => {
                    const newEndDate = new Date(e.target.value);
                    setSelectedCarAssignment(prev => ({
                      ...prev,
                      endDate: newEndDate
                    }));
                  }}
                  className="w-full p-3 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Assigned Users <span className="text-slate-500 text-xs">(Optional - Select multiple)</span>
                </label>

                {/* Search Input */}
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search users by name..."
                      value={editCarUserSearchQuery}
                      onChange={(e) => setEditCarUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* User Multi-Selection */}
                <div className="border border-steel-300 rounded-xl max-h-48 overflow-y-auto">
                  {users
                    .filter(user => {
                      // Filter by search query
                      if (!editCarUserSearchQuery) return true;
                      const searchLower = editCarUserSearchQuery.toLowerCase();
                      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
                      return fullName.includes(searchLower);
                    })
                    .map(user => {
                      const isSelected = editCarAssignmentUsers.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          onClick={() => {
                            if (isSelected) {
                              setEditCarAssignmentUsers(prev => prev.filter(id => id !== user.id));
                            } else {
                              setEditCarAssignmentUsers(prev => [...prev, user.id]);
                            }
                          }}
                          className={`w-full text-left p-3 border-b border-steel-200 last:border-b-0 transition-all ${
                            isSelected
                              ? 'bg-brand-50'
                              : 'hover:bg-steel-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="w-4 h-4 text-brand-600 border-steel-300 rounded focus:ring-brand-500"
                            />
                            <div>
                              <div className="font-medium text-slate-900">
                                {user.first_name} {user.last_name}
                              </div>
                              {user.role && (
                                <div className="text-xs text-slate-600">{user.role}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>

                {editCarAssignmentUsers.length > 0 && (
                  <div className="mt-2 text-sm text-slate-600">
                    Selected: {editCarAssignmentUsers.length} {editCarAssignmentUsers.length === 1 ? 'user' : 'users'}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCarAssignmentEdit(false);
                    setSelectedCarAssignment(null);
                    setOriginalCarAssignmentDates(null);
                    setEditCarAssignmentUsers([]);
                    setEditCarUserSearchQuery('');
                  }}
                  className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      // NEW dates (potentially modified by user)
                      const newStartDate = selectedCarAssignment.startDate;
                      const newEndDate = selectedCarAssignment.endDate;

                      console.log('🟢 Save clicked - NEW dates:', { newStartDate, newEndDate });

                      // ORIGINAL dates (stored when modal opened)
                      const originalStartDate = originalCarAssignmentDates.startDate;
                      const originalEndDate = originalCarAssignmentDates.endDate;

                      console.log('🟢 ORIGINAL dates:', { originalStartDate, originalEndDate });

                      // Find ALL assignments for the same car, project, and overlapping ORIGINAL dates
                      // IMPORTANT: Use savedCarAssignments (not carAssignments) to find assignments that exist in backend
                      const overlappingAssignments = savedCarAssignments.filter(a => {
                        if (a.carId !== selectedCarAssignment.carId || a.projectId !== selectedCarAssignment.projectId) return false;
                        const aStart = new Date(a.startDate);
                        const aEnd = new Date(a.endDate);
                        // Check if date ranges overlap with ORIGINAL dates
                        return (aStart <= originalEndDate && aEnd >= originalStartDate);
                      });

                      console.log('🟢 Found assignments to DELETE from SAVED:', overlappingAssignments.length, overlappingAssignments.map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));

                      // Delete ALL overlapping assignments
                      for (const assignment of overlappingAssignments) {
                        console.log('🟢 Deleting assignment:', assignment.id);
                        await axios.delete(
                          `${API_BASE_URL}/api/vehicle-assignments/${assignment.id}/`,
                          {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                          }
                        );
                      }

                      // Remove ALL overlapping assignments from local state
                      const assignmentIdsToRemove = new Set(overlappingAssignments.map(a => a.id));
                      const saveTimestamp = Date.now();
                      console.log(`🟢 [${saveTimestamp}] Removing IDs from state:`, Array.from(assignmentIdsToRemove));
                      console.log(`🟢 [${saveTimestamp}] carAssignments BEFORE removal (total: ${carAssignments.length}):`, carAssignments.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));
                      console.log(`🟢 [${saveTimestamp}] savedCarAssignments BEFORE removal (total: ${savedCarAssignments.length}):`, savedCarAssignments.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));

                      setCarAssignments(prev => {
                        const filtered = prev.filter(a => !assignmentIdsToRemove.has(a.id));
                        console.log(`🟢 [${saveTimestamp}] carAssignments AFTER removal (total: ${filtered.length}):`, filtered.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));
                        return filtered;
                      });
                      setSavedCarAssignments(prev => {
                        const filtered = prev.filter(a => !assignmentIdsToRemove.has(a.id));
                        console.log(`🟢 [${saveTimestamp}] savedCarAssignments AFTER removal (total: ${filtered.length}):`, filtered.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));
                        return filtered;
                      });

                      // Create new assignments for each selected user with NEW dates
                      if (editCarAssignmentUsers.length > 0) {
                        const newAssignments = [];

                        for (const userId of editCarAssignmentUsers) {
                          const payload = {
                            car: selectedCarAssignment.carId,
                            project: selectedCarAssignment.projectId,
                            assigned_date: newStartDate.toISOString(),
                            expected_return_date: newEndDate.toISOString(),
                            status: 'assigned',
                            assignment_reason: 'Resource planning assignment',
                            user: userId
                          };

                          const response = await axios.post(
                            `${API_BASE_URL}/api/vehicle-assignments/`,
                            payload,
                            {
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                            }
                          );

                          newAssignments.push({
                            id: response.data.id,
                            carId: selectedCarAssignment.carId,
                            userId: userId,
                            projectId: selectedCarAssignment.projectId,
                            startDate: newStartDate,
                            endDate: newEndDate,
                            originalAssignment: response.data
                          });
                        }

                        // Add new assignments to state
                        console.log(`🟢 [${saveTimestamp}] Adding NEW assignments:`, newAssignments.map(a => ({ id: a.id, userId: a.userId, start: a.startDate, end: a.endDate })));
                        setCarAssignments(prev => {
                          // Filter out any assignments that already exist (React Strict Mode protection)
                          const existingIds = new Set(prev.map(a => a.id));
                          const assignmentsToAdd = newAssignments.filter(a => !existingIds.has(a.id));
                          const updated = [...prev, ...assignmentsToAdd];
                          console.log(`🟢 [${saveTimestamp}] carAssignments AFTER adding new (total: ${updated.length}):`, updated.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));
                          return updated;
                        });
                        setSavedCarAssignments(prev => {
                          // Filter out any assignments that already exist (React Strict Mode protection)
                          const existingIds = new Set(prev.map(a => a.id));
                          const assignmentsToAdd = newAssignments.filter(a => !existingIds.has(a.id));
                          const updated = [...prev, ...assignmentsToAdd];
                          console.log(`🟢 [${saveTimestamp}] savedCarAssignments AFTER adding new (total: ${updated.length}):`, updated.filter(a => a.carId === selectedCarAssignment.carId).map(a => ({ id: a.id, start: a.startDate, end: a.endDate })));
                          return updated;
                        });
                      } else {
                        // No users selected - create assignment without user
                        const payload = {
                          car: selectedCarAssignment.carId,
                          project: selectedCarAssignment.projectId,
                          assigned_date: newStartDate.toISOString(),
                          expected_return_date: newEndDate.toISOString(),
                          status: 'assigned',
                          assignment_reason: 'Resource planning assignment'
                        };

                        const response = await axios.post(
                          `${API_BASE_URL}/api/vehicle-assignments/`,
                          payload,
                          {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                          }
                        );

                        const newAssignment = {
                          id: response.data.id,
                          carId: selectedCarAssignment.carId,
                          userId: null,
                          projectId: selectedCarAssignment.projectId,
                          startDate: newStartDate,
                          endDate: newEndDate,
                          originalAssignment: response.data
                        };

                        // Add to state with React Strict Mode protection
                        setCarAssignments(prev => {
                          if (prev.some(a => a.id === newAssignment.id)) return prev;
                          return [...prev, newAssignment];
                        });
                        setSavedCarAssignments(prev => {
                          if (prev.some(a => a.id === newAssignment.id)) return prev;
                          return [...prev, newAssignment];
                        });
                      }

                      setShowCarAssignmentEdit(false);
                      setSelectedCarAssignment(null);
                      setOriginalCarAssignmentDates(null);
                      setEditCarAssignmentUsers([]);
                      setEditCarUserSearchQuery('');

                      alert('Car assignment updated successfully!');
                    } catch (error) {
                      console.error('Error updating car assignment:', error);
                      alert('Failed to update car assignment. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Car Assignment Delete Confirmation Modal */}
      {showCarDeleteConfirm && carAssignmentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Icon and Title */}
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <AlertTriangle className="w-6 h-6 text-brand-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Remove Car Assignment
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              {/* Assignment Details */}
              <div className="bg-steel-50 rounded-xl p-4 mb-6">
                <div className="text-sm text-slate-700 space-y-2">
                  <div>
                    <span className="font-medium">Car:</span>{' '}
                    {(() => {
                      const car = (Array.isArray(carsData) ? carsData : []).find(c => c.id === carAssignmentToDelete.carId);
                      return car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : 'Unknown';
                    })()}
                  </div>
                  <div>
                    <span className="font-medium">Project:</span>{' '}
                    {(() => {
                      const project = (Array.isArray(projects) ? projects : []).find(p => p.id === carAssignmentToDelete.projectId);
                      return project?.name || 'Unknown';
                    })()}
                  </div>
                  <div>
                    <span className="font-medium">Dates:</span>{' '}
                    {new Date(carAssignmentToDelete.startDate).toLocaleDateString()} - {new Date(carAssignmentToDelete.endDate).toLocaleDateString()}
                  </div>
                  {carAssignmentToDelete.userId && (() => {
                    const user = (Array.isArray(users) ? users : []).find(u => u.id === carAssignmentToDelete.userId);
                    return user ? (
                      <div>
                        <span className="font-medium">User:</span> {user.first_name} {user.last_name}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Question */}
              <p className="text-slate-700 mb-6">
                Are you sure you want to remove this car assignment?
              </p>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCarDeleteConfirm(false);
                    setCarAssignmentToDelete(null);
                  }}
                  className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Delete from backend
                      await resourceBoardService.deleteVehicleAssignment(carAssignmentToDelete.id);

                      // Remove from local state
                      setCarAssignments(prev => prev.filter(a => a.id !== carAssignmentToDelete.id));
                      setSavedCarAssignments(prev => prev.filter(a => a.id !== carAssignmentToDelete.id));

                      setShowCarDeleteConfirm(false);
                      setCarAssignmentToDelete(null);
                    } catch (error) {
                      logger.error('Error deleting car assignment:', error);
                      alert('Failed to delete car assignment. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors font-medium"
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Travel Planning Modal */}
      {showTravelModal && travelPlanningData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                <Plane className="h-5 w-5 mr-2 text-brand-600" />
                Plan Travel
              </h3>
              <button 
                onClick={() => {
                  setShowTravelModal(false);
                  setTravelPlanningData(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <TravelPlanningForm
              user={travelPlanningData.user}
              date={travelPlanningData.date}
              initialData={travelPlanningData.initialData}
              allUsers={travelPlanningData.allUsers || []}
              onSubmit={handlePlanTravel}
              onCancel={() => {
                setShowTravelModal(false);
                setTravelPlanningData(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Travel Project Selection Modal */}
      {showTravelProjectModal && travelProjectSelectionData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Select Project for {travelProjectSelectionData.travelType === 'check_in' ? 'Check-in' : 'Check-out'}
              </h3>
              <button
                onClick={() => {
                  setShowTravelProjectModal(false);
                  setTravelProjectSelectionData(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-4">
                Multiple projects found for this travel date. Please select the correct project:
              </p>
              <div className="space-y-2">
                {travelProjectSelectionData.matchingProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => handleTravelProjectSelected(project)}
                    className="w-full text-left p-3 border rounded-xl hover:bg-brand-50 hover:border-brand-300 transition-colors"
                  >
                    <div className="font-medium text-slate-900">{project.name}</div>
                    <div className="text-sm text-slate-500">
                      {[project.street_address, project.city, project.country].filter(Boolean).join(', ')}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(project.start_date).toLocaleDateString()} - {new Date(project.end_date).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => {
                    createTravelWithProject(travelProjectSelectionData.pendingTravelData, null);
                  }}
                  className="w-full text-center p-2 text-slate-500 hover:text-slate-700 text-sm"
                >
                  Create without project assignment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Travel Alert/Confirm Modal */}
      {travelAlertModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className={`flex items-center justify-between p-4 border-b ${
              travelAlertModal.type === 'alert' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'
            }`}>
              <h3 className={`text-lg font-semibold flex items-center ${
                travelAlertModal.type === 'alert' ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {travelAlertModal.type === 'alert' ? (
                  <AlertTriangle className="h-5 w-5 mr-2" />
                ) : (
                  <AlertTriangle className="h-5 w-5 mr-2" />
                )}
                {travelAlertModal.title}
              </h3>
              <button
                onClick={() => {
                  if (travelAlertModal.onCancel) {
                    travelAlertModal.onCancel();
                  } else {
                    setTravelAlertModal(prev => ({ ...prev, show: false }));
                  }
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-700 whitespace-pre-line">
                {travelAlertModal.message}
              </p>
              <div className="mt-4 flex justify-end space-x-3">
                {travelAlertModal.type === 'confirm' && travelAlertModal.onCancel && (
                  <button
                    onClick={travelAlertModal.onCancel}
                    className="px-4 py-2 text-slate-600 bg-steel-100 hover:bg-steel-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={travelAlertModal.onConfirm}
                  className={`px-4 py-2 text-white rounded-xl transition-colors ${
                    travelAlertModal.type === 'alert'
                      ? 'bg-red-500 hover:bg-brand-500'
                      : 'bg-yellow-500 hover:bg-yellow-600'
                  }`}
                >
                  {travelAlertModal.type === 'alert' ? 'OK' : 'Yes, Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Travel Planning Modal */}
      {assignmentTravelModal.show && assignmentTravelModal.assignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-brand-200 bg-brand-50">
              <h3 className="text-lg font-semibold text-brand-700 flex items-center">
                <Plane className="h-5 w-5 mr-2" />
                Plan Travel for Assignment
              </h3>
              <button
                onClick={() => setAssignmentTravelModal(prev => ({ ...prev, show: false }))}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              {/* Assignment Info */}
              <div className="mb-4 p-3 bg-steel-50 rounded-xl">
                <div className="text-sm text-slate-600">
                  <span className="font-medium">
                    {users.find(u => u.id === assignmentTravelModal.assignment.userId)?.first_name}{' '}
                    {users.find(u => u.id === assignmentTravelModal.assignment.userId)?.last_name}
                  </span>
                  {' → '}
                  <span className="font-medium">
                    {projects.find(p => p.id === assignmentTravelModal.assignment.projectId)?.name || 'Unknown Project'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(assignmentTravelModal.assignment.startDate).toLocaleDateString()} - {new Date(assignmentTravelModal.assignment.endDate).toLocaleDateString()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Check-in Section */}
                <div className="border rounded-xl p-3">
                  <h4 className="font-medium text-yellow-600 mb-3 flex items-center">
                    <span className="w-3 h-3 bg-yellow-300 rounded-full mr-2"></span>
                    Check-in (Arrival)
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkIn"
                        checked={assignmentTravelModal.checkInOption === 'day_before'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkInOption: 'day_before' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        Day before
                        <span className="text-xs text-slate-500 block">
                          {(() => {
                            const d = new Date(assignmentTravelModal.assignment.startDate);
                            d.setDate(d.getDate() - 1);
                            return d.toLocaleDateString();
                          })()}
                        </span>
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkIn"
                        checked={assignmentTravelModal.checkInOption === 'first_day'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkInOption: 'first_day' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        First day of work
                        <span className="text-xs text-slate-500 block">
                          {new Date(assignmentTravelModal.assignment.startDate).toLocaleDateString()}
                        </span>
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkIn"
                        checked={assignmentTravelModal.checkInOption === 'none'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkInOption: 'none' }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-slate-500">No check-in</span>
                    </label>
                  </div>
                </div>

                {/* Check-out Section */}
                <div className="border rounded-xl p-3">
                  <h4 className="font-medium text-orange-600 mb-3 flex items-center">
                    <span className="w-3 h-3 bg-orange-600 rounded-full mr-2"></span>
                    Check-out (Departure)
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkOut"
                        checked={assignmentTravelModal.checkOutOption === 'day_after'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkOutOption: 'day_after' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        Day after
                        <span className="text-xs text-slate-500 block">
                          {(() => {
                            const d = new Date(assignmentTravelModal.assignment.endDate);
                            d.setDate(d.getDate() + 1);
                            return d.toLocaleDateString();
                          })()}
                        </span>
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkOut"
                        checked={assignmentTravelModal.checkOutOption === 'last_day'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkOutOption: 'last_day' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        Last day of work
                        <span className="text-xs text-slate-500 block">
                          {new Date(assignmentTravelModal.assignment.endDate).toLocaleDateString()}
                        </span>
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="checkOut"
                        checked={assignmentTravelModal.checkOutOption === 'none'}
                        onChange={() => setAssignmentTravelModal(prev => ({ ...prev, checkOutOption: 'none' }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-slate-500">No check-out</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setAssignmentTravelModal(prev => ({ ...prev, show: false }))}
                  className="px-4 py-2 text-slate-600 bg-steel-100 hover:bg-steel-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAssignmentTravels}
                  disabled={assignmentTravelModal.checkInOption === 'none' && assignmentTravelModal.checkOutOption === 'none'}
                  className={`px-4 py-2 text-white rounded-xl transition-colors ${
                    assignmentTravelModal.checkInOption === 'none' && assignmentTravelModal.checkOutOption === 'none'
                      ? 'bg-steel-300 cursor-not-allowed'
                      : 'bg-brand-500 hover:bg-brand-500'
                  }`}
                >
                  Create Travel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Travel Tooltip */}
      {showTravelTooltip && selectedTravel && (
        <div
          className="fixed z-50"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            pointerEvents: 'auto'
          }}
          onMouseEnter={() => {
            // Clear any existing timeout and keep tooltip open
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
              tooltipTimeoutRef.current = null;
            }
            setShowTravelTooltip(true);
          }}
          onMouseLeave={() => {
            // Close tooltip when leaving it
            setShowTravelTooltip(false);
            setSelectedTravel(null);
          }}
        >
          <TravelTooltip
            travel={selectedTravel}
            user={(Array.isArray(users) ? users : []).find(u => u.id === selectedTravel.user_id || u.id === selectedTravel.user)}
            car={(Array.isArray(carsData) ? carsData : []).find(c => c.id === selectedTravel.car_id || c.id === selectedTravel.car?.id)}
            onDelete={handleDeleteTravel}
            onClose={() => {
              setShowTravelTooltip(false);
              setSelectedTravel(null);
            }}
          />
        </div>
      )}

      {/* Note Tooltip */}
      {showNoteTooltip && selectedNoteData && (
        <div
          className="fixed z-50"
          style={{
            left: noteTooltipPosition.x - 100, // Center the tooltip
            top: noteTooltipPosition.y - 10,
            pointerEvents: 'auto'
          }}
          onMouseEnter={() => {
            // Clear any existing timeout and keep tooltip open
            if (noteTooltipTimeoutRef.current) {
              clearTimeout(noteTooltipTimeoutRef.current);
              noteTooltipTimeoutRef.current = null;
            }
            setShowNoteTooltip(true);
          }}
          onMouseLeave={() => {
            // Don't close if confirmation dialog is open
            if (!showConfirmNoteDelete) {
              // Close tooltip when leaving it
              setShowNoteTooltip(false);
              setSelectedNoteData(null);
            }
          }}
        >
          {showConfirmNoteDelete && selectedNoteData ? (
            <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-brand-600 flex items-center">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Note
                </h3>
                <button
                  onClick={() => {
                    setShowConfirmNoteDelete(false);
                    setNoteToDelete(null);
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="text-sm text-slate-700 mb-4">
                Are you sure you want to delete this note for <strong>{selectedNoteData.user.first_name} {selectedNoteData.user.last_name}</strong>?
                <div className="mt-2 p-2 bg-steel-50 rounded border">
                  <div className="font-medium">{(noteToDelete || selectedNoteData.notes[0])?.content}</div>
                  <div className="text-xs text-slate-600">
                    {selectedNoteData.date ? selectedNoteData.date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    }) : new Date((noteToDelete || selectedNoteData.notes[0])?.createdAt).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })} • {new Date((noteToDelete || selectedNoteData.notes[0])?.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowConfirmNoteDelete(false);
                    setNoteToDelete(null);
                  }}
                  className="px-3 py-1 text-sm text-slate-600 border border-steel-300 rounded hover:bg-steel-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const noteToDeleteId = noteToDelete?.id || selectedNoteData?.notes?.[0]?.id;
                    if (noteToDeleteId) {
                      handleDeleteNote(noteToDeleteId);
                    }
                  }}
                  className="px-3 py-1 text-sm bg-brand-500 text-white rounded hover:bg-brand-600"
                >
                  Delete Note
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-steel-200 rounded-xl shadow-lg p-4 min-w-80 max-w-96">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 flex items-center">
                  📝 Notes ({selectedNoteData.notes.length})
                </h3>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setShowConfirmNoteDelete(true)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title="Delete all notes for this day"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowNoteTooltip(false);
                      setSelectedNoteData(null);
                    }}
                    className="p-1 text-slate-400 hover:text-slate-600"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="text-xs text-slate-500 mb-3">
                {selectedNoteData.user.first_name} {selectedNoteData.user.last_name}
                {selectedNoteData.date && (
                  <> • {selectedNoteData.date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}</>
                )}
                {!selectedNoteData.date && <> • All Notes</>}
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedNoteData.notes.map((note, idx) => (
                  <div key={note.id} className="bg-steel-50 p-2 rounded text-sm relative group">
                    <div className="text-slate-900 font-medium pr-6">{note.content}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {!selectedNoteData.date && (
                        <span className="font-medium text-brand-600">{note.date} • </span>
                      )}
                      {new Date(note.createdAt).toLocaleString()}
                    </div>
                    <button
                      className="absolute top-1 right-1 text-slate-400 hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConfirmDeleteNote(note);
                      }}
                      title="Delete this note"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Travel Context Menu */}
      {showTravelContextMenu && contextMenuData && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-xl border border-steel-200 py-1 min-w-48 will-change-transform"
          style={{
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            transform: 'translate3d(0,0,0)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-sm font-semibold text-slate-800 border-b border-steel-100">
            {contextMenuData.type === 'car' ? (
              `${contextMenuData.car.car_brand} ${contextMenuData.car.car_model}`
            ) : (
              `${contextMenuData.user.first_name} ${contextMenuData.user.last_name}`
            )}
          </div>
          
          <button
            className="w-full px-3 py-2 text-left text-sm hover:bg-yellow-50 text-yellow-800 flex items-center space-x-2"
            onClick={() => handleQuickTravel('check_in')}
          >
            <span className="w-3 h-3 bg-yellow-300 rounded-full"></span>
            <span>Add Check-in Travel</span>
          </button>
          
          <button
            className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 text-orange-800 flex items-center space-x-2"
            onClick={() => handleQuickTravel('check_out')}
          >
            <span className="w-3 h-3 bg-orange-600 rounded-full"></span>
            <span>Add Check-out Travel</span>
          </button>
          
          <div className="border-t border-steel-100 mt-2"></div>

          <button
            className="w-full px-3 py-2 text-left text-sm hover:bg-brand-50 text-brand-800 flex items-center space-x-2"
            onClick={handleAddNote}
          >
            <span className="text-brand-600">📝</span>
            <span>Add Note</span>
          </button>
        </div>
      )}
      
      {/* Car Rental Edit Modal */}
      {showCarRentalModal && selectedCarForEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Edit Rental Data - {selectedCarForEdit.car_brand} {selectedCarForEdit.car_model}
              </h3>
              <button 
                onClick={() => setShowCarRentalModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ownership Type</label>
                <select 
                  value={carRentalFormData.ownership_type}
                  onChange={(e) => setCarRentalFormData(prev => ({ ...prev, ownership_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                >
                  <option value="own">Owned</option>
                  <option value="rented">Rented</option>
                </select>
              </div>
              
              {carRentalFormData.ownership_type === 'rented' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Rental Start Date</label>
                    <input
                      type="date"
                      value={carRentalFormData.rental_start_date}
                      onChange={(e) => setCarRentalFormData(prev => ({ ...prev, rental_start_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Rental End Date</label>
                    <input
                      type="date"
                      value={carRentalFormData.rental_end_date}
                      onChange={(e) => setCarRentalFormData(prev => ({ ...prev, rental_end_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Daily Rental Cost (PLN)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={carRentalFormData.daily_rental_cost}
                      onChange={(e) => setCarRentalFormData(prev => ({ ...prev, daily_rental_cost: e.target.value }))}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500"
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-steel-200">
              <button
                onClick={() => setShowCarRentalModal(false)}
                className="px-4 py-2 text-slate-700 bg-steel-100 hover:bg-steel-200 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleCarRentalUpdate}
                className="px-4 py-2 bg-brand-500 text-white hover:bg-brand-600 rounded-xl"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Bar */}
      <StatusBar
        assignments={assignments}
        users={users}
        projects={projects}
        days={days}
        dayWidth={dayWidth}
        showPeople={showPeople}
        showCars={showCars}
        showEquipment={showEquipment}
        cars={carsData}
        carAssignments={carAssignments}
        trainings={trainingData}
        isSidebarVisible={isSidebarVisible}
        currentStatus={currentStatus}
        onDeleteAssignment={handleDeleteAssignmentFromConflicts}
        onQuickAction={(action) => {
          switch(action) {
            case 'today':
              // Scroll to today or highlight current date
              const today = new Date();
              break;
            case 'conflicts':
              // Highlight conflicting assignments
              break;
            default:
              break;
          }
        }}
      />
      
      
      {/* Note Modal */}
      {showNoteModal && noteModalData.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900">Add Note</h3>
              <button
                onClick={() => setShowNoteModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-slate-600">
                  <strong>{noteModalData.user.first_name} {noteModalData.user.last_name}</strong>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formatDateForAPI(noteModalData.date) || ''}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setNoteModalData(prev => ({ ...prev, date: newDate }));
                  }}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Note Content
                </label>
                <textarea
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  rows={4}
                  placeholder="Enter your note here..."
                  autoFocus
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNote}
                  disabled={!noteInput.trim()}
                  className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Car Travel Planning Modal */}
      {showCarTravelModal && carTravelData.car && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                {(() => {
                  const { Icon, color } = getEquipmentIcon(carTravelData.car.equipment_type);
                  return <Icon className={`w-5 h-5 ${color} mr-2`} />;
                })()}
                Equipment Travel Planning - {carTravelData.car.car_brand} {carTravelData.car.car_model}
              </h3>
              <button
                onClick={() => {
                  setShowCarTravelModal(false);
                  setCarTravelData({ car: null, date: null, currentProject: null });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <CarTravelPlanningForm
              car={carTravelData.car}
              date={carTravelData.date}
              currentProject={carTravelData.currentProject}
              initialTravelType={carTravelData.travelType}
              assignments={assignments}
              projects={projects}
              users={users}
              carAssignments={[]} // Add if you have car assignments data
              travelData={travelData}
              onSubmit={(formData) => {
                // Handle form submission
                setShowCarTravelModal(false);
                setCarTravelData({ car: null, date: null, currentProject: null });
              }}
              onCancel={() => {
                setShowCarTravelModal(false);
                setCarTravelData({ car: null, date: null, currentProject: null });
              }}
            />
          </div>
        </div>
      )}

      {/* Publish to Mobile Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-wide">Publish Schedule</h3>
              <button
                onClick={() => {
                  setShowPublishModal(false);
                  setPublishStartDate('');
                  setPublishEndDate('');
                  setPublishEndMode('date');
                  setPublishEndWeek('');
                }}
                className="p-1.5 text-slate-500 hover:text-white hover:bg-brand-500 rounded-xl transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Mobile app users will see all historic data and assignments until the selected end date.
              </p>

              <div className="space-y-4">
                {/* Info about historic data */}
                <div className="bg-steel-50 border border-steel-200 rounded-xl p-3">
                  <p className="text-sm text-slate-700">
                    <strong>Historic Data:</strong> All past assignments will be visible (no start date limit)
                  </p>
                </div>

                {/* End Date Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select End Date Mode
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="publishEndMode"
                        value="date"
                        checked={publishEndMode === 'date'}
                        onChange={(e) => setPublishEndMode(e.target.value)}
                        className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-slate-700">Custom Date</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="publishEndMode"
                        value="week"
                        checked={publishEndMode === 'week'}
                        onChange={(e) => setPublishEndMode(e.target.value)}
                        className="h-4 w-4 text-brand-600 border-steel-300 focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-slate-700">End of Week</span>
                    </label>
                  </div>
                </div>

                {/* Custom Date Input */}
                {publishEndMode === 'date' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={publishEndDate}
                      onChange={(e) => setPublishEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                )}

                {/* Week Selection */}
                {publishEndMode === 'week' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Select Week (publishes until Sunday of selected week)
                    </label>
                    <select
                      value={publishEndWeek}
                      onChange={(e) => {
                        setPublishEndWeek(e.target.value);
                        // Calculate Sunday of selected week
                        if (e.target.value) {
                          const [year, week] = e.target.value.split('-W').map(Number);
                          const simple = new Date(year, 0, 1 + (week - 1) * 7);
                          const dow = simple.getDay();
                          const ISOweekStart = simple;
                          if (dow <= 4)
                            ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
                          else
                            ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
                          const weekStart = new Date(ISOweekStart);
                          const weekEnd = new Date(ISOweekStart);
                          weekEnd.setDate(weekEnd.getDate() + 6); // Sunday
                          setPublishEndDate(weekEnd.toISOString().split('T')[0]);
                        }
                      }}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value="">Select week...</option>
                      {(() => {
                        const weeks = [];
                        const currentYear = new Date().getFullYear();
                        // Only current year and next year (2025, 2026)
                        const years = [currentYear, currentYear + 1];

                        for (const year of years) {
                          for (let week = 1; week <= 52; week++) {
                            weeks.push(`${year}-W${String(week).padStart(2, '0')}`);
                          }
                        }

                        return weeks.map(week => (
                          <option key={week} value={week}>
                            Week {week.split('-W')[1]}, {week.split('-W')[0]}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                )}

                {publishEndDate && (
                  <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
                    <p className="text-sm text-brand-800">
                      Mobile users will see all historic assignments until <strong>{publishEndDate}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-steel-200">
              <button
                onClick={() => {
                  setShowPublishModal(false);
                  setPublishStartDate('');
                  setPublishEndDate('');
                  setPublishEndMode('date');
                  setPublishEndWeek('');
                }}
                className="px-4 py-2 text-slate-700 bg-steel-200 rounded-xl hover:bg-steel-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                disabled={!publishEndDate}
                className={`px-4 py-2 rounded-xl transition-colors ${
                  publishEndDate
                    ? 'bg-brand-500 text-white hover:bg-brand-600'
                    : 'bg-steel-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                Publish to Mobile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save History Modal */}
      {showSaveHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b-2 border-brand-500 bg-gradient-to-r from-brand-50 to-steel-50">
              <h3 className="text-lg font-black text-slate-900 flex items-center uppercase tracking-wide">
                <Clock className="h-5 w-5 mr-2 text-brand-600" />
                Save History
              </h3>
              <button
                onClick={() => setShowSaveHistoryModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {/* Filters and Pagination Controls */}
              <div className="mb-4 space-y-3 border-b border-steel-200 pb-4">
                {/* Row 1: Search filters */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Search by Person</label>
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={saveHistorySearchUser}
                      onChange={(e) => setSaveHistorySearchUser(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Search by Project</label>
                    <input
                      type="text"
                      placeholder="Search by project name..."
                      value={saveHistorySearchProject}
                      onChange={(e) => setSaveHistorySearchProject(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Row 2: Pagination options */}
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium text-slate-700">Show entries:</label>
                  <div className="flex gap-2">
                    {[10, 20, 50, 100].map(limit => (
                      <button
                        key={limit}
                        onClick={() => setSaveHistoryLimit(limit)}
                        className={`px-3 py-1 text-xs font-medium rounded-xl transition-colors ${
                          saveHistoryLimit === limit
                            ? 'bg-brand-500 text-white'
                            : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                        }`}
                      >
                        {limit}
                      </button>
                    ))}
                    <button
                      onClick={() => setSaveHistoryLimit(9999)}
                      className={`px-3 py-1 text-xs font-medium rounded-xl transition-colors ${
                        saveHistoryLimit === 9999
                          ? 'bg-brand-500 text-white'
                          : 'bg-steel-100 text-slate-700 hover:bg-steel-200'
                      }`}
                    >
                      All
                    </button>
                  </div>
                  {saveHistorySearchUser || saveHistorySearchProject ? (
                    <button
                      onClick={() => {
                        setSaveHistorySearchUser('');
                        setSaveHistorySearchProject('');
                      }}
                      className="ml-auto text-xs text-brand-600 hover:text-brand-800"
                    >
                      Clear filters
                    </button>
                  ) : null}
                </div>
              </div>

              {(() => {
                // Filter and limit history list
                let filteredHistory = saveHistoryList;

                // Filter by user name
                if (saveHistorySearchUser) {
                  const searchLower = saveHistorySearchUser.toLowerCase();
                  filteredHistory = filteredHistory.filter(history => {
                    const fullName = history.saved_by_data?.first_name && history.saved_by_data?.last_name
                      ? `${history.saved_by_data.first_name} ${history.saved_by_data.last_name}`
                      : history.saved_by_data?.username || history.saved_by_data?.email || '';
                    return fullName.toLowerCase().includes(searchLower);
                  });
                }

                // Filter by project name in change_details
                if (saveHistorySearchProject) {
                  const searchLower = saveHistorySearchProject.toLowerCase();
                  filteredHistory = filteredHistory.filter(history => {
                    if (!history.change_details) return false;

                    // Check in created assignments
                    const hasInCreated = history.change_details.created?.some(item =>
                      item.project?.toLowerCase().includes(searchLower)
                    );

                    // Check in updated assignments
                    const hasInUpdated = history.change_details.updated?.some(item =>
                      item.changes?.some(change => change.toLowerCase().includes('project') && change.toLowerCase().includes(searchLower))
                    );

                    // Check in deleted assignments
                    const hasInDeleted = history.change_details.deleted?.some(item =>
                      item.project?.toLowerCase().includes(searchLower)
                    );

                    return hasInCreated || hasInUpdated || hasInDeleted;
                  });
                }

                // Limit entries
                const limitedHistory = filteredHistory.slice(0, saveHistoryLimit);

                // Show results info
                const totalResults = filteredHistory.length;
                const showingResults = limitedHistory.length;

                return (
                  <>
                    {/* Results info */}
                    {totalResults > 0 && (
                      <div className="mb-3 text-xs text-slate-600">
                        Showing <span className="font-semibold">{showingResults}</span> of <span className="font-semibold">{totalResults}</span> entries
                        {saveHistoryList.length !== totalResults && ` (filtered from ${saveHistoryList.length} total)`}
                      </div>
                    )}

                    {limitedHistory.length > 0 ? (
                  <div className="space-y-3">
                    {limitedHistory.map((history, index) => {
                    const totalChanges = history.assignments_created + history.assignments_updated + history.assignments_deleted;
                    const fullName = history.saved_by_data?.first_name && history.saved_by_data?.last_name
                      ? `${history.saved_by_data.first_name} ${history.saved_by_data.last_name}`
                      : history.saved_by_data?.username || history.saved_by_data?.email || 'Unknown User';

                    return (
                      <div
                        key={history.id}
                        className={`border rounded-xl p-4 ${index === 0 ? 'bg-green-50 border-green-200' : 'bg-steel-50 border-steel-200'}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2 flex-1">
                            <User className={`h-4 w-4 ${index === 0 ? 'text-green-600' : 'text-slate-600'}`} />
                            <span className="font-semibold text-slate-900">{fullName}</span>
                            <span className="text-slate-400">•</span>
                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-sm text-slate-600">{formatDateTime(new Date(history.saved_at))}</span>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full ml-2">Latest</span>
                            )}
                          </div>
                        </div>

                        {/* Change Details */}
                        {history.change_details && Object.keys(history.change_details).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-steel-200">
                            <div className="text-xs space-y-2">
                              {/* Created assignments */}
                              {history.change_details.created && history.change_details.created.length > 0 && (
                                <div>
                                  <div className="font-semibold text-brand-700 mb-1">Created:</div>
                                  <div className="space-y-1 ml-3">
                                    {history.change_details.created.map((item, idx) => (
                                      <div key={idx} className="text-slate-600">
                                        • <span className="font-medium">{item.displayLabel || item.car || 'Unknown'}</span>
                                        <span className="text-slate-400 ml-1">
                                          [{item.start_date} - {item.end_date}]
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Updated assignments */}
                              {history.change_details.updated && history.change_details.updated.length > 0 && (
                                <div>
                                  <div className="font-semibold text-yellow-700 mb-1">Updated:</div>
                                  <div className="space-y-1 ml-3">
                                    {history.change_details.updated.map((item, idx) => (
                                      <div key={idx} className="text-slate-600">
                                        • <span className="font-medium">{item.displayLabel || item.car || 'Unknown'}</span>
                                        {item.changes && item.changes.length > 0 && (
                                          <div className="ml-4 mt-0.5 text-slate-500">
                                            {item.changes.map((change, cIdx) => (
                                              <div key={cIdx}>→ {change}</div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Deleted assignments */}
                              {history.change_details.deleted && history.change_details.deleted.length > 0 && (
                                <div>
                                  <div className="font-semibold text-red-700 mb-1">Deleted:</div>
                                  <div className="space-y-1 ml-3">
                                    {history.change_details.deleted.map((item, idx) => (
                                      <div key={idx} className="text-slate-600">
                                        • <span className="font-medium">{item.displayLabel || item.car || 'Unknown'}</span>
                                        <span className="text-slate-400 ml-1">
                                          [{item.dates}]
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>
                    {saveHistorySearchUser || saveHistorySearchProject
                      ? 'No matching history found'
                      : 'No save history available'}
                  </p>
                  {(saveHistorySearchUser || saveHistorySearchProject) && (
                    <button
                      onClick={() => {
                        setSaveHistorySearchUser('');
                        setSaveHistorySearchProject('');
                      }}
                      className="mt-2 text-sm text-brand-600 hover:text-brand-800"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              )
                    }
                  </>
                );
              })()}
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-steel-200">
              <button
                onClick={() => setShowSaveHistoryModal(false)}
                className="px-4 py-2 bg-steel-200 text-slate-700 rounded-xl hover:bg-steel-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish History Modal */}
      {showPublishHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-steel-200">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                <Send className="h-5 w-5 mr-2 text-brand-600" />
                Publish History
              </h3>
              <button
                onClick={() => setShowPublishHistoryModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {publishHistoryList.length > 0 ? (
                <div className="space-y-3">
                  {publishHistoryList.map((history, index) => {
                    const fullName = history.published_by_data?.first_name && history.published_by_data?.last_name
                      ? `${history.published_by_data.first_name} ${history.published_by_data.last_name}`
                      : history.published_by_data?.username || history.published_by_data?.email || 'Unknown User';

                    return (
                      <div
                        key={history.id}
                        className={`border rounded-xl px-4 py-2 ${index === 0 ? 'bg-brand-50 border-brand-200' : 'bg-steel-50 border-steel-200'}`}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-3 flex-wrap">
                            <div className="flex items-center space-x-2">
                              <User className={`h-4 w-4 ${index === 0 ? 'text-brand-600' : 'text-slate-600'}`} />
                              <span className="font-semibold text-slate-900">{fullName}</span>
                            </div>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-brand-500 text-white text-xs rounded-full">Latest</span>
                            )}
                            <span className="text-slate-600">•</span>
                            <span className="text-slate-600">{formatDateTime(new Date(history.published_at))}</span>
                            <span className="text-slate-600">•</span>
                            <span className="text-purple-700 font-medium">
                              Date Range: {history.start_date} - {history.end_date}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Send className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No publish history available</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-steel-200">
              <button
                onClick={() => setShowPublishHistoryModal(false)}
                className="px-4 py-2 bg-steel-200 text-slate-700 rounded-xl hover:bg-steel-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 animate-fadeIn">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <AlertTriangle className="h-8 w-8 text-brand-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900 text-center mb-2">
                Delete Assignment
              </h3>
              <p className="text-slate-600 text-center mb-6">
                Are you sure you want to delete this assignment? You can undo this action with Ctrl+Z.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelDeleteAssignment}
                  className="flex-1 px-4 py-2 bg-steel-200 text-slate-700 rounded-xl hover:bg-steel-300 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAssignment}
                  className="flex-1 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Approval Modal */}
      {showHolidayModal && selectedHoliday && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 animate-fadeIn">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">
                  Holiday Request
                </h3>
                <button
                  onClick={() => {
                    setShowHolidayModal(false);
                    setSelectedHoliday(null);
                    setHolidayRejectionReason('');
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 space-y-3">
                <div>
                  <span className="text-sm font-semibold text-slate-700">User:</span>
                  <span className="ml-2 text-sm text-slate-900">
                    {users.find(u => u.id === selectedHoliday.userId)?.name || 'Unknown'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-slate-700">Dates:</span>
                  <span className="ml-2 text-sm text-slate-900">
                    {selectedHoliday.startDate?.toLocaleDateString()} - {selectedHoliday.endDate?.toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-semibold text-slate-700">Status:</span>
                  <span className={`ml-2 text-sm font-semibold ${
                    selectedHoliday.status === 'approved' ? 'text-green-600' :
                    selectedHoliday.status === 'rejected' ? 'text-brand-600' :
                    'text-yellow-600'
                  }`}>
                    {selectedHoliday.status.charAt(0).toUpperCase() + selectedHoliday.status.slice(1)}
                  </span>
                </div>
                {selectedHoliday.notes && (
                  <div>
                    <span className="text-sm font-semibold text-slate-700">Notes:</span>
                    <p className="mt-1 text-sm text-slate-600 bg-steel-50 p-2 rounded">
                      {selectedHoliday.notes}
                    </p>
                  </div>
                )}
              </div>

              {selectedHoliday.status === 'pending' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Rejection Reason (optional):
                    </label>
                    <textarea
                      value={holidayRejectionReason}
                      onChange={(e) => setHolidayRejectionReason(e.target.value)}
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      rows="3"
                      placeholder="Enter reason for rejection..."
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={handleRejectHoliday}
                      className="flex-1 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors font-medium"
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleApproveHoliday}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
                    >
                      Approve
                    </button>
                  </div>
                </>
              )}

              {selectedHoliday.status !== 'pending' && (
                <div className="text-center text-sm text-slate-600">
                  This holiday has already been {selectedHoliday.status}.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role Order Modal */}
      {showRoleOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Set Role Order</h2>
              <button
                onClick={() => setShowRoleOrderModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Drag roles to change their order. Users will be sorted in this order from top to bottom.
            </p>

            <div className="space-y-2 mb-4">
              {globalRoles.map((role, index) => {
                const inOrder = roleOrder.includes(role.id);
                const orderIndex = roleOrder.indexOf(role.id);

                return (
                  <div
                    key={role.id}
                    className="flex items-center justify-between p-3 bg-steel-50 rounded border border-steel-200"
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3"
                        style={{ backgroundColor: role.color }}
                      >
                        {role.name.length <= 3 ? role.name.toUpperCase() : role.name.substring(0, 3).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{role.name}</span>
                      {inOrder && (
                        <span className="ml-2 text-xs text-slate-500">
                          (Position: {orderIndex + 1})
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {inOrder && orderIndex > 0 && (
                        <button
                          onClick={() => {
                            const newOrder = [...roleOrder];
                            const temp = newOrder[orderIndex];
                            newOrder[orderIndex] = newOrder[orderIndex - 1];
                            newOrder[orderIndex - 1] = temp;
                            setRoleOrder(newOrder);
                          }}
                          className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-500"
                        >
                          ↑
                        </button>
                      )}
                      {inOrder && orderIndex < roleOrder.length - 1 && (
                        <button
                          onClick={() => {
                            const newOrder = [...roleOrder];
                            const temp = newOrder[orderIndex];
                            newOrder[orderIndex] = newOrder[orderIndex + 1];
                            newOrder[orderIndex + 1] = temp;
                            setRoleOrder(newOrder);
                          }}
                          className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-500"
                        >
                          ↓
                        </button>
                      )}
                      {!inOrder ? (
                        <button
                          onClick={() => setRoleOrder([...roleOrder, role.id])}
                          className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Add
                        </button>
                      ) : (
                        <button
                          onClick={() => setRoleOrder(roleOrder.filter(id => id !== role.id))}
                          className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-brand-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={async () => {
                  // Save role order to database
                  try {
                    await resourceBoardService.saveRoleOrderSetting(roleOrder);
                    setShowRoleOrderModal(false);
                  } catch (error) {
                    logger.error('Error saving role order:', error);
                    alert('Failed to save role order. Please try again.');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-steel-100 hover:bg-steel-200 rounded-xl"
              >
                Save & Close
              </button>
              <button
                onClick={async () => {
                  // Set all roles in default order
                  const newOrder = globalRoles.map(r => r.id);
                  setRoleOrder(newOrder);
                  // Save to database
                  try {
                    await resourceBoardService.saveRoleOrderSetting(newOrder);
                    setShowRoleOrderModal(false);
                  } catch (error) {
                    logger.error('Error saving role order:', error);
                    alert('Failed to save role order. Please try again.');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-xl"
              >
                Add All & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      <UserManagementModal
        isOpen={showUserManagementModal}
        onClose={() => {
          setShowUserManagementModal(false);
          setSelectedUserForManagement(null);
        }}
        user={selectedUserForManagement}
        globalRoles={globalRoles}
        currentUserRoles={selectedUserForManagement ? userGlobalRoles.filter(
          ugr => (ugr.user === selectedUserForManagement.id || ugr.user_id === selectedUserForManagement.id)
        ) : []}
        countryDays={selectedUserForManagement ? calculateDaysInCountries(selectedUserForManagement.id) : {}}
        onSave={handleSaveUserManagement}
      />

      {/* Chat Widgets */}
      {openChats.map((chat, index) => (
        <ChatWidget
          key={chat.user_data.id}
          user={chat}
          position={index}
          onClose={() => setOpenChats(openChats.filter(c => c.user_data.id !== chat.user_data.id))}
        />
      ))}

      {/* Drag Preview - pokazuje dokładnie taki sam pasek który kopiujesz */}
      {assignmentDragState && assignmentDragState.isDragging && assignmentDragState.assignment && (
        (() => {
          const assignment = assignmentDragState.assignment;
          const project = (Array.isArray(projects) ? projects : []).find(p => p.id === assignment.projectId);
          const projectIndex = (Array.isArray(projects) ? projects : []).findIndex(p => p.id === assignment.projectId);

          // Użyj tej samej logiki kolorowania co w oryginalnych paskach
          const color = project ? getProjectColor(project, projectIndex) : projectColors[projectIndex % projectColors.length];
          const inlineStyles = project ? getProjectInlineStyles(project) : {};

          // Oblicz szerokość na podstawie dat (taka sama jak oryginalny pasek)
          const days = getTimeRangeDays;
          const dayWidth = getDayWidth;
          const startDay = days.findIndex(day =>
            day.toDateString() === assignment.startDate.toDateString()
          );
          let endDay = days.findIndex(day =>
            day.toDateString() === assignment.endDate.toDateString()
          );
          const effectiveStartDay = startDay === -1 ? 0 : startDay;
          const effectiveEndDay = endDay === -1 ? days.length - 1 : endDay;
          const assignmentWidth = (effectiveEndDay - effectiveStartDay + 1) * dayWidth;
          const assignmentHeight = 24; // Taka sama wysokość jak w oryginalnym pasku

          return (
            <div
              ref={(el) => {
                dragPreviewRef.current = el;
                // Set initial position only once when element is created
                if (el && !el.style.transform) {
                  el.style.transform = `translate(${assignmentDragState.startX + 15}px, ${assignmentDragState.startY - 12}px)`;
                }
              }}
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                // No transform here - it's set by ref callback above and updated by handleMouseMove
                pointerEvents: 'none',
                zIndex: 9999,
                width: `${assignmentWidth}px`,
                height: `${assignmentHeight}px`,
                willChange: 'transform'
              }}
            >
              <div
                className={`${color} rounded border-l-4 px-2 text-xs flex items-center shadow-2xl relative ${
                  assignmentDragState.isCopying ? 'ring-2 ring-green-400' : 'ring-2 ring-brand-400'
                }`}
                style={{
                  opacity: 0.95,
                  height: '100%',
                  ...inlineStyles
                }}
              >
                {/* Ikona copy jeśli Alt jest wciśnięty */}
                {assignmentDragState.isCopying && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow-lg border-2 border-white">
                    +
                  </div>
                )}

                <span className="truncate font-medium">
                  {project?.name}
                </span>
              </div>
            </div>
          );
        })()
      )}

      {/* Bottom Navigation Bar - Fixed at bottom */}
      {selectedTimeRange !== 'custom' && (
        <div
          className="fixed bottom-0 mt-[3px] px-3"
          style={{
            left: isSidebarVisible ? 'calc(20rem + 1px + 100px)' : '100px',
            right: '100px',
            width: isSidebarVisible ? 'calc(100% - 20rem - 1px - 200px)' : 'calc(100% - 200px)',
            zIndex: 40
          }}
        >
          <div className="flex items-center justify-center gap-3">
            {/* Navigation buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateTime(-1)}
                className="p-0.5 border border-steel-300 rounded hover:bg-steel-50 hover:border-brand-400 hover:text-brand-600 transition-all"
                title="Previous period"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>

              <span className="text-[10px] font-semibold min-w-[80px] text-center px-2 py-0.5 bg-brand-50 text-brand-700 rounded border border-brand-200">
                {getTimeRangeLabel()}
              </span>

              <button
                onClick={() => navigateTime(1)}
                className="p-0.5 border border-steel-300 rounded hover:bg-steel-50 hover:border-brand-400 hover:text-brand-600 transition-all"
                title="Next period"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>

            {/* Fine-grained scroll slider */}
            <div className="flex-1 max-w-xs">
              <input
                type="range"
                min={-getMaxOffset()}
                max={getMaxOffset()}
                value={dayOffset}
                onChange={(e) => setDayOffset(parseInt(e.target.value))}
                className="w-full h-1.5 bg-steel-200 rounded-xl appearance-none cursor-pointer slider-thumb mb-[2px]"
                title={`Scroll by days (${dayOffset > 0 ? '+' : ''}${dayOffset} days)`}
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((dayOffset + getMaxOffset()) / (getMaxOffset() * 2)) * 100}%, #e5e7eb ${((dayOffset + getMaxOffset()) / (getMaxOffset() * 2)) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>

            {/* Scroll hint */}
            <div className="text-[10px] text-slate-500 border-l border-steel-300 pl-2">
              <span className="font-medium">Shift+Scroll</span>
            </div>
          </div>
        </div>
      )}
    </div>
      </Suspense>
    </DndProvider>
  );
};


export default ResourceBoard;